<template>
    <div @pointerdown="showCustomTimeframeMenu = false" class="chart-page workspace-page" :class="{ 'chart-page-dark': night, 'workspace-replaying': workspaceReplay.active, 'workspace-focused': workspaceFocused }">
        <ChartAnalysis v-if="analysisVisible" :ai-enabled="aiChatEnabled" :candles="workspaceCandles" :source="workspaceSource" :symbol="symbolDisplay" :timeframe="timeframe" @close="analysisVisible=false" @ask-ai="explainChartWithAI" />
        <ChartContextMenu :menu="chartMenu" :selected="!!drawingSelected()" :locked="!!drawingSelected()?.ov.settings.$locked" :clipboard="!!drawingClipboard" :alerts="workspaceAllAlerts" @close="chartMenu=null" @action="drawingMenuAction" />
        <button v-if="workspaceFocused" class="workspace-focus-restore workspace-icon-button" @click="workspaceAction({ action: 'focus' })" title="Show chart controls (Escape)" aria-label="Show chart controls"><WorkspaceIcon name="focus" /></button>
        <div id="tc-header" v-bind:class="{ dark: night }" class="scrollable">
            <div class="tc-logo-wrapper">
                <a href="/" @click.prevent="navigateMenu('/')" class="tc-logo-link">
                    <img :src="night ? '/logo_dark.png' : '/logo.png'" class="tc-logo-img" alt="TradingCandle" />
                </a>
            </div>
            <span class="searchbar">
                <span class="chart-feed-indicator">
                    <button class="chart-feed-status" type="button" :aria-label="connector + ' · ' + feedLabel" aria-describedby="chart-feed-details">
                        <span class="chart-feed-dot" :class="{live: feedLabel === 'Live prices'}"></span>
                    </button>
                    <span id="chart-feed-details" class="chart-feed-details" role="tooltip">
                        <strong>{{ connector }} · {{ feedLabel }}</strong>
                        <span>{{ feedDescription }}</span>
                        <button v-if="feedError" @click="recoverFeed" :disabled="feedRecovering">Retry recovery</button>
                    </span>
                </span>
                <button type="button" class="symbol-search-trigger" @click="onOpen" aria-label="Search symbols">
                    <span class="searchbar-text" v-bind:class="{ dark: night }">{{ symbolDisplay }}</span>
                </button>
                <button type="button" class="symbol-compare-trigger" @click="openCompareSearch" title="Compare symbols" aria-label="Compare symbols" :aria-expanded="workspacePanel === 'compare'">+</button>
            </span>
            <span class="multi_timeframe_bar">
                <span v-for="item in regularTimeframeItems" :key="item.timeframe + '-' + timeframeRenderTick" class="tf-slot">
                    <button type="button"
                        :class="[
                            'tf-btn',
                            item.indicatorTrend ? item.className.replace(/\btc-\S+/g,'').trim() : item.className,
                            item.indicatorTrend?.className,
                            { 'tf-indicator-colored': !!item.indicatorTrend?.background },
                            { 'tf-active': timeframe === item.timeframe },
                            { 'tf-hidden': item.className && item.className.includes('p-hidden') }
                        ]"
                        :style="item.indicatorTrend ? {backgroundImage:item.indicatorTrend.background,backgroundColor:item.indicatorTrend.background?'var(--ws-bg,#fff)':undefined,color:item.indicatorTrend.foreground} : {}"
                        :title="item.indicatorTrend?.title" :aria-label="item.indicatorTrend?.title ? getTimeframeLabel(item.timeframe)+': '+item.indicatorTrend.title : getTimeframeLabel(item.timeframe)"
                        @click="e => onTimeframeSelect(item, e)">{{ item.label }}<span v-if="item.indicatorTrend?.glyph" class="tf-indicator-glyph" aria-hidden="true">{{item.indicatorTrend.glyph}}</span></button>
                </span>
                <div v-if="customTimeframeItems.length > 0" class="tf-divider tf-custom-divider"></div>
                <span v-for="item in customTimeframeItems" :key="'custom-' + item.timeframe + '-' + timeframeRenderTick" class="tf-slot">
                    <button type="button"
                        :class="[
                            'tf-btn',
                            item.indicatorTrend ? item.className.replace(/\btc-\S+/g,'').trim() : item.className,
                            item.indicatorTrend?.className,
                            { 'tf-indicator-colored': !!item.indicatorTrend?.background },
                            { 'tf-active': timeframe === item.timeframe },
                            { 'tf-hidden': item.className && item.className.includes('p-hidden') }
                        ]"
                        :style="item.indicatorTrend ? {backgroundImage:item.indicatorTrend.background,backgroundColor:item.indicatorTrend.background?'var(--ws-bg,#fff)':undefined,color:item.indicatorTrend.foreground} : {}"
                        :title="item.indicatorTrend?.title" :aria-label="item.indicatorTrend?.title ? getTimeframeLabel(item.timeframe)+': '+item.indicatorTrend.title : getTimeframeLabel(item.timeframe)"
                        @click="e => onTimeframeSelect(item, e)">{{ item.label }}<span v-if="item.indicatorTrend?.glyph" class="tf-indicator-glyph" aria-hidden="true">{{item.indicatorTrend.glyph}}</span></button>
                </span>
                <IndicatorTimeframeTrend ref="indicatorTimeframeTrend" v-if="indicatorTrendVisible" :source="workspaceSource" :sources="indicatorTrendSources"
                     :timeframes="indicatorTrendTimeframes" :intervals="indicatorTrendIntervals" :current-timeframe="timeframe" :is-dark="night" :load-scope="indicatorTrendLoadScope" @scope-change="setIndicatorTrendLoadScope" @rule-change="setIndicatorSignalRule" @source-change="setTimeframeColorSource" @updated="touchTimeframeRender" />
                <!-- Custom timeframe dropdown -->
                <span class="custom-timeframe-dropdown-wrapper">
                    <button @pointerdown.stop @click="positionTimeframeMenu($event); toggleCustomTimeframeMenu()" aria-label="Custom timeframes" class="tf-btn tf-dropdown-toggle" :class="{ active: showCustomTimeframeMenu }">▾</button>
                    <transition name="dropdown-fade">
                        <Teleport to="body"><div v-if="showCustomTimeframeMenu" class="custom-timeframe-dropdown-menu tc-timeframe-portal" :class="{ dark: night }" :style="timeframeMenuPosition">
                            <div class="dropdown-item" @click="openCustomTimeframePopover">
                                <span class="dropdown-icon">+</span>
                                <span>Add Custom Timeframe</span>
                            </div>
                            <div v-if="customTimeframes.length > 0" class="dropdown-divider"></div>
                            <div class="dropdown-item" v-for="ctf in customTimeframes" :key="ctf.id"
                                 :class="{ 'dropdown-active': timeframe === ctf.timeframe }"
                                 @click="selectCustomTimeframe(ctf)">
                                {{ ctf.name }} ({{ ctf.timeframe }})
                            </div>
                            <div v-if="customTimeframes.length > 0 && connector === 'Fyers'" class="dropdown-divider"></div>
                            <template v-for="sec in ['5s','10s','15s','30s','45s']" :key="sec"><div v-if="connector === 'Fyers'" class="dropdown-item"
                                 :class="{ 'dropdown-active': selectedSeconds === sec }"
                                 @click="selectSeconds(sec)">{{ sec }}</div></template>
                        </div>
                    </Teleport></transition>
                </span>
                <div class="tf-divider"></div>
                <!-- Chart Type Selector (icon-only) -->
                <span class="chart-type-selector-wrapper">
                    <ChartTypeSelector 
                        :is-dark="night" 
                        :current-type="currentChartType"
                        @chart-type-changed="onChartTypeChanged"
                    />
                    <button class="header-btn indicator-btn header-indicator-btn" @click="openAddIndicator" title="Indicators">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                        </svg>
                    </button>
                </span>
                <span id="chart-top-replay" class="chart-top-replay"></span>
                <div class="tf-divider"></div>
                <span class="chart-top-actions" aria-label="Chart actions">
                    <button class="header-btn" @click="workspaceAction({ action: 'exportCSV' })" title="Download chart data" aria-label="Download chart data"><WorkspaceIcon name="download" /></button>
                    <button class="header-btn" @click="workspaceAction({ action: 'snapshot' })" title="Download chart snapshot" aria-label="Download chart snapshot"><WorkspaceIcon name="camera" /></button>
                    <button class="header-btn" @click="workspaceAction({ action: 'focus' })" :aria-pressed="workspaceFocused" :title="workspaceFocused ? 'Show chart controls' : 'Focus chart'" :aria-label="workspaceFocused ? 'Show chart controls' : 'Focus chart'"><WorkspaceIcon name="focus" /></button>
                </span>
            </span>
            <WorkspaceBar ref="workspaceControls" :model="workspaceModel" @action="workspaceAction" />
            <span class="night-mode">
              <span class="header-actions">
                <span class="workspace-header-name" :title="workspaceModel.name || 'Untitled'">{{ workspaceModel.name || 'Untitled' }}</span>
                <button class="workspace-icon-button" @click="workspaceAction({ action: 'workspaces' })" title="Save and load workspaces" aria-label="Save and load workspaces"><WorkspaceIcon name="save" /></button>
                    <button
                        class="workspace-icon-button"
                        @click="workspaceAction({ action: 'fullscreen' })"
                        aria-label="Toggle fullscreen"
                        title="Toggle fullscreen"
                    >
                        <WorkspaceIcon name="fullscreen" />
                    </button>
                <!-- Confidence Donut Chart -->
                <span class="confidence-donut-wrapper">
                  <ConfidenceDonut 
                      :confidence="getCurrentConfidence()"
                      :is-dark="night"
                  />
                </span>
                <!-- Profile Menu -->
                <div class="tc-profile-wrapper">
                    <button class="tc-profile-btn" @click="userMenuOpen = !userMenuOpen" title="Profile Menu">
                        <img v-if="$clerk && $clerk.user && $clerk.user.imageUrl" :src="$clerk.user.imageUrl" class="tc-profile-img" alt="User" />
                        <span v-else class="tc-profile-fallback">{{ userInitial }}</span>
                    </button>
                    <!-- User Menu Dropdown -->
                    <transition name="menu-fade">
                        <div v-if="userMenuOpen" class="tc-user-menu" :class="{ dark: night }">
                            <div class="tc-menu-header">
                                <img :src="night ? '/logo_dark.png' : '/logo.png'" class="tc-menu-logo" alt="TradingCandle" />
                                <span class="tc-menu-brand">TradingCandle</span>
                            </div>
                            <div class="tc-menu-divider"></div>
                            <div class="tc-menu-user" v-if="$clerk && $clerk.user">
                                <img v-if="$clerk.user.imageUrl" :src="$clerk.user.imageUrl" class="tc-menu-user-avatar" alt="User" />
                                <span v-else class="tc-menu-user-avatar tc-avatar-fallback">{{ userInitial }}</span>
                                <span class="tc-menu-user-name">{{ $clerk.user.fullName || $clerk.user.firstName || 'User' }}</span>
                                <svg class="tc-menu-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                            </div>
                            <div class="tc-menu-divider"></div>
                            <a class="tc-menu-item" href="/dashboard" @click.prevent="navigateMenu('/dashboard')">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                                <span>Dashboard</span>
                            </a>
                            <a class="tc-menu-item" href="/" @click.prevent="navigateMenu('/')">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                                <span>Home</span>
                            </a>
                            <a class="tc-menu-item" href="/help" @click.prevent="navigateMenu('/help')">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                <span>Help Center</span>
                            </a>
                            <div class="tc-menu-divider"></div>
                            <div class="tc-menu-item tc-menu-toggle">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                                <span>Dark theme</span>
                                <label class="tc-toggle" @click.stop>
                                    <input type="checkbox" :checked="night" @change="toggleTheme">
                                    <span class="tc-toggle-slider"></span>
                                </label>
                            </div>
                            <div class="tc-menu-item tc-menu-toggle">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                                <span>Drawings panel</span>
                                <label class="tc-toggle" @click.stop>
                                    <input type="checkbox" :checked="showDrawingTools" @change="toggleDrawingTools">
                                    <span class="tc-toggle-slider"></span>
                                </label>
                            </div>
                            <div class="tc-menu-divider"></div>
                            <a class="tc-menu-item" href="https://tradingcandle.com/download" target="_blank" rel="noopener">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                                <span>Get Desktop App</span>
                                <svg class="tc-menu-external" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            </a>
                            <div class="tc-menu-divider"></div>
                            <button class="tc-menu-item tc-menu-signout" @click="logout">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                                <span>Sign out</span>
                            </button>
                        </div>
                    </transition>
                    <!-- Click outside overlay -->
                    <div v-if="userMenuOpen" class="tc-menu-overlay" @click="userMenuOpen = false"></div>
                </div>
              </span>
            </span>
         </div>
         <div id="app-conainer" :style="{top: top+'px'}">
             <symbol-search
                  ref="symbolSearch"
                  :is-dark="night"
                  :all-symbols="allSymbols"
                  @select="onSelect"
                  @watchlist-changed="onWatchlistChanged"
             />

             <div class="chart-layout-container">
                 <div class="chart-main-column" ref="chartMainColumn">


                 <button v-if="showDrawingTools && !drawingRailExpanded" type="button" class="drawing-sidebar-toggle" @click="drawingRailExpanded=true" aria-expanded="false" aria-controls="drawing-rail-tools" title="Expand drawing tools" aria-label="Expand drawing tools">
                   <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 7 5 5 5-5"/></svg>
                 </button>
                 <div class="workspace-chart-stage" @contextmenu="drawingMenuOpen">
                 <aside v-if="showDrawingTools && drawingRailExpanded" class="workspace-drawing-sidebar" :style="{ width: workspaceToolbarWidth + 'px' }" aria-label="Drawing tools for selected chart">

                   <div id="drawing-rail-tools" class="drawing-rail-reveal" :class="{'is-expanded':drawingRailExpanded}" :inert="!drawingRailExpanded">
                   <WorkspaceDrawingToolbar v-if="workspaceToolbarView && workspaceToolbarView.decubed" :key="workspaceActivePane" :data="workspaceToolbarView.decubed" :height="chartHeight" :colors="workspaceToolbarView.chart_props.colors" :config="workspaceToolbarView.chart_config" :tv_id="workspaceToolbarView.id" @custom-event="workspaceToolEvent">
                     <template #footer><button type="button" class="drawing-sidebar-collapse tc-util-btn" @click="drawingRailExpanded=false" aria-expanded="true" aria-controls="drawing-rail-tools" title="Collapse drawing tools" aria-label="Collapse drawing tools"><svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5-5 5 5"/></svg></button></template>
                   </WorkspaceDrawingToolbar>
                   </div>
                 </aside>
                 <div ref="workspaceChartGrid" class="workspace-chart-grid" :class="'workspace-grid-' + workspaceLayout">
                 <div class="workspace-chart-pane" :class="{ 'workspace-pane-selected': workspaceLayout > 1 && workspacePaneUserSelected && workspaceActivePane === 0 }" @pointerdown.capture="workspaceSelectPane(0)" @focusin="workspaceSelectPane(0)" aria-label="Chart 1">
                 <tc @load-profile="loadDrawingProfile" v-if="protectChart !== true" ref="tv" id="workspace-chart-0" :active="workspaceActivePane === 0" @vue:mounted="workspacePaneReady" :data="workspaceDisplayCube" :width="workspacePaneWidth" :height="workspacePaneHeight"
                     :title-txt="this.query" :key="this.resetkey"
                     :tf="this.timeframe"
                     :exchange="this.exchange"
                     :night="night"
                     :timezone="chartTimezone"
                     :legend-buttons="workspaceLegendButtons"
                     :toolbar="false"
                     :chart-config="chart_config"
                     :color-scale="colors.colorScale" :color-back="colors.colorBack"
                     :color-grid="workspacePreferences.grid ? colors.colorGrid : colors.colorBack"
                     :color-candle-up="workspacePreferences.up" :color-candle-dw="workspacePreferences.down"
                     :color-text="colors.colorText"
                     :extensions="extensions"
                     :overlays="overlays"
                     :x-settings="xsett"
                     :alerts="workspaceAllAlerts"
                     :strategy-records="strategyPaneRecords(0)" :strategy-selected-id="strategyPresentation?.selectedId" :strategy-plots="strategyPaneRecords(0).length ? strategyPresentation?.plots : {}" :strategy-times="strategyPresentation?.times" @strategy-event="strategyEvent"
                     @range-changed="workspaceRange($event, 0)"
                     @legend-button-click="onLegendButtonClick"
                     @alert-action="onChartAlertAction">
                 </tc>
                 </div>
                 <div v-for="pane in workspaceLayout - 1" :key="pane" class="workspace-chart-pane workspace-secondary-pane" :class="{ 'workspace-pane-selected': workspacePaneUserSelected && workspaceActivePane === pane }" @pointerdown.capture="workspaceSelectPane(pane)" @focusin="workspaceSelectPane(pane)" :aria-label="'Chart ' + (pane+1)">
                   <select v-model="workspaceSecondaryTf[pane-1]" class="workspace-pane-timeframe" :aria-label="'Timeframe for chart ' + (pane+1)"><option v-for="tf in Object.keys(chart)" :key="tf" :value="tf">{{ tf }}</option></select>
                   <WorkspaceChartPane @load-profile="loadDrawingProfile" v-if="chart[workspaceSecondaryTf[pane-1]] && !workspaceReplay.active" ref="workspaceSecondary" :pane="pane" :cache="workspacePaneCubes" :cache-key="workspacePaneKey(pane, workspaceSecondaryTf[pane-1])" :saved="workspaceSavedPanes[workspacePaneKey(pane, workspaceSecondaryTf[pane-1])]" :source="chart[workspaceSecondaryTf[pane-1]]" :active="workspaceActivePane === pane" :toolbar="false" :extensions="extensions" :x-settings="xsett" :legend-buttons="workspaceLegendButtons" :alerts="workspaceAllAlerts" :strategy-records="strategyPaneRecords(pane)" :strategy-selected-id="strategyPresentation?.selectedId" :strategy-plots="strategyPaneRecords(pane).length ? strategyPresentation?.plots : {}" :strategy-times="strategyPresentation?.times" @strategy-event="strategyEvent" @alert-action="onChartAlertAction" @ready="workspacePaneReady" @range-changed="workspaceRange($event, pane)" @legend-button-click="onLegendButtonClick" :id="'workspace-chart-' + pane" :data="chart[workspaceSecondaryTf[pane-1]]" :width="workspacePaneWidth" :height="workspacePaneHeight" :tf="workspaceSecondaryTf[pane-1]" :title-txt="symbol" :night="night" :timezone="chartTimezone" :color-scale="colors.colorScale" :color-back="colors.colorBack" :color-grid="workspacePreferences.grid ? colors.colorGrid : colors.colorBack" :color-text="colors.colorText" :color-candle-up="workspacePreferences.up" :color-candle-dw="workspacePreferences.down" :overlays="overlays" :chart-config="chart_config" />
                   <div v-else class="workspace-pane-empty">{{ workspaceReplay.active ? 'Replay is active in the primary chart' : 'Select a loaded timeframe' }}</div>
                 </div>
                 </div>
                 </div>
                 <StrategyDock @visibility="strategyDockOpen=$event" ref="strategyDock" :context="strategyContext" :candles="strategyCandles" :owner="$clerk?.user?.id || ''" :replay="workspaceReplay.active" @resize="updateChartDimensions" @binding="strategyBinding" @presentation="strategyPresented" @route="strategyRoute" @locate="strategyLocate" />
                 <date-range-bar
                     ref="dateRangeBar"
                     :busy="workspaceRangeBusy"
                     :is-dark="night"
                     :timezone="chartTimezone"
                     @range-select="onDateRangeSelect"
                     @timezone-change="onTimezoneChange"
                     @custom-range="onCustomRange"
                 />
                 <ChartFeaturePanel ref="featurePanel" :view="chartFeatureView" :symbol="symbolDisplay" :model="workspaceModel" @close="chartFeatureView=''; refreshChartRender()" />
                 <nav ref="chartFeatures" class="chart-feature-bar" aria-label="Chart features">
                     <div id="chart-bottom-vt-actions" class="chart-bottom-vt-actions"></div>
                     <button @click="openStrategyDock('build')" :class="{active:strategyDockOpen && ['strategies','build','results'].includes($refs.strategyDock?.tab)}" :aria-expanded="strategyDockOpen && ['strategies','build','results'].includes($refs.strategyDock?.tab)">Strategy</button>
                     <button @click="openStrategyDock('activity')" :class="{active:strategyDockOpen && ['automate','activity','alerts'].includes($refs.strategyDock?.tab)}" :aria-expanded="strategyDockOpen && ['automate','activity','alerts'].includes($refs.strategyDock?.tab)">Bots</button>
                     <button v-for="feature in ['Trade History','Positions','Performance','Notes']" :key="feature" @click="toggleChartFeature(feature)" :class="{active:chartFeatureView===feature}" :aria-expanded="chartFeatureView===feature">{{ feature }}</button>
                     <button @click="workspaceAction({ action: 'commands' })" aria-label="More chart features" title="More chart features">+</button>
                     <div id="chart-bottom-actions" class="chart-bottom-actions"></div>
                     <span class="chart-feature-market">{{ category }}<span class="chart-feature-dot"></span>{{ connector }}</span>
                 </nav>
                 </div>

                 <aside id="chart-sidebar-content" class="chart-sidebar-host" v-show="['alerts','orderflow','objects','paper'].includes(workspacePanel)" :style="{width:watchlistPanelWidth+'px'}" aria-label="Chart sidebar"></aside>
                 <aside class="right-tools-bar" :class="{ dark: night }" aria-label="Analysis tools">
                     <div class="right-tools-main">

                         <button v-if="aiChatEnabled"
                             class="right-tool-btn ai-chat-btn"
                             :class="{ active: aiChatVisible }"
                             @click="toggleAIChat"
                             title="AI Chat">
                             <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                 <rect x="3" y="11" width="18" height="10" rx="2"/>
                                 <circle cx="12" cy="5" r="2"/>
                                 <path d="M12 7v4"/>
                                 <rect x="8" y="15" width="2" height="2" rx="0.5"/>
                                 <rect x="14" y="15" width="2" height="2" rx="0.5"/>
                                 <path d="M8 7l-2-2"/>
                                 <path d="M16 7l2-2"/>
                             </svg>
                             <span>AI Chat</span>
                         </button>

                         <button
                             class="right-tool-btn watchlist-btn"
                             :class="{ active: watchlistVisible }"
                             @click="toggleWatchlist"
                             title="Watchlist">
                             <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2"
                                 stroke-linecap="round" stroke-linejoin="round">
                                 <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                             </svg>
                             <span>Watchlist</span>
                         </button>
                         <button v-if="scannerAvailable"
                             class="right-tool-btn crypto-scanner-btn"
                             :class="{ active: cryptoScannerVisible }"
                             @click="toggleCryptoScanner"
                             :title="activeScannerTitle">
                             <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2"
                                 stroke-linecap="round" stroke-linejoin="round">
                                 <path d="M3 3v18h18"/>
                                 <path d="M7 15l3-3 3 2 5-7"/>
                                 <path d="M18 7h-4"/>
                                 <path d="M18 7v4"/>
                             </svg>
                             <span>{{ activeScannerTitle }}</span>
                         </button>
                         <button class="right-tool-btn candlescript-btn"
                             :class="{ active: candleScriptVisible }"
                             @click="toggleCandleScript"
                             title="Script Editor">
                             <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2"
                                 stroke-linecap="round" stroke-linejoin="round">
                                 <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                             </svg>
                             <span>Script Editor</span>
                         </button>
                         <button v-if="$store && $store.getters.indianMarketDataEnabled && category === 'IndianMarket'"
                             class="right-tool-btn options-terminal-btn"
                             @click="openOptionsTerminal"
                             title="TradingCandle Options">
                             <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2"
                                 stroke-linecap="round" stroke-linejoin="round">
                                 <path d="M4 5h16"/>
                                 <path d="M4 12h16"/>
                                 <path d="M4 19h16"/>
                                 <path d="M8 5v14"/>
                                 <path d="M16 5v14"/>
                             </svg>
                             <span>Options</span>
                         </button>
                         <button v-for="tool in [{action:'alerts',icon:'alert',label:'Alerts'},{action:'orderflow',icon:'layers',label:'Order Book'},{action:'objects',icon:'objects',label:'Objects'},{action:'paper',icon:'paper',label:'Trading'}]" :key="tool.action" class="right-tool-btn" :class="{active:workspacePanel===tool.action}" @click="workspaceAction({action:tool.action})" :title="tool.label" :aria-label="tool.label" :aria-expanded="workspacePanel===tool.action"><WorkspaceIcon :name="tool.icon" /><span>{{ tool.label }}</span></button>
                     </div>
                     <div v-if="brokerReconnectNeeded" class="right-tools-bottom">
                         <button v-if="brokerReconnectNeeded"
                             class="right-tool-btn reconnect-broker-btn"
                             @click="triggerBrokerReconnect"
                             title="Reconnect Broker">
                             <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                 <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                 <line x1="12" y1="9" x2="12" y2="13"/>
                                 <line x1="12" y1="17" x2="12.01" y2="17"/>
                             </svg>
                             <span>Reconnect</span>
                         </button>

                     </div>
                 </aside>

                 <transition name="slide-panel">
                   <div v-if="cryptoScannerVisible" class="crypto-scanner-panel" :class="{ 'dark': night }" :style="{width:watchlistPanelWidth+'px'}">
                     <CryptoScanner
                         :is-dark="night"
                         :mode="activeScannerMode"
                         :compact="true"
                         @close="closeCryptoScanner"
                         @select="onScannerSelect"
                     />
                   </div>
                 </transition>
                 
                                  
                 <!-- Watchlist Panel -->
                 <transition name="slide-panel">
                   <div v-if="watchlistVisible" class="watchlist-panel watchlist-with-signals" :class="{ 'dark': night, 'float-mode': watchlistFloatMode, 'inline-mode': !watchlistFloatMode }" :style="{ width: watchlistPanelWidth + 'px' }">
                     <button class="tc-panel-pin" @click="toggleWatchlistFloatMode" :aria-pressed="!watchlistFloatMode" title="Pin watchlist"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m8 3 8 0-1 6 4 4H5l4-4zM12 13v8"/></svg></button>
                     <div class="ai-chat-splitter" @mousedown="startWatchlistResize" role="separator" aria-label="Resize watchlist"></div>
                     <div class="watchlist-stack-main">
                       <Watchlist
                           ref="watchlist"
                           :is-dark="night"
                           :all-symbols="allSymbols"
                           :current-symbol="symbol"
                           :current-connector="connector"
                           :is-expanded="watchlistFloatMode"
                           @close="closeWatchlist"
                           @select="onWatchlistSelect"
                           @toggle-expand="toggleWatchlistFloatMode"
                       />
                     </div>
                     <IndicatorInsights
                         :source="workspaceSource" :candles="workspaceCandles" :interval="timeframeToMs(timeframe)"
                         class="watchlist-signals-panel"
                         variant="sidebar"
                         :is-dark="night"
                         :items="signalTimeframeItems"
                         :current-timeframe="timeframe"
                         :symbol-display="symbolDisplay"
                         @select="onSignalSelect"
                     />
                   </div>
                 </transition>

                 <!-- AI Chat Panel - Right side popover -->
                 <transition name="slide-panel">
                   <div v-if="aiChatVisible && aiChatEnabled" class="ai-chat-panel" :class="{ 'dark': night, 'float-mode': aiChatFloatMode, 'inline-mode': !aiChatFloatMode }" :style="{ width: aiChatPanelWidth + 'px' }">
                     <button class="tc-panel-pin" @click="toggleAIChatFloatMode" :aria-pressed="!aiChatFloatMode" title="Pin AI Chat"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m8 3 8 0-1 6 4 4H5l4-4zM12 13v8"/></svg></button>
                     <div class="ai-chat-splitter" :class="{ dragging: splitterDragging }" @mousedown="startSplitterDrag">
                       <div class="splitter-handle"></div>
                     </div>
                     <AIChat
                         ref="aiChat"
                         :trading-context="aiTradingContext"
                         :is-dark="night"
                         :user-id="$clerk && $clerk.user ? $clerk.user.id : null"
                         @close="closeAIChat"
                         @load-chart="onAIChatLoadChart"
                         @capture-chart="onAIChatCaptureChart"
                         @toggle-float-mode="toggleAIChatFloatMode"
                     />
                   </div>
                 </transition>

                 <!-- CandleScript Builder Panel -->
                 <transition name="slide-panel">
                   <div v-if="candleScriptVisible" class="candlescript-panel" :class="{ 'dark': night, 'float-mode': candleScriptFloatMode, 'inline-mode': !candleScriptFloatMode }" :style="!candleScriptFloatMode ? { width: candleScriptPanelWidth + 'px' } : {}">
                     <button class="tc-panel-pin" @click="toggleAIChatFloatMode" :aria-pressed="!aiChatFloatMode" title="Pin AI Chat"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m8 3 8 0-1 6 4 4H5l4-4zM12 13v8"/></svg></button>
                     <div v-if="!candleScriptFloatMode" class="candlescript-splitter" :class="{ dragging: splitterDragging }" @mousedown="startCandleScriptSplitterDrag">
                       <div class="splitter-handle"></div>
                     </div>
                     <ChartIndicatorBuilder
                        ref="candleScriptBuilder"
                        :dc="workspaceDisplayCube"
                        :is-dark="night"
                        :user-id="$clerk && $clerk.user ? $clerk.user.id : null"
                        @close="toggleCandleScript"
                        @toggle-float-mode="toggleCandleScriptFloatMode"
                        @remove-candlescript-indicator="onCandleScriptIndicatorRemoved"
                     />
                   </div>
                 </transition>
             </div>
         </div>

         <!-- Broker Reconnect Modal -->
        <BrokerReconnectModal
            :visible="showBrokerReconnectModal"
            :broker="expiredBroker"
            :is-dark="night"
            @dismiss="closeBrokerReconnectModal"
            @initiate-auth="handleBrokerAuth"
            @reconnected="onBrokerReconnected"
            @connected="onBrokerConnected"
        />

        <!-- Custom Timeframe Popover -->
         <div v-if="showCustomTimeframePopover" class="custom-timeframe-popover-overlay" @click="closeCustomTimeframePopover">
             <div class="custom-timeframe-popover" :class="{ 'dark': night }" @click.stop>
                 <div class="popover-header">
                     <h3>Add Custom Timeframe</h3>
                     <button class="close-btn" @click="closeCustomTimeframePopover">×</button>
                 </div>
                 <div class="popover-content">
                     <div class="form-group">
                         <label>Name (max 2 characters):</label>
                         <input type="text" v-model="customTimeframeForm.name" maxlength="2" placeholder="e.g., X7" class="form-input" />
                     </div>
                     <div class="form-group">
                         <label>Type:</label>
                         <select v-model="customTimeframeForm.type" class="form-select">
                             <option value="minute">Minutes</option>
                             <option value="hour">Hours</option>
                             <option value="day">Days</option>
                         </select>
                     </div>
                     <div class="form-group">
                         <label>Value:</label>
                         <input type="number" v-model.number="customTimeframeForm.value" min="1" max="999" class="form-input" placeholder="e.g., 7" />
                     </div>
                     <div class="form-actions">
                         <button class="btn-cancel" @click="closeCustomTimeframePopover">Cancel</button>
                         <button class="btn-add" @click="addCustomTimeframe" :disabled="!isValidCustomTimeframe">Add</button>
                     </div>
                 </div>
             </div>
         </div>

         <!-- Broker Reconnect/Connect Popup -->
         <BrokerReconnectPopup ref="fyersPopup" :is-dark="night" @needs-reconnect="onNeedsReconnect" @reconnected="onBrokerReconnected" />
    </div>
</template>

<script>
import { chartRangeStart } from './workspace/chart-ranges.mjs';

import WorkspaceBar from './workspace/WorkspaceBar.vue'
import WorkspaceIcon from './workspace/WorkspaceIcon.vue'
import workspaceMixin from './workspace/mixin'
import liveFeedMixin from './workspace/live-feed'
import drawingActions from './workspace/drawing-actions'
import ChartContextMenu from './workspace/ChartContextMenu.vue'
import strategyChartMixin from './workspace/strategy/chart-mixin'
import './workspace/workspace.css'
import ChartFeaturePanel from './workspace/ChartFeaturePanel.vue'
import IndicatorInsights from './workspace/IndicatorInsights.vue'
import IndicatorTimeframeTrend from './workspace/IndicatorTimeframeTrend.vue'
import Vue, { markRaw } from 'vue'
import _ from 'lodash';
import Tc from './Tc.vue'
import Data from '../data/data.json'
import Cryptocurrencies from './cryptocurrencies.json'
import DataCube from '../src/helpers/datacube.js'
import Extensions from './index_dev'
import WorkspaceChartPane from './workspace/WorkspaceChartPane.vue'
import WorkspaceDrawingToolbar from './components/Toolbar.vue'
import Overlays from './indicator-catalog'
import Utils from './stuff/utils'
import binanceApi from './services/binance_service'
import aliceApi, { ALICEBLUE_SUPPORTED_TFS } from './services/aliceblue_service'
import intApi from './services/int_service'
import fyersApi, { FYERS_SUPPORTED_TFS } from './services/fyers_service'
import upstoxApi, { UPSTOX_SUPPORTED_TFS } from './services/upstox_service'
import dhanApi, { DHAN_SUPPORTED_TFS } from './services/dhan_service'
import coinbaseApi from './services/coinbase_service'
import krakenApi from './services/kraken_service'
import bybitApi from './services/bybit_service'
import okxApi from './services/okx_service'
import amfiApi from './services/amfi_service'

// The market_proxy backend endpoint these four support only the handful of
// granularities each upstream exchange actually offers (see
// server/exchanges/market_proxy.js); AMFI is daily NAV only, resampled to
// weekly/monthly server-side.
const CRYPTO_PROXY_SUPPORTED_TFS = ['1m', '5m', '15m', '1H', '4H', '1D']
const AMFI_SUPPORTED_TFS = ['1D', '1W', '1M']
import indianMarketApi from './services/indian_market_service'
import { resetBrokerReconnectDismissal } from './components/BrokerReconnectPopup.vue';
import DataProviderManager from './services/data_provider_manager'
import DataProviderSwitcher from './components/DataProviderSwitcher.vue'
import AIChat from './components/AIChat.vue'
import Watchlist from './components/Watchlist.vue'
import AIChatService from './services/ai_chat_service.js'
import ChartIndicatorBuilder from './components/indicator-builder/ChartIndicatorBuilder.vue'
import BrokerReconnectModal from './components/BrokerReconnectModal.vue'
import { getRunner as getCandleScriptRunner } from './candle-script/runner.js'
import SymbolSearch from './components/SymbolSearch.vue'
import BrokerReconnectPopup from './components/BrokerReconnectPopup.vue'
import DateRangeBar from './components/DateRangeBar.vue'
import ChartTypeSelector from './components/ChartTypeSelector.vue'
import ConfidenceDonut from './components/ConfidenceDonut.vue'
import CryptoScanner from './components/CryptoScanner.vue'
import SignalsPanel from './components/SignalsPanel.vue'
import ChartAnalysis from './workspace/ChartAnalysis.vue'
import { timeframeTrendEnabled } from './workspace/vt-visibility.mjs'
import { DEFAULT_VT_GROUP } from './services/indicatorGroups.js'

import BINANCE_SPOT from '../data/binance_spot.json'
import BINANCE_FUTURES from '../data/binance_futures.json'
import INT from '../data/international.json'

import ANT_INDICES from '../data/INDICES.json'
import ANT_NSE from '../data/NSE.json'
import ANT_NFO from '../data/NFO.json'

import { EventBus } from './EventBus';

import moment from 'moment'





const DEFAULT = `// Chart link rules
{
    '*': {
        cursor: true,
        position: true,
        tools: true
    }
}
`


// 'up', 'down', 'add',

export default {
    name: 'app',
    mixins: [workspaceMixin, liveFeedMixin, strategyChartMixin, drawingActions],
    props: ['up', 'down', 'add', 'data'],
    components: {
        ChartFeaturePanel, IndicatorInsights, IndicatorTimeframeTrend,
        WorkspaceChartPane, WorkspaceDrawingToolbar,
        WorkspaceBar,
        WorkspaceIcon,
        ChartContextMenu,
        Tc,
        ChartAnalysis,
        DataProviderSwitcher,
        AIChat,
        Watchlist,
        ChartIndicatorBuilder,
        BrokerReconnectModal,
        SymbolSearch,
        BrokerReconnectPopup,
        DateRangeBar,
        ChartTypeSelector,
        ConfidenceDonut,
        CryptoScanner,
        SignalsPanel
    },
    methods: {
        async explainChartWithAI(report) {
            if (!this.aiChatEnabled) return;
            if (!this.aiChatVisible) this.toggleAIChat();
            this.analysisVisible = false;
            await this.$nextTick();
            if (this.$refs.aiChat) this.$refs.aiChat.inputText = 'Explain market structure, important zones, indicator conflicts and the historical analogue for '+this.symbolDisplay+' '+this.timeframe+'. Use this actual chart summary, disclose limitations and avoid invented signals: '+JSON.stringify(report);
        },
        positionTimeframeMenu(event) {
            const rect = event.currentTarget.getBoundingClientRect();
            this.timeframeMenuPosition = {position:'fixed', left:Math.max(8, Math.min(rect.left, window.innerWidth-260))+'px', top:rect.bottom+6+'px', zIndex:10000};
        },
        startWatchlistResize(event) {
            const start = event.clientX, width = this.watchlistPanelWidth;
            const move = e => { this.watchlistPanelWidth = Math.max(280, Math.min(window.innerWidth-70, width+start-e.clientX)); };
            const end = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', end); };
            document.addEventListener('mousemove', move); document.addEventListener('mouseup', end); event.preventDefault();
        },
        toggleAIChat() {
            this.aiChatVisible = !this.aiChatVisible;
            if (this.aiChatVisible) {
                this.workspacePanel = null;
                // Keep one right-side panel open at a time.
                this.candleScriptVisible = false;
                this.cryptoScannerVisible = false;
                this.watchlistVisible = false;
                this.updateAITradingContext();
            }
        },
                closeAIChat() {
            this.aiChatVisible = false;
        },
        toggleWatchlist() {
            this.watchlistVisible = !this.watchlistVisible;
            if (this.watchlistVisible) {
                this.workspacePanel = null;
                // Keep one right-side panel open at a time.
                this.cryptoScannerVisible = false;
                this.aiChatVisible = false;
                this.candleScriptVisible = false;
            }
        },
        closeWatchlist() {
            this.watchlistVisible = false;
        },
        onSignalSelect(item) {
            if (!item) return;
            this.onTimeframeSelect(item, null);
        },
        toggleWatchlistFloatMode() {
            this.watchlistFloatMode = !this.watchlistFloatMode;
        },
        onWatchlistSelect(item) {
            this.onSelect(item);
        },
        onWatchlistChanged() {
            // Refresh the Watchlist panel if it's open so it reflects star changes
            if (this.$refs.watchlist) {
                this.$refs.watchlist.loadWatchlist();
            }
        },
        toggleCryptoScanner() {
            if (!this.scannerAvailable) return;
            this.cryptoScannerVisible = !this.cryptoScannerVisible;
            if (this.cryptoScannerVisible) {
                this.workspacePanel = null;
                // Keep one right-side panel open at a time.
                this.aiChatVisible = false;
                this.candleScriptVisible = false;
                this.watchlistVisible = false;
            }
        },
        closeCryptoScanner() {
            this.cryptoScannerVisible = false;
        },
        onScannerSelect(item) {
            this.cryptoScannerVisible = false;
            this.onSelect(item);
        },
        toggleAIChatFloatMode() {
            this.aiChatFloatMode = !this.aiChatFloatMode;
        },
        toggleCandleScriptFloatMode() {
            this.candleScriptFloatMode = !this.candleScriptFloatMode;
        },
        startSplitterDrag(event) {
            this.splitterDragging = true;
            this.startX = event.clientX;
            this.startWidth = this.aiChatPanelWidth;
            
            document.addEventListener('mousemove', this.onSplitterDrag);
            document.addEventListener('mouseup', this.endSplitterDrag);
            event.preventDefault();
        },
        onSplitterDrag(event) {
            if (!this.splitterDragging) return;
            
            const deltaX = this.startX - event.clientX;
            const newWidth = Math.max(300, Math.min(600, this.startWidth + deltaX));
            this.aiChatPanelWidth = newWidth;
        },
        endSplitterDrag() {
            this.splitterDragging = false;
            document.removeEventListener('mousemove', this.onSplitterDrag);
            document.removeEventListener('mouseup', this.endSplitterDrag);
        },
        startCandleScriptSplitterDrag(event) {
            this.splitterDragging = true;
            this.startX = event.clientX;
            this.startWidth = this.candleScriptPanelWidth;
            
            document.addEventListener('mousemove', this.onCandleScriptSplitterDrag);
            document.addEventListener('mouseup', this.endCandleScriptSplitterDrag);
            event.preventDefault();
        },
        onCandleScriptSplitterDrag(event) {
            if (!this.splitterDragging) return;
            
            const deltaX = this.startX - event.clientX;
            const newWidth = Math.max(500, Math.min(800, this.startWidth + deltaX));
            this.candleScriptPanelWidth = newWidth;
        },
        endCandleScriptSplitterDrag() {
            this.splitterDragging = false;
            document.removeEventListener('mousemove', this.onCandleScriptSplitterDrag);
            document.removeEventListener('mouseup', this.endCandleScriptSplitterDrag);
        },
        
        // Broker reconnect methods
        checkFyersConnection() {
            this.fyersConnected = fyersApi.isAuthenticated();
            if (!this.fyersConnected) {
                return fyersApi.restoreSessionFromServer().then(restored => {
                    if (restored) this.fyersConnected = true;
                    return restored;
                }).catch(() => false);
            }
            return Promise.resolve(true);
        },

        checkUpstoxConnection() {
            this.upstoxConnected = upstoxApi.isAuthenticated();
            if (!this.upstoxConnected) {
                return upstoxApi.restoreSessionFromServer().then(restored => {
                    if (restored) this.upstoxConnected = true;
                    return restored;
                }).catch(() => false);
            }
            return Promise.resolve(true);
        },

        checkAliceBlueConnection() {
            this.aliceblueConnected = aliceApi.isAuthenticated();
            if (!this.aliceblueConnected) {
                return aliceApi.restoreSessionFromServer().then(restored => {
                    if (restored) this.aliceblueConnected = true;
                    return restored;
                }).catch(() => false);
            }
            return Promise.resolve(true);
        },

        checkDhanConnection() {
            this.dhanConnected = dhanApi.isAuthenticated();
            if (!this.dhanConnected) {
                return dhanApi.restoreSessionFromServer().then(restored => {
                    if (restored) this.dhanConnected = true;
                    return restored;
                }).catch(() => false);
            }
            return Promise.resolve(true);
        },

        async checkBrokerSessions() {
            const brokers = [
                { name: 'fyers', service: fyersApi, connected: this.fyersConnected },
                { name: 'upstox', service: upstoxApi, connected: this.upstoxConnected },
                { name: 'aliceblue', service: aliceApi, connected: this.aliceblueConnected },
                { name: 'dhan', service: dhanApi, connected: this.dhanConnected }
            ];

            for (const broker of brokers) {
                if (broker.connected) {
                    try {
                        const validation = await broker.service.validateSession();
                        if (!validation.valid && validation.expired) {
                            this.showBrokerReconnectDialog(broker.name);
                            break; // Show one at a time
                        }
                    } catch (error) {
                        console.error(`[${broker.name}] Error checking session:`, error);
                    }
                }
            }
        },
        
        showBrokerReconnectDialog(broker) {
            this.expiredBroker = broker;
            this.showBrokerReconnectModal = true;
        },
        
        closeBrokerReconnectModal() {
            this.showBrokerReconnectModal = false;
            this.expiredBroker = null;
        },
        
        async handleBrokerAuth(broker, resolve, reject) {
            try {
                const service = this.getBrokerService(broker);
                
                // The server uses stored/env credentials automatically.
                // If credentials are missing, the auth-url endpoint will return an error
                // indicating the user needs to save credentials first.
                let authUrl;
                try {
                    authUrl = await service.getAuthUrl();
                } catch (credErr) {
                    if (credErr.response?.data?.credentialSource === 'user') {
                        // No stored credentials — redirect to dashboard to set them up
                        reject(new Error('Please save your API credentials in the Dashboard first.'));
                        this.$router.push('/dashboard/brokers');
                        return;
                    }
                    throw credErr;
                }
                
                // Open auth window
                const authWindow = window.open(authUrl, 'broker-auth', 'width=800,height=600,scrollbars=yes,resizable=yes');
                
                if (!authWindow) {
                    reject(new Error('Popup blocked. Please allow popups for this site.'));
                    return;
                }
                
                // Listen for auth success
                const checkAuth = setInterval(async () => {
                    if (authWindow.closed) {
                        clearInterval(checkAuth);
                        
                        // Small delay to ensure callback has been processed
                        await new Promise(r => setTimeout(r, 1500));
                        
                        try {
                            await service.completeLogin();
                            resolve();
                        } catch (completeErr) {
                            reject(new Error(completeErr.response?.data?.error || completeErr.message || 'Authentication was not completed.'));
                        }
                    }
                }, 1000);
                
                // Safety timeout
                setTimeout(() => {
                    clearInterval(checkAuth);
                    reject(new Error('Authentication timed out.'));
                }, 300000);
                
            } catch (error) {
                reject(error);
            }
        },
        
        getBrokerService(broker) {
            const services = {
                fyers: fyersApi,
                upstox: upstoxApi,
                aliceblue: aliceApi,
                dhan: dhanApi
            };
            return services[broker];
        },
        
        onBrokerReconnected(broker) {
            this.brokerReconnectNeeded = false;
            this.closeBrokerReconnectModal();
            // Refresh data connection
            this.reloadData();
        },
        
        onBrokerConnected(broker) {
            this.brokerReconnectNeeded = false;
            this.closeBrokerReconnectModal();
            // Refresh data connection
            this.reloadData();
        },
        
        async reloadData() {
            // Trigger data reload for current symbol and timeframe
            if (this.symbol && this.timeframe) {
                await this.loadMoreHistory(true);
            }
        },
        
        triggerBrokerReconnect() {
            // Re-show the reconnect popup
            if (this.$refs.fyersPopup) {
                this.$refs.fyersPopup.visible = true;
            }
        },
        
        onNeedsReconnect(brokerKey) {
            this.brokerReconnectNeeded = true;
        },
        toggleCandleScript() {
            this.candleScriptVisible = !this.candleScriptVisible;
            if (this.candleScriptVisible) {
                this.workspacePanel = null;
                // Keep one right-side panel open at a time.
                this.aiChatVisible = false;
                this.cryptoScannerVisible = false;
                this.watchlistVisible = false;
            }
        },
        openStrategyBuilder() { this.openStrategyDock('build'); },
        openOptionsTerminal() {
            if (this.category !== 'IndianMarket') return;
            this.$router.push({ path: '/options', query: { instrument: this.pairId, symbol: this.symbol, name: this.name, exchange: this.exchange } });
        },
        _reapplyCandleScriptIndicators() {
            const runner = getCandleScriptRunner();
            if (!runner.hasApplied()) return;
            const dc = this.chart[this.timeframe];
            if (!dc) return;
            // Run async - don't block the UI
            runner.reapply(dc).then(() => {
                // Force reactivity update
                this.resetkey++;
            }).catch(e => {
                console.warn('[Chart] CandleScript reapply failed:', e);
            });
        },
        onLegendButtonClick(event) {
            if (!event || event.button !== 'remove') return;
            const dc = this.workspaceToolbarView?.$props.data || this.chart[this.timeframe];
            const ov = dc && dc.data && dc.data[event.type]
                ? dc.data[event.type][event.dataIndex]
                : null;
            if (!ov) return;

            const settings = ov.settings || {};
            const runner = getCandleScriptRunner();
            const applied = runner.getApplied();
            const match = applied.find(ind =>
                (settings.candleScriptId && ind.id === settings.candleScriptId) ||
                (settings.candleScriptName && ind.name === settings.candleScriptName) ||
                (settings.group && ind.name === settings.group) ||
                this.isCandleScriptOverlayMatch(ov, ind.id, ind.name)
            );
            if (!match) return;

            runner.unregisterByIdOrName(match.id, match.name);

            // The legend extension removes the clicked overlay after this event
            // bubbles. Clean the rest of that CandleScript group afterward.
            this.$nextTick(() => {
                setTimeout(() => {
                    this.removeCandleScriptOverlaysFromAll(match.id, match.name);
                    this.resetkey++;
                }, 0);
            });
        },
        isCandleScriptOverlayMatch(ov, id, name) {
            if (!ov) return false;
            const settings = ov.settings || {};
            if (id && settings.candleScriptId === id) return true;
            if (name && settings.candleScriptName === name) return true;
            if (name && settings.group === name) return true;
            return !!(name && ov.name && (
                ov.name === name ||
                ov.name.startsWith(name + ' —') ||
                ov.name.startsWith(name + ' -')
            ));
        },
        removeCandleScriptOverlaysFromData(data, id, name) {
            if (!data) return;
            ['onchart', 'offchart'].forEach(side => {
                const arr = data[side];
                if (!Array.isArray(arr)) return;
                for (let i = arr.length - 1; i >= 0; i--) {
                    if (this.isCandleScriptOverlayMatch(arr[i], id, name)) {
                        arr.splice(i, 1);
                    }
                }
            });
        },
        removeCandleScriptOverlaysFromAll(id, name) {
            Object.values(this.chart || {}).forEach(dc => {
                if (!dc || !dc.data) return;
                this.removeCandleScriptOverlaysFromData(dc.data, id, name);
                if (dc.update_ids) dc.update_ids();
            });
            Object.values(this.dataset || {}).forEach(data => {
                this.removeCandleScriptOverlaysFromData(data, id, name);
            });
            if (this._savedDrawings) {
                this.removeCandleScriptOverlaysFromData(this._savedDrawings, id, name);
            }
        },
        onCandleScriptIndicatorRemoved(indicator) {
            if (!indicator) return;
            this.removeCandleScriptOverlaysFromAll(indicator.id, indicator.name);
            this.resetkey++;
        },
        toggleTheme() {
            this.$store.dispatch('toggleTheme');
        },
        // Custom timeframe methods
        toggleCustomTimeframeMenu() {
            this.showCustomTimeframeMenu = !this.showCustomTimeframeMenu;
            this.showSecondsMenu = false; // Close seconds menu if open
            this.loadCustomTimeframes();
        },
        openCustomTimeframePopover() {
            this.showCustomTimeframePopover = true;
            this.showCustomTimeframeMenu = false;
            this.resetCustomTimeframeForm();
        },
        closeCustomTimeframePopover() {
            this.showCustomTimeframePopover = false;
            this.resetCustomTimeframeForm();
        },
        resetCustomTimeframeForm() {
            this.customTimeframeForm = {
                name: '',
                type: 'minute',
                value: null
            };
        },
        getCustomTimeframe(tf) {
            return this.customTimeframes.find(ctf => ctf.timeframe === tf);
        },
        getTimeframeLabel(tf) {
            const customTimeframe = this.getCustomTimeframe(tf);
            return customTimeframe ? customTimeframe.name : tf;
        },
        setIndicatorTrendLoadScope(value) {
            this.indicatorTrendLoadScope=value==='current'?'current':'all';
            try{localStorage.setItem('tc_timeframe_trend_scope',this.indicatorTrendLoadScope)}catch(_){}
            for(const source of Object.values(this.indicatorTrendSources))for(const marker of [...(source?.onchart||[]),...(source?.offchart||[])])
                if(marker.type==='TimeframeTrend')this.$set(marker.settings,'trendScope',this.indicatorTrendLoadScope);
            if(this.indicatorTrendLoadScope==='all')this.ensureIndicatorTrendTimeframes();
        },
        ensureIndicatorTrendTimeframes() {
            if(!this.indicatorTrendVisible || this.indicatorTrendLoadScope!=='all')return;
            if(this._trendHistoryLoad)return this._trendHistoryLoad;
            const generation=this._loadGeneration;
            this._trendHistoryLoad=(async()=>{
                try{await this._timeframePreloadPromise}catch(_){}
                for(const tf of this.indicatorTrendTimeframes) {
                    if(generation!==this._loadGeneration || !this.indicatorTrendVisible || this.indicatorTrendLoadScope!=='all')return;
                    if(this.hasTimeframeCandles(tf))continue;
                    try{await this.autoLoadTimeframe(tf,this.category,generation)}catch(error){console.warn('[Timeframe Trend] History unavailable for '+tf,error.message)}
                    if(['Fyers','Upstox','Indian','AliceBlue'].includes(this.connector))await new Promise(resolve=>setTimeout(resolve,1200));
                }
            })().finally(()=>{this._trendHistoryLoad=null});
            return this._trendHistoryLoad;
        },
        setTimeframeColorSource(value) {
            const marker=[...(this.workspaceSource?.onchart||[]),...(this.workspaceSource?.offchart||[])].find(item=>item.type==='TimeframeTrend');
            if(marker){this.$set(marker.settings,'trendSource',value);this.$set(marker.settings,'trendColorVersion',2);}
        },
        setIndicatorSignalRule({id,config}) {
            const overlays=[...(this.workspaceSource?.onchart||[]),...(this.workspaceSource?.offchart||[])];
            const overlay=overlays.find(item=>(item.settings?.$uuid||item.id)===id);
            if(overlay) {
                this.$set(overlay.settings,'signalConfig',config);
                if(overlay.settings.candleScriptId) {
                    getCandleScriptRunner().setSignalConfig(overlay.settings.candleScriptId,config);
                    for(const source of Object.values(this.indicatorTrendSources))for(const item of [...(source?.onchart||[]),...(source?.offchart||[])])
                        if(item.settings?.candleScriptId===overlay.settings.candleScriptId)this.$set(item.settings,'signalConfig',config);
                }
            }
        },
        makeTimeframeItem(tf, source = {}) {
            const customTimeframe = this.getCustomTimeframe(tf);
            let className = source.className || (customTimeframe ? 'custom-timeframe' : '');
            let label = customTimeframe ? customTimeframe.name : (source.nativeLabel || source.label || tf);
            const nativeLabel=label;
            // The VT buy/sell trend coloring (tc-*) and its signal glyph in the
            // label are a VT indicator look (Timeframe Trend, part of the VT
            // group) — only show them while that indicator is on this timeframe.
            if (!this.isTimeframeTrendActive(tf)) {
                className = className.replace(/\btc-\S+/g, '').replace(/\s+/g, ' ').trim();
                label = String(label).replace(/[⇡⇣✓◦]/g, '').trim();
            }
            const indicatorTrend=this.indicatorTrendVisible ? this.$refs.indicatorTimeframeTrend?.tab(tf) : null;
            if(indicatorTrend?.className || indicatorTrend?.background)label=String(label).replace(/[⇡⇣✓◦]/g,'').trim();
            return {
                ...source,
                indicatorTrend,
                nativeLabel,
                className,
                label,
                value: tf,
                timeframe: tf,
                entry: source.entry || {},
                isCustom: !!customTimeframe
            };
        },
        isTimeframeTrendActive(tf) {
            // Live DataCube settings change when VT is hidden or removed. Cached
            // datasets do not reliably contain those visibility changes.
            const active = this.chart && this.chart[this.timeframe];
            const data = active && active.data ? active.data : this.dataset && this.dataset[this.timeframe];
            return timeframeTrendEnabled(data);
        },
        touchTimeframeRender() {
            this.timeframeRenderTick += 1;
        },
        setTimeframeProcessed(tf, data) {
            if (!data) return null;
            const item = this.makeTimeframeItem(tf, {
                ...data,
                timeframe: tf
            });
            Vue.set(this.timeframes, tf, item);
            Vue.set(this.loaded, tf, true);
            this.touchTimeframeRender();
            // Emit timeframe data for MTFTargets and LSR overlays
            EventBus.$emit('timeframes-updated', this.timeframes);
            return item;
        },
        loadCustomTimeframes() {
            const stored = localStorage.getItem('tc_custom_timeframes');
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    this.customTimeframes = Array.isArray(parsed) ? parsed.map(ctf => ({
                        ...ctf,
                        name: String(ctf.name || ctf.timeframe || '').toUpperCase(),
                        timeframe: ctf.timeframe || this.generateCustomTimeframeString(ctf.type, ctf.value)
                    })).filter(ctf => ctf.name && ctf.timeframe) : [];
                } catch (e) {
                    console.warn('Failed to load custom timeframes:', e);
                    this.customTimeframes = [];
                }
            } else {
                this.customTimeframes = [];
            }
            // Ensure default ST/MT/LT timeframes are always present
            this.ensureDefaultCustomTimeframes();
        },
        ensureDefaultCustomTimeframes() {
            const defaults = [
                { id: 'default_st', name: 'ST', type: 'minute', value: 4, timeframe: '4m' },
                { id: 'default_mt', name: 'MT', type: 'minute', value: 40, timeframe: '40m' },
                { id: 'default_lt', name: 'LT', type: 'minute', value: 400, timeframe: '400m' }
            ];
            let added = false;
            for (const def of defaults) {
                const exists = this.customTimeframes.some(
                    ctf => ctf.timeframe === def.timeframe || ctf.name === def.name
                );
                if (!exists) {
                    this.customTimeframes.push(def);
                    added = true;
                }
            }
            if (added) {
                this.saveCustomTimeframes();
            }
        },
        saveCustomTimeframes() {
            localStorage.setItem('tc_custom_timeframes', JSON.stringify(this.customTimeframes));
        },
        // Chart type methods
        onChartTypeChanged(chartType) {
            this.currentChartType = chartType;
            // Persist the chart type selection
            localStorage.setItem('tc_chart_type', chartType);
            this.updateMainChartType(chartType);
        },
        // Confidence methods
        getCurrentConfidence() {
            if (!this.aiTradingContext || !this.aiTradingContext.allTimeframeData || !this.timeframe) {
                return 0;
            }
            return this.aiTradingContext.allTimeframeData[this.timeframe]?.confidence || 0;
        },
        // Get the overlay name for a chart type id
        _getChartTypeOverlay(chartType) {
            const chartTypeMap = {
                'bars': 'XOhlcBars',
                'candles': 'XCandles',
                'standard_candles': 'Candles',
                'hollow_candles': 'HollowCandles',
                'volume_candles': 'VolumeCandles',
                'line': 'LineChart',
                'line_markers': 'LineMarkers',
                'step_line': 'StepLine',
                'area': 'AreaChart',
                'hlc_area': 'HLCArea',
                'baseline': 'Baseline',
                'columns': 'Columns',
                'high_low': 'HighLow',
                'heikin_ashi': 'HeikinAshi',
                'renko': 'Renko',
                'line_break': 'LineBreak',
                'kagi': 'Kagi',
                'point_and_figure': 'PointAndFigure',
                'range': 'RangeChart'
            };
            return chartTypeMap[chartType] || 'XCandles';
        },
        // Apply the current chart type to a specific timeframe's dataset
        _applyCurrentChartType(tf) {
            const overlayName = this._getChartTypeOverlay(this.currentChartType);
            if (this.dataset[tf] && this.dataset[tf].chart) {
                this.dataset[tf].chart.type = overlayName;
            }
            // Also apply to the live DataCube if it exists
            const dc = this.chart[tf];
            if (dc && dc.data && dc.data.chart) {
                dc.data.chart.type = overlayName;
            }
        },
        updateMainChartType(chartType) {
            const overlayName = this._getChartTypeOverlay(chartType);
            
            // Apply to ALL loaded timeframes
            for (const tf in this.chart) {
                const dc = this.chart[tf];
                if (dc && dc.data && dc.data.chart) {
                    dc.data.chart.type = overlayName;
                }
            }
            
            // Also update all datasets so it persists across timeframe switches
            for (const tf in this.dataset) {
                if (this.dataset[tf] && this.dataset[tf].chart) {
                    this.dataset[tf].chart.type = overlayName;
                }
            }
            
            // Force chart refresh
            this.resetkey++;
        },
        addCustomTimeframe() {
            if (!this.isValidCustomTimeframe) return;
            
            const timeframe = this.generateCustomTimeframeString(
                this.customTimeframeForm.type,
                this.customTimeframeForm.value
            );
            
            const newCustomTimeframe = {
                id: Date.now().toString(),
                name: this.customTimeframeForm.name.toUpperCase(),
                type: this.customTimeframeForm.type,
                value: this.customTimeframeForm.value,
                timeframe: timeframe
            };
            
            this.customTimeframes.push(newCustomTimeframe);
            this.saveCustomTimeframes();
            this.closeCustomTimeframePopover();
            
            // Add to timeframes object
            Vue.set(this.timeframes, timeframe, this.makeTimeframeItem(timeframe, {
                className: 'custom-timeframe',
                label: newCustomTimeframe.name,
                entry: {}
            }));
            this.touchTimeframeRender();
            
            // Select the new custom timeframe
            this.selectCustomTimeframe(newCustomTimeframe);
        },
        selectCustomTimeframe(customTimeframe) {
            const item = {
                timeframe: customTimeframe.timeframe,
                label: customTimeframe.name,
                className: 'custom-timeframe',
                isCustom: true
            };
            this.onTimeframeSelect(item, null);
            this.showCustomTimeframeMenu = false;
        },
        generateCustomTimeframeString(type, value) {
            const suffix = type === 'minute' ? 'm' : type === 'hour' ? 'H' : 'D';
            return `${value}${suffix}`;
        },
        timeframeToMs(tf) {
            if (!tf || typeof tf !== 'string') return 0;
            const value = parseInt(tf, 10) || 1;
            if (tf.includes('s')) return value * 1000;
            if (tf.includes('m')) return value * 60 * 1000;
            if (tf.includes('H') || tf.includes('h')) return value * 60 * 60 * 1000;
            if (tf.includes('D') || tf.includes('d')) return value * 24 * 60 * 60 * 1000;
            if (tf.includes('W') || tf.includes('w')) return value * 7 * 24 * 60 * 60 * 1000;
            if (tf.includes('M')) return value * 30 * 24 * 60 * 60 * 1000;
            return 0;
        },
        customTimeframeToMs(customTimeframe) {
            if (!customTimeframe) return 0;
            const value = Number(customTimeframe.value) || parseInt(customTimeframe.timeframe, 10) || 1;
            if (customTimeframe.type === 'minute') return value * 60 * 1000;
            if (customTimeframe.type === 'hour') return value * 60 * 60 * 1000;
            if (customTimeframe.type === 'day') return value * 24 * 60 * 60 * 1000;
            return this.timeframeToMs(customTimeframe.timeframe);
        },
        getTimeframeCandles(tf) {
            const dc = this.chart[tf];
            if (dc && dc.data && dc.data.chart && Array.isArray(dc.data.chart.data) && dc.data.chart.data.length > 0) {
                return dc.data.chart.data;
            }
            if (this.dataset[tf] && this.dataset[tf].chart && Array.isArray(this.dataset[tf].chart.data) && this.dataset[tf].chart.data.length > 0) {
                return this.dataset[tf].chart.data;
            }
            return [];
        },
        canDeriveCustomFromBase(customTimeframe, baseTf) {
            const targetMs = this.customTimeframeToMs(customTimeframe);
            const baseMs = this.timeframeToMs(baseTf);
            return baseMs > 0 && targetMs > baseMs && targetMs % baseMs === 0;
        },
        getBaseTimeframeCandidates(customTimeframe) {
            const providerTfs = Object.keys(this.timeframes || {}).filter(tf => !this.getCustomTimeframe(tf));
            const fallbackTfs = ['1m', '2m', '3m', '5m', '10m', '15m', '20m', '30m', '1H', '2H', '4H', '1D'];
            const candidates = [...providerTfs, ...fallbackTfs]
                .filter((tf, index, arr) => arr.indexOf(tf) === index)
                .filter(tf => this.canDeriveCustomFromBase(customTimeframe, tf));
            return candidates.sort((a, b) => this.timeframeToMs(a) - this.timeframeToMs(b));
        },
        getBestLoadedBaseTimeframe(customTimeframe) {
            let best = null;
            let bestCount = -1;
            this.getBaseTimeframeCandidates(customTimeframe).forEach(tf => {
                const candles = this.getTimeframeCandles(tf);
                if (!candles.length) return;
                const projectedCount = this.aggregateTimeframeData(candles, customTimeframe.type, customTimeframe.value).length;
                if (projectedCount > bestCount) {
                    best = tf;
                    bestCount = projectedCount;
                }
            });
            return best;
        },
        removeCustomTimeframe(id) {
            const index = this.customTimeframes.findIndex(ctf => ctf.id === id);
            if (index !== -1) {
                const removed = this.customTimeframes[index];
                this.customTimeframes.splice(index, 1);
                this.saveCustomTimeframes();
                
                // Remove from timeframes object
                Vue.delete(this.timeframes, removed.timeframe);
                this.touchTimeframeRender();
            }
        },
        initializeCustomTimeframes() {
            // Add existing custom timeframes to the timeframes object
            this.customTimeframes.forEach(ctf => {
                Vue.set(this.timeframes, ctf.timeframe, this.makeTimeframeItem(ctf.timeframe, {
                    className: 'custom-timeframe',
                    label: ctf.name,
                    entry: {}
                }));
                this.touchTimeframeRender();
            });
        },
        readSavedIndicatorGroups() {
            try {
                const groups = JSON.parse(localStorage.getItem('tc_saved_indicator_groups') || '[]');
                return this.withDefaultIndicatorGroups(Array.isArray(groups) ? groups : []);
            } catch (e) {
                return this.withDefaultIndicatorGroups([]);
            }
        },
        withDefaultIndicatorGroups(groups) {
            const hasDefault = groups.some(group =>
                group && (group.id === 'default_vt' || group.name === 'VT' || group.isDefault)
            );
            if (hasDefault) return groups;

            return [_.cloneDeep(DEFAULT_VT_GROUP), ...groups];
        },
        overlayGroupName(overlay) {
            if (!overlay) return undefined;
            return (overlay.settings && overlay.settings.group) || overlay.group;
        },
        findDefaultOverlayTemplate(indicator) {
            const sides = ['onchart', 'offchart'];
            for (const side of sides) {
                const list = Data[side] || [];
                const found = list.find(ov =>
                    ov.type === indicator.type ||
                    (indicator.name && ov.name === indicator.name)
                );
                if (found) return { side, overlay: found };
            }
            return null;
        },
        applyPersistedDefaultIndicatorGroups(data) {
            if (!data) return;
            const groups = this.readSavedIndicatorGroups();
            const defaultGroups = groups.filter(group =>
                group && Array.isArray(group.indicators) &&
                group.isDefault === true &&
                !(this.isVTGroup(group) && this.workspaceVTEnabled === false)
            );

            // The Visual Trading toggle is an explicit, per-user choice that must
            // survive timeframe switches: never let the generic "default group"
            // auto-apply (below, and in the saved/session restore paths) bring
            // VT back after it was turned off, and strip it out if it somehow
            // ended up in a freshly loaded/restored dataset while disabled.
            if (this.workspaceVTEnabled === false) {
                this.removeIndicatorGroupFromData(data, 'VT');
            }

            if (!defaultGroups.length) return;

            for (const group of defaultGroups) {
                this.applyIndicatorGroupToData(data, group);
            }
        },
        isVTGroup(group) {
            return !!group && (group.id === 'default_vt' || group.name === 'VT');
        },
        removeIndicatorGroupFromData(data, groupName) {
            if (!data) return;
            ['onchart', 'offchart'].forEach(side => {
                if (Array.isArray(data[side])) {
                    data[side] = data[side].filter(ov => this.overlayGroupName(ov) !== groupName);
                }
            });
        },
        applyIndicatorGroupToData(data, group) {
            if (!data || !group || !Array.isArray(group.indicators)) return;
            const groupName = group.name || 'VT';

            for (const indicator of group.indicators) {
                if (!indicator || indicator.hidden) continue;
                const template = this.findDefaultOverlayTemplate(indicator);
                const side = indicator.side === 'offchart'
                    ? 'offchart'
                    : (template && template.side === 'offchart' ? 'offchart' : 'onchart');
                if (!data[side]) Vue.set(data, side, []);

                // Only add if not already present (don't remove existing indicators).
                // Use overlayGroupName() (not ov.settings.group directly) since some
                // overlays — e.g. data.json's seed template — tag their group on the
                // overlay itself (ov.group) rather than inside ov.settings; checking
                // settings.group only made this miss those and add a duplicate.
                const alreadyExists = data[side].some(ov =>
                    ov.type === indicator.type &&
                    this.overlayGroupName(ov) === groupName
                );
                if (alreadyExists) continue;

                const base = template
                    ? _.cloneDeep(template.overlay)
                    : { name: indicator.name || indicator.type, type: indicator.type, data: [], settings: {} };
                base.name = indicator.name && indicator.name !== indicator.type
                    ? indicator.name
                    : (base.name || indicator.type);
                base.type = indicator.type || base.type;
                base.group = groupName;
                base.data = [];
                base.settings = {
                    ...(base.settings || {}),
                    ...(indicator.settings || {}),
                    group: groupName
                };
                data[side].push(base);
            }
        },
        workspaceVTGroup() {
            const groups = this.readSavedIndicatorGroups();
            return groups.find(group => this.isVTGroup(group)) || _.cloneDeep(DEFAULT_VT_GROUP);
        },
        workspaceToggleVT() {
            const data = this.workspaceDisplayCube?.data;
            if (!data) throw new Error('Load chart data first.');
            const group = this.workspaceVTGroup();
            const groupName = group.name || 'VT';

            this.workspaceVTEnabled = !this.workspaceVTEnabled;
            try {
                localStorage.setItem(
                    'tc_workspace_vt_enabled',
                    JSON.stringify(this.workspaceVTEnabled)
                );
            } catch (_) {
                // ignore storage errors; the in-memory flag still drives this session
            }

            if (!this.workspaceVTEnabled) {
                this.removeIndicatorGroupFromData(data, groupName);
                this.workspaceNotify('Visual Trading indicators removed.', 'layers');
            } else {
                this.applyIndicatorGroupToData(data, group);
                this.workspaceNotify('Visual Trading indicators added.', 'layers');
            }
            this.workspaceDataChanged();
            this.refreshChartRender();
        },
        workspaceVTIndicatorList() {
            const group = this.workspaceVTGroup();
            return (group.indicators || []).map((indicator, index) => ({
                index,
                type: indicator.type,
                name: indicator.name || indicator.type,
                visible: !indicator.hidden,
            }));
        },
        workspaceSaveVTGroup(group) {
            const groups = this.readSavedIndicatorGroups();
            const index = groups.findIndex(g => this.isVTGroup(g));
            if (index === -1) groups.unshift(group);
            else groups[index] = group;
            try {
                localStorage.setItem('tc_saved_indicator_groups', JSON.stringify(groups));
            } catch (_) {
                // ignore storage errors; the toggle still applies for this session
            }
        },
        removeOverlayFromData(data, groupName, type) {
            if (!data) return;
            ['onchart', 'offchart'].forEach(side => {
                if (Array.isArray(data[side])) {
                    data[side] = data[side].filter(ov =>
                        !(this.overlayGroupName(ov) === groupName && ov.type === type)
                    );
                }
            });
        },
        workspaceToggleVTIndicator(index) {
            const group = this.workspaceVTGroup();
            const indicator = group.indicators && group.indicators[index];
            if (!indicator) return;

            indicator.hidden = !indicator.hidden;
            this.workspaceSaveVTGroup(group);

            // Keep the currently displayed chart in sync immediately,
            // without disturbing any of the other VT indicators.
            if (this.workspaceVTEnabled) {
                const data = this.workspaceDisplayCube?.data;
                const groupName = group.name || 'VT';
                if (data) {
                    if (indicator.hidden) {
                        this.removeOverlayFromData(data, groupName, indicator.type);
                    } else {
                        this.applyIndicatorGroupToData(data, {
                            name: groupName,
                            indicators: [indicator],
                        });
                    }
                    this.workspaceDataChanged();
                    this.refreshChartRender();
                }
            }

            this.workspaceNotify(
                (indicator.name || indicator.type) +
                    (indicator.hidden ? ' hidden.' : ' shown.'),
                'layers'
            );
        },
        applyPersistedDefaultsToTimeframe(tf) {
            // Only compute default indicator groups (VT, etc.) for the
            // timeframe the user is actually looking at. This also gets
            // called for every background-preloaded timeframe (up to 15 of
            // them); applying a 10-indicator custom-script group to all of
            // them at once means that many Web Workers all doing a full
            // history backfill simultaneously, which is why indicators
            // could take 10+ seconds to show up even though nothing was
            // actually broken. onTimeframeSelect() already calls this again
            // for whichever timeframe becomes active, so deferring here
            // costs nothing — same guard rerunIndicatorScripts() already
            // uses for the same reason.
            if (tf !== this.timeframe) return;
            if (this.dataset && this.dataset[tf]) {
                this.applyPersistedDefaultIndicatorGroups(this.dataset[tf]);
            }
        },
        restoreSavedOverlaysToTimeframe(tf) {
            if (!this.dataset || !this.dataset[tf] || !this._savedDrawings) return;
            this.dataset[tf].onchart = _.cloneDeep(this._savedDrawings.onchart || []);
            this.dataset[tf].offchart = _.cloneDeep(this._savedDrawings.offchart || []);
        },
        finalizeIndicatorsForTimeframe(tf) {
            if (this._savedDrawings) {
                // A live overlay snapshot is authoritative, including deletions.
                // Reapplying defaults here would resurrect removed indicators.
                this.restoreSavedOverlaysToTimeframe(tf);
                if (this.workspaceVTEnabled === false) {
                    this.removeIndicatorGroupFromData(this.dataset[tf], 'VT');
                }
            } else {
                this.applyPersistedDefaultsToTimeframe(tf);
            }
        },
        rerunIndicatorScripts(tf = this.timeframe) {
            if (tf !== this.timeframe) return;
            const run = () => {
                if (!this.$refs.tv || !this.chart || !this.chart[tf]) return;
                this.$refs.tv.custom_event({
                    event: 'exec-all-scripts',
                    args: []
                });
            };
            // Overlay mounts register their own scripts. Coalesce history /
            // group refreshes into one run after Vue has mounted the layers;
            // staggered retries repeatedly reload worker indicators (DC/TSMA).
            if (this._rerunScriptsTimers) {
                this._rerunScriptsTimers.forEach(t => clearTimeout(t));
            }
            const generation = (this._rerunScriptsGeneration || 0) + 1;
            this._rerunScriptsGeneration = generation;
            this._rerunScriptsTimers = [];
            this.$nextTick(() => {
                if (generation !== this._rerunScriptsGeneration) return;
                this._rerunScriptsTimers = [setTimeout(() => {
                    this._rerunScriptsTimers = [];
                    if (tf === this.timeframe) run();
                }, 50)];
            });
        },
        refreshCurrentIndicators(tf = this.timeframe) {
            if (tf !== this.timeframe) return;
            if (!this.dataset || !this.dataset[tf] || !this.dataset[tf].chart) return;

            this.finalizeIndicatorsForTimeframe(tf);
            this._applyCurrentChartType(tf);

            // Re-emit timeframes data so MTFTargets/LSR overlays stay updated
            EventBus.$emit('timeframes-updated', this.timeframes);

            this.$nextTick(() => {
                this.rerunIndicatorScripts(tf);
                this._reapplyCandleScriptIndicators();
            });
        },
        // Timeframe derivation logic
        _buildCustomTimeframeChart(customTimeframe, tf) {
            const derivedData = this.deriveCustomTimeframeData(customTimeframe);
            
            if (!derivedData || derivedData.length === 0) {
                console.warn('Failed to derive custom timeframe data:', tf);
                return null;
            }
            
            // Set up dataset for custom timeframe
            if (this.session && this.session.properties) {
                if (this.session.properties[this.query]) {
                    this.dataset[tf] = _.cloneDeep(this.session.properties[this.query]);
                } else {
                    this.dataset[tf] = _.cloneDeep(Data);
                }
            } else if (!this.dataset[tf]) {
                this.dataset[tf] = _.cloneDeep(Data);
            }
            this.finalizeIndicatorsForTimeframe(tf);
            
            // Calculate interval_ms for custom timeframe
            const { type, value } = customTimeframe;
            const intervalMs = value * (type === 'minute' ? 60000 : type === 'hour' ? 3600000 : 86400000);
            
            // Set the derived data
            this.dataset[tf].chart.data = derivedData;
            this.dataset[tf].chart.tf = tf;
            this.dataset[tf].chart.interval_ms = intervalMs;
            
            // Update timeframes object with derived data
            var data = this.processTimeframes(derivedData, tf);
            if (data) {
                this.setTimeframeProcessed(tf, data);
            }
            
            // Apply current chart type before creating DataCube
            this._applyCurrentChartType(tf);
            
            // Create DataCube so the chart can render
            Vue.set(this.chart, tf, new DataCube(this.dataset[tf]));
            this.protectChart = false;
            this.resetkey++;
            this.refreshCurrentIndicators(tf);
            
            return data;
        },
        deriveCustomTimeframeData(customTimeframe) {
            const { type, value } = customTimeframe;
            const baseInterval = this.getBestLoadedBaseTimeframe(customTimeframe) || this.getNearestBaseTimeframe(customTimeframe);
            
            if (!baseInterval) {
                console.warn('No suitable base timeframe found for custom timeframe:', customTimeframe);
                return [];
            }
            
            // Get the base data for derivation - check DataCube first, then raw dataset
            let baseCandles = null;
            const dc = this.chart[baseInterval];
            if (dc && dc.data && dc.data.chart && dc.data.chart.data && dc.data.chart.data.length > 0) {
                baseCandles = dc.data.chart.data;
            } else if (this.dataset[baseInterval] && this.dataset[baseInterval].chart && this.dataset[baseInterval].chart.data && this.dataset[baseInterval].chart.data.length > 0) {
                baseCandles = this.dataset[baseInterval].chart.data;
            }
            
            if (!baseCandles || baseCandles.length === 0) {
                console.warn('Base timeframe data not available for derivation:', baseInterval);
                return [];
            }
            
            return this.aggregateTimeframeData(baseCandles, type, value);
        },
        getNearestBaseTimeframe(customTimeframe) {
            return this.getBaseTimeframeCandidates(customTimeframe)[0] || null;
        },
        aggregateTimeframeData(data, type, value) {
            const targetInterval = value * (type === 'minute' ? 60000 : type === 'hour' ? 3600000 : 86400000);
            const aggregated = [];
            
            let currentCandle = null;
            let currentTimestamp = null;
            
            for (const candle of data) {
                const [timestamp, open, high, low, close, volume] = candle;
                
                // Calculate the start of the target interval
                const intervalStart = Math.floor(timestamp / targetInterval) * targetInterval;
                
                if (currentTimestamp !== intervalStart) {
                    // Save previous candle if exists
                    if (currentCandle) {
                        aggregated.push(currentCandle);
                    }
                    
                    // Start new candle
                    currentCandle = [intervalStart, open, high, low, close, volume];
                    currentTimestamp = intervalStart;
                } else {
                    // Update existing candle
                    currentCandle[2] = Math.max(currentCandle[2], high); // high
                    currentCandle[3] = Math.min(currentCandle[3], low);   // low
                    currentCandle[4] = close;                              // close (last price)
                    currentCandle[5] += volume;                            // volume (sum)
                }
            }
            
            // Add the last candle
            if (currentCandle) {
                aggregated.push(currentCandle);
            }
            
            return aggregated;
        },
                calculateTrend(data) {
            if (!data || data.length < 10) return 'NEUTRAL';
            
            const recent = data.slice(-10);
            const firstPrice = recent[0][4]; // Close price is at index 4
            const lastPrice = recent[recent.length - 1][4];
            const change = ((lastPrice - firstPrice) / firstPrice) * 100;
            
            if (change > 2) return 'BULLISH';
            if (change < -2) return 'BEARISH';
            return 'NEUTRAL';
        },
        calculateAlignmentScore(allTimeframeData) {
            if (!allTimeframeData || Object.keys(allTimeframeData).length === 0) return 0;
            
            let bullishCount = 0, bearishCount = 0, totalCount = 0;
            
            Object.values(allTimeframeData).forEach(tfData => {
                if (tfData && tfData.trend) {
                    totalCount++;
                    if (tfData.trend === 'BULLISH') bullishCount++;
                    else if (tfData.trend === 'BEARISH') bearishCount++;
                }
            });
            
            if (totalCount === 0) return 0;
            
            // Calculate alignment score based on how many timeframes agree
            const maxCount = Math.max(bullishCount, bearishCount);
            const agreementPercentage = (maxCount / totalCount) * 100;
            
            if (agreementPercentage >= 80) return 5; // Strong alignment
            if (agreementPercentage >= 60) return 4; // Good alignment
            if (agreementPercentage >= 40) return 3; // Some alignment
            if (agreementPercentage >= 20) return 2; // Weak alignment
            return 1; // No alignment
        },
        onAIChatLoadChart(chartRequest) {
            // Handle loading a chart from AI chat
            if (chartRequest.symbol && chartRequest.timeframe) {
                this.symbol = chartRequest.symbol;
                this.timeframe = chartRequest.timeframe;
                this.loadChart();
            }
        },
        onAIChatCaptureChart(chartRequest) {
            // Capture current chart as image and send to AI chat
            try {
                // Get the chart canvas - try multiple selectors
                let canvas = document.querySelector('.tvjs-chart canvas');
                if (!canvas) {
                    canvas = document.querySelector('.chart-container canvas');
                }
                if (!canvas) {
                    // Try to find any canvas in the chart area
                    const chartContainer = document.querySelector('.tvjs-chart') || document.querySelector('.chart-container');
                    if (chartContainer) {
                        canvas = chartContainer.querySelector('canvas');
                    }
                }

                if (canvas) {
                    const imageData = canvas.toDataURL('image/png');
                    // Send to AI chat component
                    const aiChatComponent = this.$refs.aiChat;
                    if (aiChatComponent && aiChatComponent.addChartImageToMessage) {
                        aiChatComponent.addChartImageToMessage(
                            imageData,
                            chartRequest.symbol || this.symbol,
                            chartRequest.timeframe || this.timeframe
                        );
                    }
                } else {
                    console.warn('[Chart] Could not find chart canvas for capture');
                    alert('Could not capture chart. Canvas not found.');
                }
            } catch (error) {
                console.error('[Chart] Error capturing chart:', error);
                alert('Failed to capture chart: ' + error.message);
            }
        },
        async onDateRangeSelect(rangeKey) {
            const request = this._dateRangeRequest = (this._dateRangeRequest || 0) + 1;
            const market = `${this.connector}:${this.exchange}:${this.symbol}`;
            let selectedTf;
            const current = () => request === this._dateRangeRequest && market === `${this.connector}:${this.exchange}:${this.symbol}` && (!selectedTf || selectedTf === this.timeframe);
            try {
                if (this.workspaceReplay.active) throw new Error('Exit replay before changing the date range.');
                this.workspaceRangeBusy = true;
                const preferred = rangeKey === '1D' ? '5m' : rangeKey === '5D' ? '30m' : '1D';
                if (this.timeframes?.[preferred] && preferred !== this.timeframe) {
                    await this.onTimeframeSelect(this.timeframes[preferred], null);
                    await this.$nextTick();
                }
                if (!current()) return;
                selectedTf = this.timeframe;
                let bars = this.workspaceCandles;
                if (!bars.length) throw new Error('Load chart data first.');
                const last = bars[bars.length - 1][0];
                const offset = ['Fyers', 'Upstox', 'Dhan', 'Indian', 'AliceBlue'].includes(this.connector) ? 330 : 0;
                const start = chartRangeStart(rangeKey, last, offset);
                if (start === undefined) return;
                let pages = 0;
                while (current() && (start === null || bars[0][0] > start) && pages++ < 40) {
                    const first = bars[0][0];
                    try { await this.workspaceLoadEarlier(true); }
                    catch (error) { this.workspaceNotify(error.message, 'alert'); break; }
                    if (!current()) return;
                    bars = this.workspaceCandles;
                    if (!bars.length || bars[0][0] >= first) break;
                }
                if (!current()) return;
                await this.$nextTick();
                const interval = bars.length > 1 ? bars[bars.length - 1][0] - bars[bars.length - 2][0] : 86400000;
                const left = start === null ? bars[0][0] : Math.max(start, bars[0][0]);
                this.$refs.tv?.setRange(Math.min(left, last - interval), last + interval * 0.5);
                if (pages > 40) this.workspaceNotify('Showing loaded history. Select the range again to load more.', 'alert');
                else if (start !== null && bars[0][0] > start) this.workspaceNotify('Showing available history for this provider.', 'alert');
            } catch (error) { if (current()) this.workspaceNotify(error.message, 'alert'); }
            finally { if (request === this._dateRangeRequest) this.workspaceRangeBusy = false; }
        },
        onTimezoneChange(tzOffset) {
            this.userSelectedTimezone = tzOffset;
        },
        onCustomRange() {
            this.workspacePanel = 'date'
        },
        handleProviderChanged(event) {
            this.currentProvider = event.provider;
            // Reload chart with new provider
            this.loadChart();
        },
        openAddIndicator() {
            // Trigger the add-indicator widget on the main chart overlay
            const view = this.workspaceToolbarView || this.$refs.tv;
            if (view) {
                view.custom_event({
                    event: 'legend-button-click',
                    args: [{ button: 'add', type: 'onchart', dataIndex: 0 }]
                })
            }
        },
        init() {
            if (!this.session.properties) {
                this.session.properties = {}
                this.session.properties[this.symbol] = _.cloneDeep(Data)
                this.applyPersistedDefaultIndicatorGroups(this.session.properties[this.symbol])
            }
            var allSymbols = {}
            if (this.session.crypto === "Y") {
                this.spotSymbols.symbols.map(function(item, key) {
                    var symbol = item.symbol
                    allSymbols[symbol] = item
                })
                this.futureSymbols.symbols.map(function(item, key) {
                    var symbol = item.symbol + ".P"
                    allSymbols[symbol] = item
                })
            }
            if (this.session.international === "Y") {
                this.intSymbols.map(function(item, key) {
                    var symbol = item.symbol + ":" + item.exchange
                    allSymbols[symbol] = item
                })
            }
            // Load Fyers symbols if authenticated
            if (fyersApi.isAuthenticated()) {
                fyersApi.loadSymbolMaster('NSE_CM').then(symbols => {
                    if (symbols && symbols.length > 0) {
                        symbols.forEach(item => {
                            // Namespace Fyers keys so they never collide with
                            // Binance symbols that share the same name
                            const key = 'fyers:' + (item.symbol || item.trading_symbol);
                            allSymbols[key] = item;
                        });
                        this.allSymbols = allSymbols;
                        this.buildSymbolIndex();
                    }
                }).catch(err => console.warn('Fyers symbol load deferred:', err.message));
            }
            // Load Upstox symbols if authenticated
            if (upstoxApi.isAuthenticated()) {
                upstoxApi.loadSymbolMaster('NSE_EQ').then(symbols => {
                    if (symbols && symbols.length > 0) {
                        symbols.forEach(item => {
                            const key = 'upstox:' + (item.instrument_key || item.symbol || item.trading_symbol);
                            allSymbols[key] = item;
                        });
                        this.allSymbols = allSymbols;
                        this.buildSymbolIndex();
                    }
                }).catch(err => console.warn('Upstox symbol load deferred:', err.message));
            }
            // Load Dhan symbols if authenticated
            if (dhanApi.isAuthenticated()) {
                dhanApi.loadSymbolMaster().then(symbols => {
                    if (symbols && symbols.length > 0) {
                        symbols.forEach(item => {
                            const key = 'dhan:' + (item.security_id || item.securityId || item.symbol || item.trading_symbol);
                            allSymbols[key] = item;
                        });
                        this.allSymbols = allSymbols;
                        this.buildSymbolIndex();
                    }
                }).catch(err => console.warn('Dhan symbol load deferred:', err.message));
            }
            // Coinbase/Kraken/Bybit/OKX/AMFI are free public sources, always
            // loaded — no auth check, unlike the brokers above.
            [
                ['coinbase', coinbaseApi],
                ['kraken', krakenApi],
                ['bybit', bybitApi],
                ['okx', okxApi],
                ['amfi', amfiApi],
            ].forEach(([prefix, api]) => {
                api.loadExchangeInfo().then(symbols => {
                    if (symbols && symbols.length > 0) {
                        symbols.forEach(item => {
                            const key = prefix + ':' + item.symbol;
                            allSymbols[key] = item;
                        });
                        this.allSymbols = allSymbols;
                        this.buildSymbolIndex();
                    }
                }).catch(err => console.warn('[' + prefix + '] symbol load deferred:', err.message));
            });
            this.allSymbols = allSymbols
            this.loadChart()
        },
        showQRAuth() {
            this.showQRModal = true;
        },
        closeQRModal() {
            this.showQRModal = false;
        },
        logout() {
            this.userMenuOpen = false;
            this.$clerk.logout();
            this.$router.push({ path: "/" });
        },
        navigateMenu(path) {
            this.userMenuOpen = false;
            this.$router.push({ path });
        },
        toggleDrawingTools() {
            this.showDrawingTools = !this.showDrawingTools;
            localStorage.setItem('tc_show_drawings', this.showDrawingTools);
        },
        // Mobile callback is handled by Welcome.vue
        legend_click(event) {
            this.$emit('toggle-button-click', event)
        },
        onResize() {
            this.updateChartDimensions()
            if (this._resizeTimer) clearTimeout(this._resizeTimer)
            this._resizeTimer = setTimeout(() => {
                this.updateChartDimensions()
            }, 150)
        },
        toggleChartFeature(feature) {
            this.chartFeatureView = this.chartFeatureView === feature ? '' : feature
            this.refreshChartRender()
        },
        updateChartDimensions() {
            this.width = window.innerWidth
            this.height = window.innerHeight - this.top - 32
            const column = this.$refs.chartMainColumn
            if (column && column.getBoundingClientRect) {
                const rect = column.getBoundingClientRect()
                const dateBar = this.$refs.dateRangeBar && this.$refs.dateRangeBar.$el
                const dateBarHeight = dateBar && dateBar.getBoundingClientRect
                    ? Math.ceil(dateBar.getBoundingClientRect().height)
                    : 32
                if (rect.width > 100 && rect.height > 100) {
                    this.chartContainerWidth = Math.floor(rect.width)
                    const featuresHeight = this.$refs.chartFeatures?.getBoundingClientRect().height || 0
                    const featurePanel = this.$refs.featurePanel?.$el
                    const featureHeight = featurePanel?.getBoundingClientRect && getComputedStyle(featurePanel).position !== 'absolute' ? featurePanel.getBoundingClientRect().height : 0
                    const dock = this.$refs.strategyDock?.$el
                    const dockHeight = dock && getComputedStyle(dock).position !== 'absolute' ? dock.getBoundingClientRect().height : 0
                    this.chartContainerHeight = Math.max(120, Math.floor(rect.height - dateBarHeight - featuresHeight - dockHeight - featureHeight))
                    return
                }
            }
            const rightToolsWidth = window.innerWidth <= 760 ? 58 : 70
            this.chartContainerWidth = Math.max(this.width - rightToolsWidth, 240)
            this.chartContainerHeight = Math.max(this.height, 240)
        },
        setupChartResizeObserver() {
            if (this._chartResizeObserver) {
                this._chartResizeObserver.disconnect()
                this._chartResizeObserver = null
            }
            if (typeof ResizeObserver === 'undefined') return
            const column = this.$refs.chartMainColumn
            if (!column) return
            this._chartResizeObserver = new ResizeObserver(() => {
                if (this._chartResizeFrame) return
                this._chartResizeFrame = requestAnimationFrame(() => {
                    this._chartResizeFrame = null
                    this.updateChartDimensions()
                })
            })
            this._chartResizeObserver.observe(column)
            const controls = this.$refs.workspaceControls && this.$refs.workspaceControls.$el
            if (controls) this._chartResizeObserver.observe(controls)
            if(this.$refs.strategyDock)this._chartResizeObserver.observe(this.$refs.strategyDock.$el)
        },
        refreshChartRender() {
            this.$nextTick(() => {
                this.updateChartDimensions()
            })
        },
        win_query() {
            let qs = (function(a) {
                if (a == "") return {};
                var b = {};
                for (var i = 0; i < a.length; ++i) {
                    var p=a[i].split('=', 2);
                    if (p.length == 1)
                        b[p[0]] = "";
                    else
                        b[p[0]] = decodeURIComponent(p[1].replace(/\+/g, " "));
                }
                return b;
            })(window.location.search.substr(1).split('&'));
            return qs
        },
        updateData(newValue) {
          this.chartData = newValue;
        },
        parseDate(value) {
            if (value) {
                return new Date(value)
            }
        },
        openCompareSearch() { this._compareSearch=true; this.$refs.symbolSearch?.show(''); },
        onOpen () {
          this._compareSearch=false;
          // Pre-fill search with current symbol display name
          let currentDisplay = this.symbolDisplay || this.query || this.symbol || ''
          // Strip exchange prefix for cleaner search
          if (currentDisplay.includes(':')) {
              currentDisplay = currentDisplay.split(':').pop()
          }
          currentDisplay = currentDisplay.replace(/-EQ$/, '').replace(/-INDEX$/, '').replace(/-FUT$/, '')
          // Use new SymbolSearch modal
          if (this.$refs.symbolSearch) {
              this.$refs.symbolSearch.show(currentDisplay)
          }
        },
        isCrypto(item) {
            return item.type === 'Crypto Spot' || item.type === 'Crypto Futures';
        },
        formatSearchName(item) {
            // For Fyers: show formatted name without exchange prefix
            if (item.type === 'Fyers' || item.exchange === 'Fyers') {
                let name = item.formatted_ins_name || item.name || item.symbol || ''
                // Remove exchange prefix like "NSE:" or "BSE:"
                if (name.includes(':')) {
                    name = name.split(':').pop()
                }
                // Remove suffixes like -EQ, -INDEX, -FUT for cleaner display
                name = name.replace(/-EQ$/, '').replace(/-INDEX$/, '').replace(/-FUT$/, '')
                return name
            }
            // For Binance
            let name = item.name || item.baseAsset || item.symbol || ''
            if (item.contractType === 'PERPETUAL') {
                name += '.P'
            }
            return name
        },
        formatSearchDesc(item) {
            if (item.type === 'Crypto Spot' || item.type === 'Crypto Futures') {
                let parts = []
                if (item.baseAsset) parts.push(item.baseAsset)
                if (item.quoteAsset) parts.push(item.quoteAsset)
                if (item.contractType && item.contractType !== 'PERPETUAL') {
                    parts.push(item.contractType)
                }
                return parts.join(' / ')
            }
            // For Fyers: show segment info
            if (item.type === 'Fyers' || item.exchange === 'Fyers') {
                let segment = item.segment || item.exchange_segment || ''
                return segment
            }
            return item.exch || item.exchange || ''
        },
        formatSearchSymbol(item) {
            let sym = item.symbol || item.trading_symbol || ''
            // For Fyers, strip the exchange prefix from symbol display
            if ((item.type === 'Fyers' || item.exchange === 'Fyers') && sym.includes(':')) {
                sym = sym.split(':').pop()
            }
            return sym
        },
        onSelect (item) {
          if(this._compareSearch) {
            this._compareSearch=false;
            if(item.exchange && item.exchange!=='Binance') { this.workspaceNotify('Comparison currently supports Binance symbols.', 'alert'); return; }
            this.workspaceCompare(item.symbol); return;
          }
          // Determine the correct connector based on the symbol's exchange/type.
          // Binance crypto symbols (spot & futures) must always route to the
          // Binance connector so that loadKlineFromBinance is called with the
          // proper symbol name.  Fyers symbols must always route to Fyers.
          // Upstox symbols must always route to Upstox.
          // Coinbase/Kraken/Bybit/OKX symbols (from market_proxy.js) are also
          // tagged type "Crypto Spot" like Binance's, so they must be told
          // apart by exchange BEFORE isBinanceCrypto is evaluated, or they'd
          // incorrectly fall into the Binance branch below.
          const isCoinbase = item.exchange === "Coinbase";
          const isKraken = item.exchange === "Kraken";
          const isBybit = item.exchange === "Bybit";
          const isOkx = item.exchange === "OKX";
          const isAmfi = item.exchange === "AMFI";
          const isBinanceCrypto = !isCoinbase && !isKraken && !isBybit && !isOkx && !isAmfi
              && (item.type === "Crypto Spot" || item.type === "Crypto Futures"
              || (item.exchange === "Binance" && !item.fyToken && !item.instrument_key));
          const isFyers = (item.exchange === "Fyers" || item.type === "Fyers" || !!item.fyToken)
              && !isBinanceCrypto;
          const isUpstox = (item.exchange === "Upstox" || item.type === "Upstox" || !!item.instrument_key)
              && !isBinanceCrypto && !isFyers && item.type !== "IndianMarket";
          const isAliceBlue = (item.exchange === "AliceBlue" || item.type === "AliceBlue" || !!item.token)
              && !isBinanceCrypto && !isFyers && !isUpstox && item.type !== "IndianMarket";
          const isIndianMarket = item.type === "IndianMarket";

          if (isCoinbase || isBybit || isOkx) {
              // Coinbase/Bybit/OKX: REST symbol and WS subscribe symbol are
              // the same string (e.g. "BTC-USD", "BTCUSDT", "BTC-USDT").
              this.symbol = item.symbol
              this.exchange = item.exchange
              this.category = item.type || "Crypto Spot"
              this.connector = item.exchange
              this.pairId = item.symbol
              this.name = item.name || this.symbol
          } else if (isKraken) {
              // Kraken: REST calls use the altname (item.symbol, e.g.
              // "XBTUSD"); the WS ticker subscribe needs the slash-separated
              // wsname (item.wsname, e.g. "XBT/USD") — kept in pairId.
              this.symbol = item.symbol
              this.exchange = "Kraken"
              this.category = item.type || "Crypto Spot"
              this.connector = "Kraken"
              this.pairId = item.wsname || item.symbol
              this.name = item.name || this.symbol
          } else if (isAmfi) {
              this.symbol = item.symbol
              this.exchange = "AMFI"
              this.category = item.type || "Mutual Fund"
              this.connector = "AMFI"
              this.pairId = item.symbol
              this.name = item.name || this.symbol
          } else if (isBinanceCrypto) {
              // Binance spot or futures — build the correct symbol name
              if (item.contractType === "PERPETUAL") {
                  // Futures perpetual: append .P suffix
                  this.symbol = item.symbol + ".P"
              } else {
                  this.symbol = item.symbol
              }
              this.exchange = "Binance"
              this.category = item.type || ""
              this.connector = "Binance"
              this.pairId = this.symbol
              this.name = item.name || item.baseAsset
                  ? (item.baseAsset || '') + '/' + (item.quoteAsset || '')
                  : this.symbol
          } else if (isFyers) {
              // Fyers symbol: NSE:SBIN-EQ
              this.symbol = item.symbol || item.trading_symbol
              this.exchange = item.exch || 'NSE'
              this.category = item.type || 'Fyers'
              this.connector = "Fyers"
              this.pairId = item.fyToken || this.symbol
              this.name = item.formatted_ins_name || item.name || this.symbol
          } else if (isUpstox) {
              // Upstox symbol: use trading_symbol for display, instrument_key for subscription
              this.symbol = item.trading_symbol || item.symbol
              this.exchange = item.exch || item.exchange || 'NSE'
              this.category = item.type || 'Upstox'
              this.connector = "Upstox"
              this.pairId = item.instrument_key || this.symbol
              this.name = item.formatted_ins_name || item.name || item.trading_symbol || this.symbol
          } else if (isAliceBlue) {
              // AliceBlue: use trading_symbol for display, numeric token for API calls
              this.symbol = item.trading_symbol || item.symbol
              this.exchange = item.exch || item.exchange || 'NSE'
              this.category = item.type || 'AliceBlue'
              this.connector = "AliceBlue"
              this.pairId = item.token || this.symbol
              this.name = item.formatted_ins_name || item.name || this.symbol
          } else if (isIndianMarket) {
              this.symbol = item.trading_symbol || item.symbol
              this.name = item.formatted_ins_name || item.name || this.symbol
              this.exchange = item.exch || item.exchange || 'NSE'
              this.category = 'IndianMarket'
              this.pairId = item.instrument_key
              this.connector = "Indian"
          } else if (item.pairId) {
              this.symbol = item.symbol
              this.exchange = item.exchange
              this.category = item.type ? item.type : ""
              this.pairId = item.pairId
              this.connector = "Int"
          } else {
              this.symbol = item.trading_symbol ? item.trading_symbol : item.symbol
              this.name = item.formatted_ins_name
              this.exchange = item.exch
              this.category = item.exchange_segment ? item.exchange_segment : ""
              this.pairId = item.token
              this.connector = "Indian"
          }
          this.selectedSymbolInfo = { symbol: this.symbol, connector: this.connector, properties: { ...item } };
          var code = this.symbol + "::" + this.pairId + "::" + this.connector + "::" + this.exchange + "::" + this.category + "::5m"
          //window.location.href="/?c=" + btoa(code)

          // Use formatted name for Fyers symbols, otherwise use symbol
          const displayName = (item.formatted_ins_name || item.name || this.symbol);
          window.history.pushState('/', displayName + " - TradingCandle", "/chart?c=" + btoa(code));
          this.chart = {};
          this.dataset = {};
          this.protectChart = true;
          this._loadGeneration = (this._loadGeneration || 0) + 1;
          this.loadChart()
        },
        buildSymbolIndex() {
            // Build index including duplicates
            this.symbolIndex = Object.values(this.allSymbols).map(item => {
                const symbolValue = ((item.trading_symbol || item.symbol || '') + ":" + (item.exchange || item.exch || '')).toUpperCase();
                const symbolName = (item.name || '').toUpperCase();
                return {
                    original: item,
                    symbolValue,
                    symbolName,
                    exchange: (item.exchange || item.exch || '').toUpperCase()
                };
            });
        },

        getSuggestions(query, cb) {
            try {
                // Always rebuild index if empty or size mismatch (due to dynamic updates)
                if (!this.symbolIndex || this.symbolIndex.length !== Object.values(this.allSymbols).length) {
                    this.buildSymbolIndex();
                }

                let results = this.symbolIndex;
                const isFyersAuthenticated = fyersApi.isAuthenticated();
                const isUpstoxAuthenticated = upstoxApi.isAuthenticated();
                const isDhanAuthenticated = dhanApi.isAuthenticated();
                const indianMarketEnabled = this.$store && this.$store.getters.indianMarketDataEnabled;

                if (query) {
                    const uppercaseQuery = query.split(".")[0].toUpperCase();

                    // Match against name and value
                    results = results.filter(item =>
                        item.symbolName.includes(uppercaseQuery) ||
                        item.symbolValue.includes(uppercaseQuery)
                    );

                    // Prioritize broker NSE symbols at the top of results
                    results.sort((a, b) => {
                        const aIsBrokerNSE = a.exchange === 'NSE' && (a.original.type === 'Fyers' || a.original.exchange === 'Fyers' || a.original.type === 'Upstox' || a.original.exchange === 'Upstox' || a.original.type === 'Dhan' || a.original.exchange === 'Dhan');
                        const bIsBrokerNSE = b.exchange === 'NSE' && (b.original.type === 'Fyers' || b.original.exchange === 'Fyers' || b.original.type === 'Upstox' || b.original.exchange === 'Upstox' || b.original.type === 'Dhan' || b.original.exchange === 'Dhan');
                        if (aIsBrokerNSE && !bIsBrokerNSE) return -1;
                        if (!aIsBrokerNSE && bIsBrokerNSE) return 1;
                        return 0;
                    });
                }

                if (query && query.length >= 1 && indianMarketEnabled) {
                    this.fyersSearchLoading = true;
                    indianMarketApi.searchSymbols(query, 30).then(indianResults => {
                        if (indianResults && indianResults.length > 0) {
                            indianResults.forEach(item => {
                                const key = 'indian-market:' + (item.instrument_key || item.symbol || item.trading_symbol);
                                this.allSymbols[key] = item;
                            });
                            this.buildSymbolIndex();

                            let updatedResults = this.symbolIndex.filter(item => {
                                const uppercaseQuery = query.split(".")[0].toUpperCase();
                                return item.symbolName.includes(uppercaseQuery) ||
                                       item.symbolValue.includes(uppercaseQuery);
                            });

                            updatedResults.sort((a, b) => {
                                const aIsIndian = a.original.type === 'IndianMarket';
                                const bIsIndian = b.original.type === 'IndianMarket';
                                if (aIsIndian && !bIsIndian) return -1;
                                if (!aIsIndian && bIsIndian) return 1;
                                if (aIsIndian && bIsIndian) {
                                    const rankDiff = this.getIndianMarketSearchRank(a.original) - this.getIndianMarketSearchRank(b.original);
                                    if (rankDiff !== 0) return rankDiff;
                                }
                                const aSym = (a.original.trading_symbol || a.original.symbol || '').toUpperCase();
                                const bSym = (b.original.trading_symbol || b.original.symbol || '').toUpperCase();
                                return aSym.localeCompare(bSym);
                            });

                            cb(updatedResults.slice(0, 100).map(item => item.original));
                        }
                        this.fyersSearchLoading = false;
                    }).catch(() => {
                        this.fyersSearchLoading = false;
                    });
                }
                // If Fyers is authenticated, search Fyers API and merge results at top
                else if (query && query.length >= 1 && isFyersAuthenticated && !isDhanAuthenticated) {
                    this.fyersSearchLoading = true;
                    fyersApi.searchSymbols(query, 30, 'ALLMFIPO').then(fyersResults => {
                        if (fyersResults && fyersResults.length > 0) {
                            fyersResults.forEach(item => {
                                const key = 'fyers:' + (item.symbol || item.trading_symbol);
                                this.allSymbols[key] = item;
                            });
                            this.buildSymbolIndex();

                            let updatedResults = this.symbolIndex.filter(item => {
                                const uppercaseQuery = query.split(".")[0].toUpperCase();
                                return item.symbolName.includes(uppercaseQuery) ||
                                       item.symbolValue.includes(uppercaseQuery);
                            });

                            updatedResults.sort((a, b) => {
                                const aIsFyers = a.original.type === 'Fyers' || a.exchange === 'FYERS';
                                const bIsFyers = b.original.type === 'Fyers' || b.exchange === 'FYERS';
                                if (aIsFyers && !bIsFyers) return -1;
                                if (!aIsFyers && bIsFyers) return 1;
                                return 0;
                            });

                            cb(updatedResults.slice(0, 100).map(item => item.original));
                        }
                        this.fyersSearchLoading = false;
                    }).catch(() => {
                        this.fyersSearchLoading = false;
                    });
                }
                // If Upstox is authenticated (and Fyers is not), search Upstox API
                else if (query && query.length >= 1 && isUpstoxAuthenticated && !isDhanAuthenticated) {
                    this.fyersSearchLoading = true;
                    upstoxApi.searchSymbols(query, 30).then(upstoxResults => {
                        if (upstoxResults && upstoxResults.length > 0) {
                            upstoxResults.forEach(item => {
                                const key = 'upstox:' + (item.instrument_key || item.symbol || item.trading_symbol);
                                this.allSymbols[key] = item;
                            });
                            this.buildSymbolIndex();

                            let updatedResults = this.symbolIndex.filter(item => {
                                const uppercaseQuery = query.split(".")[0].toUpperCase();
                                return item.symbolName.includes(uppercaseQuery) ||
                                       item.symbolValue.includes(uppercaseQuery);
                            });

                            updatedResults.sort((a, b) => {
                                const aIsUpstox = a.original.type === 'Upstox' || a.original.exchange === 'Upstox';
                                const bIsUpstox = b.original.type === 'Upstox' || b.original.exchange === 'Upstox';
                                if (aIsUpstox && !bIsUpstox) return -1;
                                if (!aIsUpstox && bIsUpstox) return 1;
                                return 0;
                            });

                            cb(updatedResults.slice(0, 100).map(item => item.original));
                        }
                        this.fyersSearchLoading = false;
                    }).catch(() => {
                        this.fyersSearchLoading = false;
                    });
                }
                // If Dhan is authenticated, search Dhan instrument master
                else if (query && query.length >= 1 && isDhanAuthenticated) {
                    this.fyersSearchLoading = true;
                    dhanApi.searchSymbols(query, 30).then(dhanResults => {
                        if (dhanResults && dhanResults.length > 0) {
                            dhanResults.forEach(item => {
                                const key = 'dhan:' + (item.security_id || item.securityId || item.symbol || item.trading_symbol);
                                this.allSymbols[key] = item;
                            });
                            this.buildSymbolIndex();

                            let updatedResults = this.symbolIndex.filter(item => {
                                const uppercaseQuery = query.split(".")[0].toUpperCase();
                                return item.symbolName.includes(uppercaseQuery) ||
                                       item.symbolValue.includes(uppercaseQuery);
                            });

                            updatedResults.sort((a, b) => {
                                const aIsDhan = a.original.type === 'Dhan' || a.original.connector === 'Dhan';
                                const bIsDhan = b.original.type === 'Dhan' || b.original.connector === 'Dhan';
                                if (aIsDhan && !bIsDhan) return -1;
                                if (!aIsDhan && bIsDhan) return 1;
                                return 0;
                            });

                            cb(updatedResults.slice(0, 100).map(item => item.original));
                        }
                        this.fyersSearchLoading = false;
                    }).catch(() => {
                        this.fyersSearchLoading = false;
                    });
                }

                // Return up to 100 matching entries (including duplicates)
                cb(results.slice(0, 100).map(item => item.original));
            } catch (error) {
                console.error("Error fetching suggestions:", error);
                cb([]);
            }
        },

        getIndianMarketSearchRank(item) {
            const segment = (item.segment || item.exchange_segment || '').toUpperCase();
            const instrumentType = (item.instrument_type || item.instrumentType || '').toUpperCase();
            const symbol = (item.trading_symbol || item.symbol || '').toUpperCase();
            const isCommodity = segment.includes('MCX') || segment.includes('COM') || instrumentType.includes('COM');
            const isForex = segment.includes('CDS') || segment.includes('BCD') || segment.includes('CUR') || instrumentType.includes('CUR');
            const isOption = instrumentType.includes('OPT') || instrumentType === 'CE' || instrumentType === 'PE' || symbol.endsWith('CE') || symbol.endsWith('PE');
            const isFuture = instrumentType.includes('FUT') || symbol.includes('FUT');
            const isIndex = segment.includes('INDEX') || instrumentType.includes('INDEX') || segment.includes('IDX');
            const isStock = segment.includes('EQ') || instrumentType === 'EQ' || instrumentType === 'EQUITY';

            if (isStock || isIndex) return 0;
            if (!isCommodity && !isForex && isFuture) return 1;
            if (!isCommodity && !isForex && isOption) return 2;
            if (isCommodity && isFuture) return 3;
            if (isCommodity && isOption) return 4;
            if (isForex && isFuture) return 5;
            if (isForex && isOption) return 6;
            if (isFuture) return 7;
            if (isOption) return 8;
            return 9;
        },

        // Auth token loading removed — authentication is handled by QR/RevenueCat flow
        loadChart() {
            // Check if user is authenticated with valid subscription.
            // /demo renders this same component for signed-out visitors
            // (see the demoGuard-gated route and the created() hook above),
            // so let it load chart data too instead of silently staying blank.
            const isDemo = this.$route.path === '/demo';
            if (!isDemo && (!this.$clerk.isAuthenticated || (!this.$clerk.subscriptionValid && !this.strategyWorkspaceEntry))) {
                return;
            }
            
            this.protectChart = true;

            const invalidReason = this.getChartLoadInvalidReason();
            if (invalidReason) {
                console.warn('[Chart] Chart load skipped:', invalidReason);
                this.chart = {};
                this.dataset = {};
                this.query = this.symbol || '';
                return;
            }

            // All supported timeframes for the chart
            const allTimeframes = { '1m': [], '3m': [], '5m': [], '15m': [], '30m': [], '1H': [], '2H': [], '4H': [], '6H': [], '8H': [], '12H': [], '1D': [], '3D': [], '1W': [], '1M': [] };

            if (this.connector === 'Fyers') {
                // Use all Fyers-supported timeframes (includes seconds, 2m, 10m, 20m, etc.)
                this.timeframes = {};
                FYERS_SUPPORTED_TFS.forEach(tf => {
                    Vue.set(this.timeframes, tf, this.makeTimeframeItem(tf, { className: '', entry: {} }));
                });
            } else if (this.connector === 'Upstox') {
                // Use Upstox-supported timeframes
                this.timeframes = {};
                UPSTOX_SUPPORTED_TFS.forEach(tf => {
                    Vue.set(this.timeframes, tf, this.makeTimeframeItem(tf, { className: '', entry: {} }));
                });
            } else if (this.connector === 'Dhan') {
                this.timeframes = {};
                DHAN_SUPPORTED_TFS.forEach(tf => {
                    Vue.set(this.timeframes, tf, this.makeTimeframeItem(tf, { className: '', entry: {} }));
                });
            } else if (this.connector === 'Indian' || this.connector === 'AliceBlue') {
                // Use AliceBlue-supported timeframes for Indian market data.
                this.timeframes = {};
                ALICEBLUE_SUPPORTED_TFS.forEach(tf => {
                    Vue.set(this.timeframes, tf, this.makeTimeframeItem(tf, { className: '', entry: {} }));
                });
            } else if (['Coinbase', 'Kraken', 'Bybit', 'OKX'].includes(this.connector)) {
                this.timeframes = {};
                CRYPTO_PROXY_SUPPORTED_TFS.forEach(tf => {
                    Vue.set(this.timeframes, tf, this.makeTimeframeItem(tf, { className: '', entry: {} }));
                });
            } else if (this.connector === 'AMFI') {
                this.timeframes = {};
                AMFI_SUPPORTED_TFS.forEach(tf => {
                    Vue.set(this.timeframes, tf, this.makeTimeframeItem(tf, { className: '', entry: {} }));
                });
            } else {
                this.timeframes = allTimeframes;
            }
            this.touchTimeframeRender();
            this.initializeCustomTimeframes();

            // Destroy existing WebSocket connections
            binanceApi.destroyWs();
            intApi.destroyWs();
            fyersApi.destroyWs();
            upstoxApi.destroyWs();
            dhanApi.destroyWs();
            coinbaseApi.destroyWs();
            krakenApi.destroyWs();
            bybitApi.destroyWs();
            okxApi.destroyWs();

            // Initialize WebSocket connections based on the selected connector
            if (this.connector === "Binance") {
                if (this.symbol.toUpperCase().includes(".P")) {
                    this.createBinanceFuturesWebsocket();
                } else {
                    this.createBinanceSpotWebsocket();
                }
            } else if (this.connector === "Int") {
                this.createIntWebsocket();
            } else if (this.connector === "Fyers" && fyersApi.isAuthenticated()) {
                this.createFyersWebsocket();
            } else if (this.connector === "Upstox" && upstoxApi.isAuthenticated()) {
                this.createUpstoxWebsocket();
            } else if (this.connector === "Dhan" && dhanApi.isAuthenticated()) {
                this.createDhanWebsocket();
            } else if (this.connector === "Coinbase") {
                this.createCoinbaseWebsocket();
            } else if (this.connector === "Kraken") {
                this.createKrakenWebsocket();
            } else if (this.connector === "Bybit") {
                this.createBybitWebsocket();
            } else if (this.connector === "OKX") {
                this.createOkxWebsocket();
            }

            // Load symbol data
            this.loadSymbol(this.connector, this.session, this.exchange, this.symbol, this.category, false);
        },

        loadSymbol(connector, session, exchange, _symbol, category, preload) {
              this.loadAllTimeframe(connector, session, exchange, _symbol, category);
        },

        getChartLoadInvalidReason() {
            if (!this.symbol || String(this.symbol).trim() === '') {
                return 'symbol is missing';
            }
            if (!this.connector || String(this.connector).trim() === '') {
                return 'connector is missing';
            }
            if (this.connector === 'Indian' && this.category === 'IndianMarket') {
                const instrumentKey = this.pairId || this.symbol;
                if (!instrumentKey || String(instrumentKey).indexOf('|') === -1) {
                    return 'Indian market instrumentKey is missing or invalid';
                }
            }
            return '';
        },

        loadAllTimeframe(connector, session, exchange, _symbol, category) {
              // Bump load generation so stale async responses are discarded
              this._loadGeneration = (this._loadGeneration || 0) + 1;
              const currentGen = this._loadGeneration;
              // Use appropriate timeframes based on the connector set during
              // symbol selection — NOT the exchange parameter which may be stale.
              const effectiveConnector = this.connector || connector;
              let tfs;
              if (effectiveConnector === 'Fyers') {
                  tfs = FYERS_SUPPORTED_TFS;
              } else if (effectiveConnector === 'Upstox') {
                  tfs = UPSTOX_SUPPORTED_TFS;
              } else if (effectiveConnector === 'Indian' || effectiveConnector === 'AliceBlue') {
                  tfs = ALICEBLUE_SUPPORTED_TFS;
              } else if (['Coinbase', 'Kraken', 'Bybit', 'OKX'].includes(effectiveConnector)) {
                  tfs = CRYPTO_PROXY_SUPPORTED_TFS;
              } else if (effectiveConnector === 'AMFI') {
                  tfs = AMFI_SUPPORTED_TFS;
              } else {
                  tfs = ['1m', '3m', '5m', '15m', '30m', '1H', '2H', '4H', '6H', '8H', '12H', '1D', '3D', '1W', '1M'];
              }
              const regularTfs = [...tfs];
              const customTfs = this.customTimeframes.map(ctf => ctf.timeframe);
              tfs = [...tfs, ...customTfs.filter(tf => !tfs.includes(tf))];
              this.initializeCustomTimeframes();
              
              // Ensure current timeframe is in the list, if not set it to first available
              if (!tfs.includes(this.timeframe)) {
                  this.timeframe = tfs[0];
              }
              const current_tf = tfs.indexOf(this.timeframe);

              this.loaded = {};
              
              // Clear process cache on symbol change
              this._processCache = {};
              this._cacheKeys = [];

              // Preserve current overlays (drawings + indicators) before resetting
              // Store them on a persistent property so loadTimeframe can restore them
              this._savedDrawings = null;
              var sourceOnchart = null;
              var sourceOffchart = null;
              if (this.chart[this.timeframe] && this.chart[this.timeframe].data) {
                  sourceOnchart = this.chart[this.timeframe].data.onchart || [];
                  sourceOffchart = this.chart[this.timeframe].data.offchart || [];
              } else if (this.dataset[this.timeframe]) {
                  sourceOnchart = this.dataset[this.timeframe].onchart || [];
                  sourceOffchart = this.dataset[this.timeframe].offchart || [];
              }
              if (sourceOnchart || sourceOffchart) {
                  var cloned = {
                      onchart: _.cloneDeep(sourceOnchart || []),
                      offchart: _.cloneDeep(sourceOffchart || [])
                  };
                  // For computed indicators (non-Tool types), clear stale data
                  // so they don't render wrong values on other timeframes.
                  // Drawing tools (type contains 'Tool') keep their coordinate data.
                  var clearIndicatorData = function(overlays) {
                      for (var ov of overlays) {
                          if (ov.type && ov.type.indexOf('Tool') === -1 &&
                              ov.type !== 'Candles' && ov.type !== 'Volume') {
                              ov.data = [];
                          }
                      }
                  };
                  clearIndicatorData(cloned.onchart);
                  clearIndicatorData(cloned.offchart);
                  this._savedDrawings = cloned;
              }

              for (let i = 0; i < tfs.length; i++) {
                  const tf = tfs[i];
                  if (session && session.properties && session.properties[_symbol]) {
                      this.dataset[tf] = _.cloneDeep(session.properties[_symbol]);
                  } else {
                      this.dataset[tf] = _.cloneDeep(Data);
                  }
                  this.applyPersistedDefaultsToTimeframe(tf);
                  this.dataset[tf].chart.data = [];
              }

              const trendMarker=[...(this.dataset[this.timeframe]?.onchart||[]),...(this.dataset[this.timeframe]?.offchart||[])].find(item=>item.type==='TimeframeTrend');
              if(['all','current'].includes(trendMarker?.settings?.trendScope))this.indicatorTrendLoadScope=trendMarker.settings.trendScope;

              // Load background timeframes with delay to respect API rate limits.
              //
              // Fyers hard limit: 10 req/sec, and exceeding the per-minute
              // limit 3 times in a day blocks access for the rest of the day.
              //
              // Strategy: load the CURRENT timeframe first (so the chart
              // renders immediately), then load remaining timeframes ONE AT A
              // TIME sequentially with a pause between each request.
              // This keeps us well under the rate limit (~2 req/sec).
              const isFyers = effectiveConnector === 'Fyers';
              const isUpstox = effectiveConnector === 'Upstox';
              const isAliceBlue = effectiveConnector === 'Indian' || effectiveConnector === 'AliceBlue';

              // Background regular timeframes load first. Custom timeframes are
              // derived afterward so they can choose the source that generates
              // the most custom candles from the data that is actually available.
              const bgTimeframes = regularTfs.filter(tf => tf !== this.timeframe);
              const bgCustomTimeframes = customTfs;
              const loadCustomQueue = async (genAtStart) => {
                  for (const tf of bgCustomTimeframes) {
                      if (this.indicatorTrendLoadScope==='current')return;
                      if (this._loadGeneration !== genAtStart) return;
                      try {
                          await this.autoLoadTimeframe(tf, category, genAtStart);
                      } catch (err) {
                          console.warn(`[Chart] Failed to auto-load custom tf=${tf}`, err.message);
                      }
                  }
                  if (this._loadGeneration === genAtStart) {
                      this.updateAITradingContext();
                  }
              };

              // Automatic loading mirrors the Binance behavior: all remaining
              // supported timeframes are started in the background. Broker
              // requests keep a small stagger and verify that candles actually
              // landed, because their loadTimeframe branches can return null
              // after an internally handled API error.
              // AliceBlue is the most rate-limited (~1 req/sec/user historical). Serialize
              // strictly and space requests >1.1s apart to stay within the per-user limit.
              const BATCH_SIZE = isUpstox ? 2 : (isAliceBlue ? 1 : (isFyers ? 4 : bgTimeframes.length || 1));
              const INTRA_BATCH_DELAY = isUpstox ? 900 : (isAliceBlue ? 0 : (isFyers ? 180 : 0));
              const INTER_BATCH_DELAY = isUpstox ? 1200 : (isAliceBlue ? 1200 : (isFyers ? 350 : 0));

              const loadBatchQueue = async (queue, genAtStart) => {
                  for (let batchStart = 0; batchStart < queue.length; batchStart += BATCH_SIZE) {
                      if (this._loadGeneration !== genAtStart || this.indicatorTrendLoadScope==='current') return;

                      const batch = queue.slice(batchStart, batchStart + BATCH_SIZE);
                      const batchPromises = batch.map((tf, idx) => {
                          return new Promise(async (resolve) => {
                              if (idx > 0) {
                                  await new Promise(r => setTimeout(r, idx * INTRA_BATCH_DELAY));
                              }
                              if (this._loadGeneration !== genAtStart || this.indicatorTrendLoadScope==='current') { resolve(); return; }

                              try {
                                  await this.autoLoadTimeframe(tf, category, genAtStart);
                              } catch (err) {
                                  console.warn(`[${effectiveConnector}] Failed to auto-load tf=${tf}, retrying...`, err.message);
                                  await new Promise(r => setTimeout(r, INTER_BATCH_DELAY));
                                  if (this._loadGeneration !== genAtStart) { resolve(); return; }
                                  try {
                                      await this.autoLoadTimeframe(tf, category, genAtStart);
                                  } catch (retryErr) {
                                      console.error(`[${effectiveConnector}] Retry failed for tf=${tf}`, retryErr.message);
                                  }
                              }
                              resolve();
                          });
                      });

                      await Promise.all(batchPromises);

                      if (batchStart + BATCH_SIZE < queue.length) {
                          await new Promise(r => setTimeout(r, INTER_BATCH_DELAY));
                      }
                  }
              };

              const tfRefs = this.$refs["item.timeframe"];
              const currentRef = tfRefs ? tfRefs[current_tf] : null;
              this.query = _symbol;
              const preloadPromise = loadBatchQueue(bgTimeframes, currentGen)
                  .then(() => loadCustomQueue(currentGen));
              this._timeframePreloadPromise=preloadPromise;

              this.autoLoadTimeframe(this.timeframe, category, currentGen, currentRef).then(() => {
                  this.protectChart = false;
                  this.resetkey++;
                  this.refreshCurrentIndicators(this.timeframe);
                  this.updateAITradingContext();
                  preloadPromise.then(() => this.updateAITradingContext());
              }).catch((err) => {
                  console.warn(`[${effectiveConnector}] Current tf=${this.timeframe} failed; continuing automatic preload`, err && err.message);
                  if (this.connector === 'Indian' && this.category === 'IndianMarket') {
                      this.chart = {};
                      this.dataset = {};
                      this.protectChart = true;
                      return;
                  }
                  this.protectChart = false;
                  preloadPromise.then(() => this.updateAITradingContext());
              });
        },
        hasTimeframeCandles(tf) {
            const candles = this.dataset && this.dataset[tf] &&
                this.dataset[tf].chart && this.dataset[tf].chart.data;
            return Array.isArray(candles) && candles.length > 0;
        },
        async autoLoadTimeframe(tf, category, genAtStart, e) {
            const maxAttempts = 3;
            let lastError = null;
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                if (this._loadGeneration !== genAtStart) return null;
                try {
                    await this.loadTimeframe(
                        this.connector,
                        this.exchange,
                        this.convertTimeframe(this.exchange, tf),
                        tf,
                        category,
                        e || null
                    );
                } catch (err) {
                    lastError = err;
                }
                if (this._loadGeneration !== genAtStart) return null;
                if (this.hasTimeframeCandles(tf)) {
                    const candles = this.dataset[tf].chart.data;
                    const data = this.processTimeframes(candles, tf);
                    if (data) {
                        this.setTimeframeProcessed(tf, data);
                    }
                    if (tf === this.timeframe) {
                        this.refreshChartRender();
                        this.refreshCurrentIndicators(tf);
                    }
                    return data;
                }
                if (attempt < maxAttempts) {
                    await new Promise(r => setTimeout(r, attempt * 1200));
                }
            }
            throw lastError || new Error(`No candle data loaded for ${tf}`);
        },
        convertTimeframe(exchange, timeframe) {
            if (exchange === "Binance") {
                return timeframe
            } else if (exchange === "Int") {
                return timeframe
            } else if (exchange === "Fyers" || exchange === "NSE" || exchange === "BSE") {
                return timeframe
            } else if (exchange === "ByBit") {
               if (timeframe.includes("m")) {
                  return timeframe.replace("m", "")
               } else if (timeframe.includes("h")) {
                  return timeframe.replace("h", "") * 60
               } else if (timeframe.includes("d")) {
                  return "D"
               }  else if (timeframe.includes("w")) {
                  return "W"
               } else {
                  return "M"
               }
            }
        },
        getCurrency(asset) {
            var cryptocurrencies = Cryptocurrencies

            if (cryptocurrencies[asset]) {
                return cryptocurrencies[asset]
            }
            return asset
        },
        async loadTimeframe(connector, exchange, _converted_tf, tf, category, e) {
            const that = this
            // Always use the connector set during symbol selection (this.connector)
            // so that the correct exchange API is called regardless of what was
            // passed as a parameter from loadAllTimeframe.
            const actualConnector = this.connector || connector;
            // Capture the current load generation so we can discard results if
            // a newer symbol load has started while this request was in-flight.
            const gen = this._loadGeneration || 0;
            
            // Check if this is a custom timeframe
            const customTimeframe = this.customTimeframes.find(ctf => ctf.timeframe === tf);
            if (customTimeframe) {
                
                // Find the base timeframe we need to derive from. Prefer the loaded
                // source that produces the most custom candles; otherwise load the
                // smallest compatible regular timeframe as a fallback.
                const baseInterval = this.getBestLoadedBaseTimeframe(customTimeframe) || this.getNearestBaseTimeframe(customTimeframe);
                if (!baseInterval) {
                    console.warn('No suitable base timeframe for custom timeframe:', tf);
                    return null;
                }
                
                // Check if base data is available
                let baseCandles = null;
                const baseDc = this.chart[baseInterval];
                if (baseDc && baseDc.data && baseDc.data.chart && baseDc.data.chart.data && baseDc.data.chart.data.length > 0) {
                    baseCandles = baseDc.data.chart.data;
                } else if (this.dataset[baseInterval] && this.dataset[baseInterval].chart && this.dataset[baseInterval].chart.data && this.dataset[baseInterval].chart.data.length > 0) {
                    baseCandles = this.dataset[baseInterval].chart.data;
                }
                
                // If base data not available, load it first then derive
                if (!baseCandles || baseCandles.length === 0) {
                    return this.loadTimeframe(
                        connector, exchange,
                        this.convertTimeframe(exchange, baseInterval),
                        baseInterval, category, null
                    ).then(() => {
                        // Now derive the custom timeframe from the loaded base
                        return this._buildCustomTimeframeChart(customTimeframe, tf);
                    });
                }
                
                // Base data available, derive directly
                return this._buildCustomTimeframeChart(customTimeframe, tf);
            }
            
            
            // DataProviderManager is NOT used for direct connector routing.
            // The connector (Binance / Fyers / Int / Indian) is determined by
            // onSelect and stored in this.connector.  We always use the direct
            // API path for each connector below.
            
            if (actualConnector === "Binance") {
                // Clean the symbol for Binance API: remove slashes, exchange
                // prefixes, and ensure uppercase
                let binanceSymbol = this.symbol.toUpperCase().replace("/", "");
                // Strip any exchange prefix like "Binance:" if present
                if (binanceSymbol.includes(":")) {
                    binanceSymbol = binanceSymbol.split(":").pop();
                }
                return binanceApi.loadKlineFromBinance(binanceSymbol, tf)
                    .then(response => response.json())
                    .then(resp => {
                        // Discard if a newer symbol load has started
                        if (gen !== that._loadGeneration) return null;
                        if (resp) {
                            var candles = []
                            if (that.session && that.session.properties) {
                                if (that.session.properties[that.query]) {
                                    that.dataset[tf] = _.cloneDeep(that.session.properties[that.query])
                                } else {
                                    that.dataset[tf] = _.cloneDeep(Data)
                                }
                            } else if (!that.dataset[tf]) {
                                that.dataset[tf] = _.cloneDeep(Data)
                            }
                            that.finalizeIndicatorsForTimeframe(tf)

                            resp.map(function(item, key) {
                                candles.push([item[0],
                                    parseFloat(item[1]),
                                    parseFloat(item[2]),
                                    parseFloat(item[3]),
                                    parseFloat(item[4]),
                                    parseFloat(item[5])])
                            });
                            var data = that.processTimeframes(candles, tf)

                            if (data) {
                                that.setTimeframeProcessed(tf, data)
                            }

                            that.dataset[tf].chart.tf = tf
                            that.dataset[tf].chart.interval_ms = Utils.parse_tf(tf)
                            that.dataset[tf].chart.data = [...candles]

                            Vue.set(that.chart, tf, new DataCube(that.dataset[tf]))
                            if (tf === that.timeframe) that.refreshCurrentIndicators(tf)

                            if (e) {
                                that.timeframe = tf
                                that.tf = tf
                            }

                            that.session.properties[that.query] = that.dataset[tf]

                            // Full OHLCV per loaded interval; replayed snapshots replace volume.
                            that.subscribeBinanceCandles()
                            return data
                        }
                        return null
                    });
            } else if (["Coinbase", "Kraken", "Bybit", "OKX", "AMFI"].includes(actualConnector)) {
                // market_proxy.js already normalizes every exchange's candles
                // to the same [time, open, high, low, close, volume] shape
                // Binance returns, so parsing here is identical regardless
                // of connector.
                const proxyApi = { Coinbase: coinbaseApi, Kraken: krakenApi, Bybit: bybitApi, OKX: okxApi, AMFI: amfiApi }[actualConnector];
                const wsSymbol = actualConnector === "Kraken" ? (this.pairId || this.symbol) : this.symbol;
                return proxyApi.loadKline(this.symbol, tf)
                    .then(resp => {
                        if (gen !== that._loadGeneration) return null;
                        if (resp) {
                            var candles = [];
                            if (that.session && that.session.properties) {
                                if (that.session.properties[that.query]) {
                                    that.dataset[tf] = _.cloneDeep(that.session.properties[that.query])
                                } else {
                                    that.dataset[tf] = _.cloneDeep(Data)
                                }
                            } else if (!that.dataset[tf]) {
                                that.dataset[tf] = _.cloneDeep(Data)
                            }
                            that.finalizeIndicatorsForTimeframe(tf)

                            resp.map(function(item) {
                                candles.push([item[0], Number(item[1]), Number(item[2]), Number(item[3]), Number(item[4]), Number(item[5])])
                            });
                            var data = that.processTimeframes(candles, tf)

                            if (data) {
                                that.setTimeframeProcessed(tf, data)
                            }

                            that.dataset[tf].chart.tf = tf
                            that.dataset[tf].chart.interval_ms = Utils.parse_tf(tf)
                            that.dataset[tf].chart.data = [...candles]

                            Vue.set(that.chart, tf, new DataCube(that.dataset[tf]))
                            if (tf === that.timeframe) that.refreshCurrentIndicators(tf)

                            if (e) {
                                that.timeframe = tf
                                that.tf = tf
                            }

                            that.session.properties[that.query] = that.dataset[tf]

                            // Only subscribe to live WebSocket ticks for the current
                            // timeframe (AMFI has no WS — NAV is daily, not live).
                            if (tf === that.timeframe && actualConnector !== "AMFI") {
                                proxyApi.subscribe(wsSymbol)
                            }
                            return data
                        }
                        return null
                    });
            } else  if (actualConnector === "Int") {
               var interval = ""
               var conv_interval = ""
               if (tf === "1m") {
                  interval = "PT1M"
               } else if (tf === "3m") {
                  interval = "PT1M"
                  conv_interval = "3m"
               }  else if (tf === "5m") {
                  interval = "PT5M"
               } else if (tf === "15m") {
                  interval = "PT15M"
               } else if (tf === "30m") {
                  interval = "PT30M"
               } else if (tf === "1H") {
                  interval = "PT1H"
               } else if (tf === "2H") {
                  interval = "PT1H"
                  conv_interval = "2H"
               } else if (tf === "4H") {
                  interval = "PT1H"
                  conv_interval = "4H"
               } else if (tf === "6H") {
                  interval = "PT1H"
                  conv_interval = "6H"
               } else if (tf === "8H") {
                  interval = "PT1H"
                  conv_interval = "8H"
               } else if (tf === "12H") {
                  interval = "PT1H"
                  conv_interval = "12H"
               } else if (tf === "1D") {
                  interval = "P1D"
               } else if (tf === "3D") {
                  interval = "P3D"
                  conv_interval = "3D"
               } else if (tf === "1W") {
                  interval = "P1W"
               } else if (tf === "1M") {
                  interval = "P1M"
               } else {
                  interval = ""
               }
               if (interval) {
                return intApi.loadKlineFromInvesting(this.pairId, interval)
                   .then(response => response.json())
                   .then(resp => {
                        // Discard if a newer symbol load has started
                        if (gen !== that._loadGeneration) return null;
                        if (resp && resp.data) {
                            var candle = that.loadIntData(that, resp.data, tf, e)
                            if (interval === "PT1M") {
                                var conv_tf = tf.replace("m", "")
                                var data = that.processNMCandles(that, candle, conv_tf, tf, "day")
                                if (conv_interval) {
                                    candle = that.loadIntData(that, data, conv_interval, e)
                                }
                            }
                            if (interval === "PT1H") {
                                var conv_tf = tf.replace("H", "")
                                var data = that.processNMCandles(that, candle, conv_tf, tf, "day")
                                if (conv_interval) {
                                    candle = that.loadIntData(that, data, conv_interval, e)
                                }
                            }
                            if (interval === "P1D") {
                                var conv_tf = tf.replace("D", "")
                                var data = that.processNMCandles(that, candle, conv_tf, tf, "month")
                                if (conv_interval) {
                                    that.loadIntData(that, data, conv_interval, null)
                                }
                            }
                            return candle
                        }
                        return null
                   });
               }
            } else if (actualConnector === "Fyers") {
                // Guard against empty symbol
                if (!this.symbol || this.symbol.trim() === '') {
                    console.warn('[Chart] Fyers loadTimeframe called with empty symbol, skipping');
                    return Promise.reject(new Error('Symbol not set'));
                }

                // Check if symbol already has exchange prefix
                let fullSymbol = this.symbol;
                if (this.symbol && !this.symbol.includes(':') && this.exchange) {
                    // Only add exchange if symbol doesn't already have it
                    fullSymbol = `${this.exchange}:${this.symbol}`;
                }
                return fyersApi.loadHistoricalData(fullSymbol, tf)
                    .then(candles => {
                        // Discard if a newer symbol load has started
                        if (gen !== that._loadGeneration) return null;
                        if (candles && candles.length > 0) {
                            if (that.session && that.session.properties) {
                                if (that.session.properties[that.query]) {
                                    that.dataset[tf] = _.cloneDeep(that.session.properties[that.query])
                                } else {
                                    that.dataset[tf] = _.cloneDeep(Data)
                                }
                            } else if (!that.dataset[tf]) {
                                that.dataset[tf] = _.cloneDeep(Data)
                            }
                            that.finalizeIndicatorsForTimeframe(tf)

                            var data = that.processTimeframes(candles, tf)

                            if (data) {
                                that.setTimeframeProcessed(tf, data)
                            }

                            that.dataset[tf].chart.tf = tf
                            that.dataset[tf].chart.interval_ms = Utils.parse_tf(tf)
                            that.dataset[tf].chart.data = [...candles]

                            Vue.set(that.chart, tf, new DataCube(that.dataset[tf]))
                            if (tf === that.timeframe) that.refreshCurrentIndicators(tf)

                            if (e) {
                                that.timeframe = tf
                                that.tf = tf
                            }

                            that.session.properties[that.query] = that.dataset[tf]

                            // Subscribe to Fyers WebSocket for live data
                            if (tf === that.timeframe) {
                                const symbolToSubscribe = fullSymbol || that.symbol;
                                fyersApi.subscribeToSymbol(symbolToSubscribe);
                            }
                            return data
                        }
                        return null
                    })
                    .catch(error => {
                        console.error('Fyers loadTimeframe error:', error);
                        // If token expired, show reconnect popup
                        if (error.message && error.message.includes('expired')) {
                            if (that.$refs.fyersPopup && !that.$refs.fyersPopup.visible) {
                                that.$refs.fyersPopup.expiredBrokerKey = 'fyers';
                                that.$refs.fyersPopup.mode = 'reconnect';
                                that.brokerReconnectNeeded = true;
                                if (!that.$refs.fyersPopup.isReconnectDismissed()) {
                                    that.$refs.fyersPopup.visible = true;
                                }
                            }
                        }
                        return null;
                    });
            } else if (actualConnector === "Upstox") {
                // Guard against empty symbol
                if (!this.symbol || this.symbol.trim() === '') {
                    console.warn('[Chart] Upstox loadTimeframe called with empty symbol, skipping');
                    return Promise.reject(new Error('Symbol not set'));
                }

                const upstoxSymbol = this.pairId || this.symbol;
                return upstoxApi.loadHistoricalData(upstoxSymbol, tf)
                    .then(candles => {
                        if (gen !== that._loadGeneration) return null;
                        if (candles && candles.length > 0) {
                            if (that.session && that.session.properties) {
                                if (that.session.properties[that.query]) {
                                    that.dataset[tf] = _.cloneDeep(that.session.properties[that.query])
                                } else {
                                    that.dataset[tf] = _.cloneDeep(Data)
                                }
                            } else if (!that.dataset[tf]) {
                                that.dataset[tf] = _.cloneDeep(Data)
                            }
                            that.finalizeIndicatorsForTimeframe(tf)

                            var data = that.processTimeframes(candles, tf)

                            if (data) {
                                that.setTimeframeProcessed(tf, data)
                            }

                            that.dataset[tf].chart.tf = tf
                            that.dataset[tf].chart.interval_ms = Utils.parse_tf(tf)
                            that.dataset[tf].chart.data = [...candles]

                            Vue.set(that.chart, tf, new DataCube(that.dataset[tf]))
                            if (tf === that.timeframe) that.refreshCurrentIndicators(tf)

                            if (e) {
                                that.timeframe = tf
                                that.tf = tf
                            }

                            that.session.properties[that.query] = that.dataset[tf]

                            // Subscribe to Upstox WebSocket for live data
                            if (tf === that.timeframe) {
                                upstoxApi.subscribeToSymbol(upstoxSymbol);
                            }
                            return data
                        }
                        return null
                    })
                    .catch(error => {
                        console.error('Upstox loadTimeframe error:', error);
                        if (error.message && error.message.includes('expired')) {
                            // Show reconnect popup for Upstox
                            if (that.$refs.fyersPopup && !that.$refs.fyersPopup.visible) {
                                that.$refs.fyersPopup.expiredBrokerKey = 'upstox';
                                that.$refs.fyersPopup.mode = 'reconnect';
                                that.brokerReconnectNeeded = true;
                                if (!that.$refs.fyersPopup.isReconnectDismissed()) {
                                    that.$refs.fyersPopup.visible = true;
                                }
                            }
                        }
                        return null;
                    });
            } else if (actualConnector === "Dhan") {
                if (!this.symbol || this.symbol.trim() === '') {
                    console.warn('[Chart] Dhan loadTimeframe called with empty symbol, skipping');
                    return Promise.reject(new Error('Symbol not set'));
                }

                const dhanSymbol = this.pairId || this.symbol;
                return dhanApi.loadHistoricalData(dhanSymbol, tf)
                    .then(candles => {
                        if (gen !== that._loadGeneration) return null;
                        if (candles && candles.length > 0) {
                            if (that.session && that.session.properties) {
                                if (that.session.properties[that.query]) {
                                    that.dataset[tf] = _.cloneDeep(that.session.properties[that.query])
                                } else {
                                    that.dataset[tf] = _.cloneDeep(Data)
                                }
                            } else if (!that.dataset[tf]) {
                                that.dataset[tf] = _.cloneDeep(Data)
                            }
                            that.finalizeIndicatorsForTimeframe(tf)

                            var data = that.processTimeframes(candles, tf)

                            if (data) {
                                that.setTimeframeProcessed(tf, data)
                            }

                            that.dataset[tf].chart.tf = tf
                            that.dataset[tf].chart.interval_ms = Utils.parse_tf(tf)
                            that.dataset[tf].chart.data = [...candles]

                            Vue.set(that.chart, tf, new DataCube(that.dataset[tf]))
                            if (tf === that.timeframe) that.refreshCurrentIndicators(tf)

                            if (e) {
                                that.timeframe = tf
                                that.tf = tf
                            }

                            that.session.properties[that.query] = that.dataset[tf]

                            if (tf === that.timeframe) {
                                dhanApi.subscribeToSymbol(dhanSymbol);
                            }
                            return data
                        }
                        return null
                    })
                    .catch(error => {
                        console.error('Dhan loadTimeframe error:', error);
                        if (error.message && error.message.includes('expired')) {
                            if (that.$refs.fyersPopup && !that.$refs.fyersPopup.visible) {
                                that.$refs.fyersPopup.expiredBrokerKey = 'dhan';
                                that.$refs.fyersPopup.mode = 'reconnect';
                                that.brokerReconnectNeeded = true;
                                if (!that.$refs.fyersPopup.isReconnectDismissed()) {
                                    that.$refs.fyersPopup.visible = true;
                                }
                            }
                        }
                        return null;
                    });
            } else if (exchange === "ByBit") {
                this.timeframe = tf
                return this.loadKlineFromByBit(this.symbol.toUpperCase(), _converted_tf, category)
                    .then(response => response.json())
                    .then(data => {
                        var candles = []
                        if (that.session && that.session.properties)
                        if (that.session.properties[that.query])
                            that.dataset[tf] = _.cloneDeep(that.session.properties[that.query])
                        else
                            that.dataset[tf] = _.cloneDeep(Data)
                        if (!that.dataset[tf]) {
                            that.dataset[tf] = _.cloneDeep(Data)
                        }
                        that.applyPersistedDefaultsToTimeframe(tf)
                        if (data.retMsg === "OK") {
                            data.result.list.map(function(item, key) {
                                candles.push([Number(item[0]),
                                    parseFloat(item[1]),
                                    parseFloat(item[2]),
                                    parseFloat(item[3]),
                                    parseFloat(item[4]),
                                    parseFloat(item[5])])
                            });
                            that.dataset[tf].chart.data = candles.reverse()
                            var data = that.processTimeframes(candles, tf)
                            that.setTimeframeProcessed(tf, data)
                            return data
                        }
                    });
            } else if (actualConnector === "Indian" || actualConnector === "AliceBlue") {
                if (this.category === "IndianMarket") {
                    const instrumentKey = this.pairId || this.symbol;
                    if (!instrumentKey || String(instrumentKey).indexOf('|') === -1) {
                        console.warn('[Chart] Indian market loadTimeframe called without a valid instrumentKey, skipping');
                        return Promise.resolve(null);
                    }
                }

                // For AliceBlue, the history API requires the numeric instrument token,
                // not the trading symbol name (e.g. "NIFTY 50"). this.pairId holds the
                // numeric token when the symbol was selected via AliceBlue search.
                const aliceTokenOrSymbol = this.pairId || this.symbol;
                const loader = this.category === "IndianMarket"
                    ? indianMarketApi.loadHistoricalData(this.pairId || this.symbol, tf)
                    : aliceApi.loadHistoricalData(`${this.exchange}:${aliceTokenOrSymbol}`, tf);

                return loader
                    .then(resp => {
                        // Discard if a newer symbol load has started
                        if (gen !== that._loadGeneration) return null;
                        if (!Array.isArray(resp) || resp.length === 0) return null;
                        var candles = []
                        if (that.session && that.session.properties)
                            if (that.session.properties[that.query])
                                that.dataset[tf] = _.cloneDeep(that.session.properties[that.query])
                            else
                                that.dataset[tf] = _.cloneDeep(Data)
                        if (!that.dataset[tf]) {
                            that.dataset[tf] = _.cloneDeep(Data)
                        }
                        that.finalizeIndicatorsForTimeframe(tf)

                        resp.map(function(item, key) {
                            candles.push([item[0],
                                parseFloat(item[1]),
                                parseFloat(item[2]),
                                parseFloat(item[3]),
                                parseFloat(item[4]),
                                parseFloat(item[5])])
                        });
                        that.dataset[tf].chart.tf = tf
                        that.dataset[tf].chart.interval_ms = Utils.parse_tf(tf)
                        that.dataset[tf].chart.indexBased = true
                        that.dataset[tf].chart.data = [...candles]
                        var data = that.processTimeframes(candles, tf)
                        that.setTimeframeProcessed(tf, data)

                        Vue.set(that.chart, tf, new DataCube(that.dataset[tf]))
                        if (tf === that.timeframe) that.refreshCurrentIndicators(tf)

                        if (e) {
                            that.timeframe = tf
                            that.tf = tf
                        }

                        that.session.properties[that.query] = that.dataset[tf]
                        //that.resetkey++
                        return data
                })
                .catch(error => {
                    console.error('AliceBlue loadTimeframe error:', error);
                    if (error.message && error.message.includes('expired')) {
                        if (that.$refs.fyersPopup && !that.$refs.fyersPopup.visible) {
                            that.$refs.fyersPopup.expiredBrokerKey = 'aliceblue';
                            that.$refs.fyersPopup.mode = 'reconnect';
                            that.brokerReconnectNeeded = true;
                            if (!that.$refs.fyersPopup.isReconnectDismissed()) {
                                that.$refs.fyersPopup.visible = true;
                            }
                        }
                    }
                    return null;
                });

            }
        },

        loadIntData(that, data, tf, e) {
           if (!data) return null;
           var candles = []
           if (that.session && that.session.properties) {
               if (that.session.properties[that.query]) {
                   that.dataset[tf] = _.cloneDeep(that.session.properties[that.query])
               } else {
                   that.dataset[tf] = _.cloneDeep(Data)
               }
           } else if (!that.dataset[tf]) {
               that.dataset[tf] = _.cloneDeep(Data)
           }
           that.finalizeIndicatorsForTimeframe(tf)

           data.map(function(item, key) {
               candles.push([item[0],
                   parseFloat(item[1]),
                   parseFloat(item[2]),
                   parseFloat(item[3]),
                   parseFloat(item[4]),
                   parseFloat(item[5])])
           });
           that.dataset[tf].chart.tf = tf
           that.dataset[tf].chart.interval_ms = Utils.parse_tf(tf)
           that.dataset[tf].chart.data = [...candles]
           var data = that.processTimeframes(candles, tf)
           that.setTimeframeProcessed(tf, data)

           Vue.set(that.chart, tf, new DataCube(that.dataset[tf]))
           if (tf === that.timeframe) that.refreshCurrentIndicators(tf)

           if (e) {
               that.timeframe = tf
               that.tf = tf
           }

           that.session.properties[that.query] = that.dataset[tf]
           var message = '{"_event":"bulk-subscribe","tzID": 8,"message":"pid-' + that.pairId + ':"}'
           intApi.sendWs(message)
           message = '{\"_event\":\"UID\",\"UID\": 0}'
           intApi.sendWs(message)
           return data
        },
        processAllTimeframes() {
           for (var timeframe in this.timeframes) {
               try {
                   if (!this.dataset[timeframe] || !this.dataset[timeframe].chart || !this.dataset[timeframe].chart.data) {
                       continue;
                   }
                   var candles = this.dataset[timeframe].chart.data;
                   if (!candles || !Array.isArray(candles) || candles.length === 0) {
                       continue;
                   }
                   var data = this.processTimeframes(candles, timeframe);
                   if (data) {
                       this.setTimeframeProcessed(timeframe, { ...data });
                   }
               } catch (e) {
                   console.warn('Error processing timeframe:', timeframe, e);
               }
           }
           this.$forceUpdate();
        },
        toggleSecondsMenu() {
            this.showSecondsMenu = !this.showSecondsMenu;
        },
        selectSeconds(timeframe) {
            this.showSecondsMenu = false;
            this.selectedSeconds = timeframe;
            
            // Load the seconds timeframe on demand
            this.loadTimeframe(
                this.connector, 
                this.exchange,
                this.convertTimeframe(this.exchange, timeframe),
                timeframe, 
                this.category, 
                null
            ).then(() => {
                // Switch to this timeframe
                this.timeframe = timeframe;
                this.protectChart = false;
                this.resetkey++;
            }).catch(error => {
                console.error('Failed to load seconds timeframe:', error);
            });
        },
        onTimeframeSelect(item, e) {
           return new Promise((resolve, reject) => {
               this.symbol = this.query;

               // Preserve overlays from current timeframe into _savedDrawings
               var prevTf = this.timeframe;
               if (this.chart[prevTf] && this.chart[prevTf].data) {
                   var onchart = _.cloneDeep(this.chart[prevTf].data.onchart || []);
                   var offchart = _.cloneDeep(this.chart[prevTf].data.offchart || []);
                   // Clear stale data for computed indicators
                   var clearIndicatorData = function(overlays) {
                       for (var ov of overlays) {
                           if (ov.type && ov.type.indexOf('Tool') === -1 &&
                               ov.type !== 'Candles' && ov.type !== 'Volume') {
                               ov.data = [];
                           }
                       }
                   };
                   clearIndicatorData(onchart);
                   clearIndicatorData(offchart);
                   this._savedDrawings = { onchart: onchart, offchart: offchart };
               }

               this.timeframe = item.timeframe;

               // If dataset doesn't exist for this timeframe, load it
               if (!this.dataset[this.timeframe] || !this.dataset[this.timeframe].chart) {
                   this.loadTimeframe(
                       this.connector, this.exchange,
                       this.convertTimeframe(this.exchange, this.timeframe),
                       this.timeframe, this.category, null
                   ).then(() => {
                       this._applyCurrentChartType(this.timeframe);
                       this.protectChart = false;
                       this.resetkey++;
                       this.refreshCurrentIndicators(this.timeframe);
                       resolve();
                   }).catch(reject);
                   return;
               }

               var candles = this.dataset[this.timeframe].chart.data;

               // If candles haven't loaded yet for this timeframe, reload
               if (!candles || !Array.isArray(candles) || candles.length === 0) {
                   this.loadTimeframe(
                       this.connector, this.exchange,
                       this.convertTimeframe(this.exchange, this.timeframe),
                       this.timeframe, this.category, null
                   ).then(() => {
                       this._applyCurrentChartType(this.timeframe);
                       this.protectChart = false;
                       this.resetkey++;
                       this.refreshCurrentIndicators(this.timeframe);
                       resolve();
                   }).catch(reject);
                   return;
               }

               var data = this.processTimeframes(candles, this.timeframe);
               this.setTimeframeProcessed(this.timeframe, data);

               this.dataset[this.timeframe].chart.data = [...candles];
               this.dataset[this.timeframe].chart.tf = this.timeframe;
               this.dataset[this.timeframe].chart.interval_ms = Utils.parse_tf(this.timeframe);

               this.finalizeIndicatorsForTimeframe(this.timeframe);

               // Apply current chart type to this timeframe
               this._applyCurrentChartType(this.timeframe);

               Vue.set(this.chart, this.timeframe, new DataCube(this.dataset[this.timeframe]));
               this.resetkey++;
               this.refreshCurrentIndicators(this.timeframe);

               resolve();
           })
           .then(() => {
               // Handle subsequent chart loading logic here, if necessary
               // For example:
               // this.loadChart();
               this.$emit('reset-chart', e)
           });
        },
        processTimeframes(candles, tf) {
            // Guard against empty or invalid candle data
            if (!candles || !Array.isArray(candles) || candles.length === 0) {
                return this.makeTimeframeItem(tf, { className: '', entry: {} });
            }
            
            // Add caching for processed data
            var lastCandle = candles[candles.length - 1];
            var lastTs = lastCandle ? lastCandle[0] : 0;
            const cacheKey = `${tf}_${candles.length}_${lastTs}`;
            if (this._processCache && this._processCache[cacheKey]) {
                return this._processCache[cacheKey];
            }
            
            if (!this._processCache) {
                this._processCache = {};
                // Limit cache size to prevent memory leaks
                this._cacheKeys = [];
            }
            
            var entry = {}
            var data = this.processVTSignals(candles, entry, true)
            
            // If processVTSignals returned empty, build a safe default
            if (!data || data.length === 0) {
                return this.makeTimeframeItem(tf, { className: '', entry: entry });
            }
            
            var result = this.onTimeframeUpdate(data, candles, tf)
            
            // Cache result with size limit
            if (this._cacheKeys.length > 50) {
                const oldKey = this._cacheKeys.shift();
                delete this._processCache[oldKey];
            }
            this._cacheKeys.push(cacheKey);
            this._processCache[cacheKey] = result;
            
            return result;
        },
        onTimeframeUpdate(data, candles, tf) {
            if (!data) {
                return this.makeTimeframeItem(tf, { className: '', entry: {} });
            }
            data.className = this.getCustomTimeframe(tf) ? 'custom-timeframe' : ""
            data.label = this.getTimeframeLabel(tf)
            data.value = tf
            data.isCustom = !!this.getCustomTimeframe(tf)
            if (data) {
              var entry = data.entry || {}
              var signal = ""
              var className = ""
              var className1 = ""
              data.timeframe = tf
              if (data && data.length > 0) {
                  var lastItem = data[data.length - 1];
                  if (!lastItem || lastItem.close === undefined) {
                      data.className = this.getCustomTimeframe(tf) ? "custom-timeframe p-hidden" : "p-hidden";
                      return data;
                  }
                  const cmp = Number(lastItem.close);
                  const smaColor = lastItem.smaColor;
                  const flashtarget = Number(entry.flashTarget || 0);
                  const target = Number(entry.target || 0);

                  if (entry.chartType === "icon-candlestick-vt-laser") {
                      var _latesttrend = Number(entry.latestFlashTrend)

                      if (this.latest_trend) {
                          if (_latesttrend > this.latest_trend) {
                              this.latest_trend = _latesttrend;
                              this.latest_timeframe = data.value;
                          }
                      } else {
                          this.latest_trend = _latesttrend;
                          this.latest_timeframe = data.value;
                      }
                      if (entry.flashSide === "BUY" && cmp < flashtarget) {
                          signal = "⇡"
                          className = "tc-buy";
                      } else if (entry.flashSide === "BUY" && cmp >= flashtarget) {
                          signal = "✓"
                          className = "tc-buy-completed";
                      } else if (entry.flashSide === "SELL" && cmp > flashtarget) {
                          signal = "⇣"
                          className = "tc-sell";
                      } else if (entry.flashSide === "SELL" && cmp <= flashtarget) {
                          signal = "✓"
                          className = "tc-sell-completed";
                      } else {
                          signal = "◦"
                      }

                      if (entry.call === "BUY" && cmp < target && smaColor === "GREEN") {
                          className1 = "tc-shadow-buy";
                      } else if (entry.call === "BUY" && cmp >= target && smaColor === "GREEN") {
                          className1 = "tc-shadow-buy-completed";
                      } else if (entry.call === "SELL" && cmp > target && smaColor === "RED") {
                          className1 = "tc-shadow-sell";
                      } else if (entry.call === "SELL" && cmp <= target && smaColor === "RED") {
                          className1 = "tc-shadow-sell-completed";
                      }

                      var label = data.label.replace("⇡", "").replace("⇣", "").replace("✓", "").replace("◦", "");

                      if (entry.flashActivated) {
                          data.label = signal + label.trim();

                          if (entry.flashSide === "BUY")
                              data.className = "tc-activated-buy" + " " + className1;
                          else if (entry.flashSide === "SELL")
                              data.className = "tc-activated-sell" + " " + className1;
                          else
                              data.className = className + " " + className1;
                      } else {
                          data.className = className + " " + className1;
                          data.label = signal + label.trim();
                      }
                  } else {
                      var _latesttrend = Number(entry.latesttrend)

                      if (this.latest_trend) {
                          if (_latesttrend > this.latest_trend) {
                              this.latest_trend = _latesttrend;
                              //this.latest_timeframe = data.value;
                          }
                      } else {
                          this.latest_trend = _latesttrend;
                          this.latest_timeframe = data.value;
                      }
                      if (entry.call === "BUY" && cmp < target && smaColor === "GREEN") {
                          signal = "⇡"
                          className = "tc-buy";
                      } else if (entry.call === "BUY" && cmp >= target && smaColor === "GREEN") {
                          signal = "✓"
                          className = "tc-buy-completed";
                      } else if (entry.call === "SELL" && cmp > target && smaColor === "RED") {
                          signal = "⇣"
                          className = "tc-sell";
                      } else if (entry.call === "SELL" && cmp <= target && smaColor === "RED") {
                          signal = "✓"
                          className = "tc-sell-completed";
                      } else {
                          signal = "◦"
                      }

                      if (entry.flashSide === "BUY" && cmp < flashtarget) {
                          className1 = "tc-shadow-buy";
                      } else if (entry.flashSide === "BUY" && cmp >= flashtarget) {
                          className1 = "tc-shadow-buy-completed";
                      } else if (entry.flashSide === "SELL" && cmp > flashtarget) {
                          className1 = "tc-shadow-sell";
                      } else if (entry.flashSide === "SELL" && cmp <= flashtarget) {
                          className1 = "tc-shadow-sell-completed";
                      }

                      var label = data.label.replace("⇡", "").replace("⇣", "").replace("✓", "").replace("◦", "");

                      if (entry.activated) {
                          data.label = signal + label.trim();

                          if (entry.call === "BUY")
                              data.className = "tc-activated-buy" + " " + className1;
                          else if (entry.call === "SELL")
                              data.className = "tc-activated-sell" + " " + className1;
                          else
                              data.className = className + " " + className1;
                      } else {
                          data.className = className + " " + className1;
                          data.label = signal + label.trim();
                      }
                  }
              } else {
                  data.className = this.getCustomTimeframe(tf) ? "custom-timeframe p-hidden" : "p-hidden";
              }
            }

              if (this.getCustomTimeframe(tf) && !String(data.className || '').includes('custom-timeframe')) {
                  data.className = (`custom-timeframe ${data.className || ''}`).trim();
              }

              if (this.latest_timeframe === data.timeframe) {
                  data.className = data.className + " tc-latest-signal";
              }
              return data;
        },
        processCandles(candles, tick, last_trade_time, ask, bid, timeframe, selectedEntry) {
            var candle = candles[candles.length - 1]
            var prevcandle = candles[candles.length - 2]
            if (prevcandle && prevcandle[0] && candle && candle[0]) {
              var prevDate = this.parseDate(prevcandle[0])
              var candleDate = this.parseDate(candle[0])
              var startDate = candleDate
              var endDate = startDate

              var interval_ms = 0
              var tf = 0
              var diff = 0

              if (timeframe.indexOf("m") !== -1) {
                  tf = timeframe.replace("m", "")
                  if (tf === "")
                      tf = "1"
                  diff = Number(tf)
                  startDate = moment(prevDate).add(diff, 'm').toDate()
                  endDate = moment(candleDate).add(diff,'m').toDate()
                  interval_ms = diff * 60 * 1000
              } else if (timeframe.indexOf("H") !== -1) {
                  tf = timeframe.replace("H", "");
                  if (tf === "")
                      tf = "1";
                  diff = Number(tf);
                  startDate = moment(prevDate).add(diff, 'h').toDate();
                  endDate = moment(startDate).add(diff,'h').toDate();
                  interval_ms = diff * 60 * 60 * 1000
              } else if (timeframe.indexOf("D") !== -1) {
                  tf = timeframe.replace("D", "");
                  if (tf === "")
                      tf = "1";
                  diff = Number(tf);
                  //startDate = moment(prevDate).add(diff, 'd').toDate();
                  endDate = moment(startDate).add(diff,'d').toDate();
                  interval_ms = diff * 24 * 60 * 60 * 1000
              } else if (timeframe.indexOf("W") !== -1) {
                  diff = 1;
                  //startDate = moment(prevDate).add(diff, 'w').toDate();
                  endDate = moment(startDate).add(diff,'w').toDate();
                  interval_ms = diff * 7 * 24 * 60 * 60 * 1000
              } else if (timeframe.indexOf("M") !== -1) {
                  diff = 1;
                  //startDate = moment(prevDate).add(diff, 'M').toDate();
                  endDate = moment(startDate).add(diff,'M').toDate();
                  interval_ms = diff * 30 * 24 * 60 * 60 * 1000
              }

              var startTime = moment(new Date());
              var endTime = moment(endDate);
              var duration = moment.duration(endTime.diff(startTime));

              var hours = parseInt(duration.asHours())%60;
              var minutes = parseInt(duration.asMinutes())%60;
              var seconds = parseInt(duration.asSeconds())%60;

              if (seconds >= 0 && minutes >= 0) {
                if (timeframe.indexOf("m") !== -1) {
                    candle.countDown = minutes + ":" + seconds;
                } else if (timeframe.indexOf("H") !== -1) {
                    candle.countDown = hours + ":" + minutes + ":" + seconds;
                } else {
                    candle.countDown = "";
                }
              } else {
                  candle.countDown = "";
              }
              if (ask) {
                candle.ask = ask;
              }
              if (bid) {
                candle.bid = bid;
              }

              var tradeTime = moment(last_trade_time);
              if (tradeTime >= moment(endDate)) {
                  var newCandle = Object.assign({}, candle)

                  newCandle[0] = last_trade_time
                  newCandle[1] = tick.open
                  newCandle[2] = tick.high
                  newCandle[3] = tick.low
                  newCandle[4] = tick.close

                  newCandle.date = last_trade_time
                  newCandle.open = tick.open
                  newCandle.high = tick.high
                  newCandle.low = tick.low
                  newCandle.close = tick.close

                  candles.push(newCandle)

              } else {
                  candle.close = tick.close
                  candle[4] = tick.close

                  if (tick.close > candle[2]) {
                      candle[2] = tick.high
                      candle.high = tick.high
                  }
                  if (tick.close < candle[3]) {
                      candle[3] = tick.low
                      candle.low = tick.low
                  }
              }
            }
            candles.interval_ms = interval_ms
            return candles
        },
        processNMCandles(that, candles, nmin, tf, interval) {
            var h_date_o = null
            var idx = 0
            var newCandles = []
            var date_o = null
            for (var j = 0; j < candles.length;) {
              var h_date = null
              var s_date = null
              h_date_o = null
              var h_open = 0
              var h_high = 0
              var h_low = Number.MAX_VALUE
              var h_close = 0
              var h_volume = 0
              var index = 0
              for (var i = 0; i < nmin; i++) {
                  if(j >= 0 && j < candles.length) {
                      var candle = candles[j]
                      var date = moment(candle.date)
                      date_o = date

                      if (h_date_o === null || moment(date_o).isSame(moment(h_date_o), interval)) {
                        var open = parseFloat(candle.open)
                        var high = parseFloat(candle.high)
                        var low = parseFloat(candle.low)
                        var close = parseFloat(candle.close)
                        var volume = parseInt(candle.volume)

                        h_close = close

                        if (index === 0)
                          h_open = open

                        if (high > h_high)
                          h_high = high

                        if (low < h_low)
                          h_low = low

                        h_volume = h_volume + volume
                        h_date = date
                        if (s_date === null)
                          s_date = date
                        h_date_o = date_o
                        index++
                        j++
                      }
                  }
              }
              if (h_date != null) {
                newCandles[idx] = []
                newCandles[idx].push(s_date.valueOf())
                newCandles[idx].push(h_open)
                newCandles[idx].push(h_high)
                newCandles[idx].push(h_low)
                newCandles[idx].push(h_close)
                newCandles[idx].push(h_volume)
                idx++
              }
            }
            return that.processTimeframes(newCandles, tf)
        },
        processEntryRecord(selectedEntry, tick, timeframe) {
           var chart = selectedEntry.data.chart.data
           var that = this
           if (chart) {
             Object.keys(chart).map(async (item, key) => {
                  var tfentry = chart[item]
                  if (tfentry) {
                      if (tfentry && tfentry.length > 0) {
                          var candle = tfentry[tfentry.length - 1]

                          if (tick.close !== 0) {
                              try {
                                  tfentry = that.processCandles(tfentry, tick, tick.date, tick.ask, tick.bid, item, chart)
                                  //var data = that.processTimeframes(tfentry, item)
                                  //data.timeframe = item
                                  //that.timeframes[item] = data
                                  chart[item] = tfentry
                              } catch(e) {
                                  throw e
                              }
                          }
                      }
                  }
             })
           }
           selectedEntry.data.chart.data = chart
           return selectedEntry
        },
        processVTSignals(candles, entry, loadTarget) {
            // Early return for empty data
            if (!candles || candles.length === 0 || !Array.isArray(candles)) {
                return [];
            }
            
            // Validate last candle exists and has expected structure
            var lastCandle = candles[candles.length - 1];
            if (!lastCandle || lastCandle[4] === undefined || lastCandle[4] === null) {
                return [];
            }
            
            var i = 0, j = 0
            var riskRewardRatio = 3 // this.risk_reward_ratio
            var smaLen = 50 // this.sma_length
            var dcLen = 20 //this.dc_length
            var clearEntry = true
            var pendingTargetB = 0
            var pendingTargetS = 0

            var lastEntry

            var sma = 0
            var dcHigh = 0
            var dcLow = 0
            var dcMid = 0
            var entryDirection = ""
            var entryPoint = 0
            var entrySL = 0
            var entryTarget = 0
            var signal = {}
            var blackCandle
            var trendChanged
            var prev
            var starFormed
            var blackCandleFormed
            var fractalHigh
            var fractalLow
            var activated = false
            var flashActivated = false
            var cmp = lastCandle[4]
            var data = [];
            var startIndex = 0
            var i = 0
            var kuchiSize = 0;

            if (!loadTarget)
                startIndex = Math.max(0, candles.length - 30)

            for (var p of candles) {
                if (!p || p.length < 5) { i++; continue; }
                let tradeTime = p[0]
                let open = p[1]
                let high = p[2]
                let low = p[3]
                let close = p[4]
                var now = { date: tradeTime, open: open, high: high, low: low, close: close }

                now[0] = p[0]
                now[1] = p[1]
                now[2] = p[2]
                now[3] = p[3]
                now[4] = p[4]
                now[5] = p[5]

                if (i >= smaLen) {
                  sma = 0
                  for (var idx = 0; idx < smaLen; idx++) {
                    var smaIndex = i - idx
                    if (smaIndex >= 0)
                      sma = sma + parseFloat(candles[smaIndex][4]) // close price
                    else
                      sma = sma + parseFloat(candles[0][4]) // close price of first candle
                  }

                  sma = parseFloat(sma / smaLen)
                } else {
                  sma = ""
                }

                dcHigh = 0
                for (var idx = 0; idx < dcLen; idx++) {
                  var dcIndex = i - idx;
                  if (dcIndex >= 0) {
                    var highPrice = parseFloat(candles[dcIndex][2])
                    if (highPrice > dcHigh)
                      dcHigh = highPrice // high price
                  }
                }

                dcLow = Number.MAX_VALUE
                for (var idx = 0; idx < dcLen; idx++) {
                  var dcIndex = i - idx
                  if (dcIndex >= 0) {
                    var lowPrice = parseFloat(candles[dcIndex][3])
                    if (lowPrice < dcLow)
                      dcLow = lowPrice // low price
                  }
                }

                dcMid = dcLow + Math.abs((dcHigh - dcLow) / 2)
                now.dcHigh = dcHigh
                now.dcLow = dcLow

                now.sma = sma
                if (now.fractalHigh) fractalHigh = now.fractalHigh
                if (now.fractalLow) fractalLow = now.fractalLow

                var candleSize = Math.abs(high - low)
                var bodySize = Math.abs(open - close)

                if (!prev) {
                    prev = now
                    prev.smaColor = ""
                }
                if(now.sma > prev.sma) {
                    now.smaColor = "GREEN"
                } else {
                    now.smaColor = "RED"
                }
                if (!now.tradedVolume) {
                    now.tradedVolume = 0
                }
                if (!now.kuchiSize) {
                    now.kuchiSize = 0
                }
                if (candleSize === 0) {
                    blackCandle = false
                    now.blackCandle = blackCandle
                } else {
                    if(bodySize <= (candleSize / 2)) {
                        blackCandle = true
                        now.blackCandle = blackCandle
                        now.kuchiSize = candleSize - bodySize
                    } else {
                        blackCandle = false
                        now.blackCandle = blackCandle
                    }
                }


                var p_open = Number(prev.open)
                var p_high = Number(prev.high)
                var p_low = Number(prev.low)
                var p_close = Number(prev.close)

                if (prev.blackCandle) {
                    var wick1 = 0
                    var wick2 = 0
                    var wickSize = 0
                    if (p_open > p_close) {
                        wick1 = p_high - p_open
                        wick2 = p_close - p_low
                    } else {
                        wick1 = p_high - p_close
                        wick2 = p_open - p_low
                    }
                    if (wick1 > wick2) {
                        wickSize = wick1
                    } else {
                        wickSize = wick2
                    }
                    if (close >= p_high) {
                        var flashTarget = p_high + Number(wickSize * riskRewardRatio)
                        var flashTarget1 = p_high + Number(wickSize * 1)
                        var flashTarget2 = p_high + Number(wickSize * 2)
                        var flashReverseTarget = p_low - Number(wickSize * riskRewardRatio)
                        var flashReverseTarget1 = p_low - Number(wickSize * 1)
                        var flashReverseTarget2 = p_low - Number(wickSize * 2)
                        now.flashTradeStart = true
                        now.flashTrade = true
                        now.flashSide = "BUY"
                        now.flashEntry = Number(p_high)
                        now.flashTarget = Number(flashTarget)
                        now.flashTarget1 = Number(flashTarget1)
                        now.flashTarget2 = Number(flashTarget2)
                        now.flashReverseTarget = Number(flashReverseTarget)
                        now.flashReverseTarget1 = Number(flashReverseTarget1)
                        now.flashReverseTarget2 = Number(flashReverseTarget2)
                        now.flashStoploss = Number(p_low)
                    } else if (close <= p_low) {
                        var flashTarget = p_low - Number(wickSize * riskRewardRatio)
                        var flashTarget1 = p_low - Number(wickSize * 1)
                        var flashTarget2 = p_low - Number(wickSize * 2)
                        var flashReverseTarget = p_high + Number(wickSize * riskRewardRatio)
                        var flashReverseTarget1 = p_high + Number(wickSize * 1)
                        var flashReverseTarget2 = p_high + Number(wickSize * 2)
                        now.flashTrade = true
                        now.flashTradeStart = true
                        now.flashSide = "SELL"
                        now.flashEntry = Number(p_low)
                        now.flashTarget = Number(flashTarget)
                        now.flashTarget1 = Number(flashTarget1)
                        now.flashTarget2 = Number(flashTarget2)
                        now.flashReverseTarget = Number(flashReverseTarget)
                        now.flashReverseTarget1 = Number(flashReverseTarget1)
                        now.flashReverseTarget2 = Number(flashReverseTarget2)
                        now.flashStoploss = Number(p_high)
                    } else {
                        now.flashTrade = false
                        now.flashTradeStart = false
                        now.flashSide = ""
                        now.flashEntry = 0
                        now.flashTarget = 0
                        now.flashTarget1 = 0
                        now.flashTarget2 = 0
                        now.flashReverseTarget = 0
                        now.flashReverseTarget1 = 0
                        now.flashReverseTarget2 = 0
                        now.flashStoploss = 0
                    }
                }

                if (!now.flashTrade) {
                    now.flashTrade = prev.flashTrade
                    now.flashTradeStart = false
                    now.flashSide = prev.flashSide
                    now.flashTarget = prev.flashTarget
                    now.flashTarget1 = prev.flashTarget1
                    now.flashTarget2 = prev.flashTarget2
                    now.flashReverseTarget = prev.flashReverseTarget
                    now.flashReverseTarget1 = prev.flashReverseTarget1
                    now.flashReverseTarget2 = prev.flashReverseTarget2
                }
                if(now.smaColor === prev.smaColor) {
                    now.trendChanged = false;
                } else {
                    trendChanged = true
                    now.trendChanged = trendChanged
                    starFormed = false
                    blackCandleFormed = false
                    entryDirection = ""
                    entryPoint = 0
                    entrySL = 0
                    entryTarget = 0
                }

                if (now.trendChanged != prev.trendChanged) {
                  starFormed = false
                }

                if (prev.blackCandle && now.trendChanged) {
                    starFormed = true
                    blackCandleFormed = true
                    if (now.smaColor === "GREEN") {
                        entryDirection = "BUY"
                        entryPoint = prev.high
                        entrySL = prev.low
                        entryTarget = entryPoint + Number(( entryPoint - entrySL) * riskRewardRatio)
                        kuchiSize = kuchiSize + now.kuchiSize
                    } else {
                        entryDirection = "SELL"
                        entryPoint = prev.low
                        entrySL = prev.high
                        entryTarget = entryPoint - Number(( entrySL - entryPoint ) * riskRewardRatio)
                        kuchiSize = kuchiSize + now.kuchiSize
                    }
                } else {
                    starFormed = false
                }

                if ((starFormed || trendChanged) && now.blackCandle) {
                    blackCandleFormed = true
                    if (now.smaColor === "GREEN") {
                        entryDirection = "BUY"
                        if (entryPoint === 0)
                            entryPoint = high
                        if (entrySL === 0)
                            entrySL = low
                        if (low < entrySL)
                            entrySL = low
                        if (high > entryPoint)
                            entryPoint = high
                        entryTarget = entryPoint + Number(( entryPoint - entrySL) * riskRewardRatio)
                        kuchiSize = kuchiSize + now.kuchiSize
                    } else {
                        entryDirection = "SELL"
                        if (entryPoint === 0)
                            entryPoint = low
                        if (entrySL === 0)
                            entrySL = high
                        if (high > entrySL)
                            entrySL = high
                        if (low < entryPoint)
                            entryPoint = low
                        entryTarget = entryPoint - Number(( entrySL - entryPoint ) * riskRewardRatio)
                        kuchiSize = kuchiSize + now.kuchiSize
                    }
                }

                if ((starFormed || trendChanged) && blackCandleFormed && !now.blackCandle) {
                    trendChanged = false
                    now.isEntry = true
                }

                if (now.isEntry) {
                    //now.trendChanged = trendChanged
                    now.entryDirection = entryDirection
                    now.entryPoint = Number(entryPoint)
                    now.entryStoploss = Number(entrySL)
                    now.entryTarget = Number(entryTarget)

                    if (entry && entryDirection) {
                        var alert = ""
                        entry.call = entryDirection
                        entry.entry = entryPoint
                        entry.stoploss = entrySL
                        entry.target = Number(entryTarget)
                        entry.latesttrend = now.date
                        entry.index = i
                        entry.cmp = cmp
                        entry.tradedVolume = Math.abs(kuchiSize)
                        var targetIndex = candles.length - 10

                        if (entryDirection === "BUY") {
                          if (cmp >= high || high >= now.dcHigh) {
                              alert = "TARGET"
                          } else if (cmp <= now.dcMid || low <= now.dcMid
                              || cmp <= entrySL) {
                              alert = "WARN"
                          } else if (cmp <= now.dcLow || low <= now.dcLow
                              || now.trendChanged) {
                              alert = "STOP"
                          }

                          if ((cmp < entryPoint && cmp > entrySL)
                              || (cmp >= entryPoint && cmp < entryTarget)
                              && ("TARGET" === alert  || alert === "")) {
                              activated = true
                          } else {
                              activated = false
                          }
                        } else if (entryDirection === "SELL") {
                          if (cmp >= now.dcHigh || high >= now.dcHigh || now.trendChanged) {
                              alert = "STOP"
                          } else if (cmp >= now.dcMid || high >= now.dcMid
                              || cmp >= entrySL) {
                              alert = "WARN"
                          } else if (cmp <= now.dcLow || low <= now.dcLow) {
                              alert = "TARGET"
                          }

                          if ((cmp > entryPoint && cmp < entrySL)
                              || (cmp <= entryPoint && cmp > entryTarget)
                              && ("TARGET" === alert  || alert === "")) {
                              activated = true
                          } else {
                              activated = false
                          }
                        }
                    }

                    if (!now.blackCandle) {
                        trendChanged = false
                        blackCandleFormed = false
                        starFormed = false
                        entryDirection = ""
                        entrySL = 0
                        entryPoint = 0
                        entryTarget = 0
                        kuchiSize = 0
                    }
                }

                if (entry.call === "BUY") {
                  if (close >= entry.target || high >= entry.target) {
                    activated = false
                  }
                } else if (entry.call === "SELL") {
                   if (close <= entry.target || low <= entry.target) {
                     activated = false
                   }
                }

                if (now.flashTrade) {
                    entry.flashSide = now.flashSide
                    entry.flashEntry = now.flashEntry
                    entry.flashStoploss = now.flashStoploss
                    entry.flashTarget = Number(now.flashTarget)
                    entry.latestFlashTrend = now.date

                    if (entry.flashSide === "BUY") {
                      if ((close < now.flashEntry && close > now.flashStoploss)
                          || (close >= now.flashEntry && close < now.flashTarget)) {
                          flashActivated = true
                      } else {
                          flashActivated = false
                      }
                      if (close >= entry.flashTarget || high >= entry.flashTarget) {
                        flashActivated = false
                      }
                    } else if (entry.flashSide === "SELL") {
                      if ((close > now.flashEntry && close < now.flashStoploss)
                          || (close <= now.flashEntry && close > now.flashTarget)) {
                          flashActivated = true
                      } else {
                          flashActivated = false
                      }
                      if (close <= entry.flashTarget || low <= entry.flashTarget) {
                        flashActivated = false
                      }
                    }
                }
                data[i] = now
                prev = now
                i++
            }

            data.entry = entry
            entry.activated = activated
            entry.flashActivated = flashActivated
            return data
        },
        resetChart(d) {
            this.$emit('update_tick', d)
        },
        reset(state) {
            let sub = Object.keys(state).filter(x => state[x])
            this.extensions = sub.map(x => Extensions[x])
            this.resetkey++
        },
        createBinanceSpotWebsocket() {
            binanceApi.createSpotWs()
            binanceApi.connectSpotWs(this.onBinanceMessage, this.feedTransport)
        },
        createBinanceFuturesWebsocket() {
            binanceApi.createFuturesWs()
            binanceApi.connectFuturesWs(this.onBinanceMessage, this.feedTransport)
        },
        createCoinbaseWebsocket() {
            coinbaseApi.createWs()
            coinbaseApi.connectWs(this.onCoinbaseMessage, this.feedTransport)
        },
        createKrakenWebsocket() {
            krakenApi.createWs()
            krakenApi.connectWs(this.onKrakenMessage, this.feedTransport)
        },
        createBybitWebsocket() {
            bybitApi.createWs()
            bybitApi.connectWs(this.onBybitMessage, this.feedTransport)
        },
        createOkxWebsocket() {
            okxApi.createWs()
            okxApi.connectWs(this.onOkxMessage, this.feedTransport)
        },

        setupWebSocket(tf) {
            // Use DataProviderManager for WebSocket connections
            if (DataProviderManager.getCurrentProvider()) {
                try {
                    DataProviderManager.createWebSocket(
                        this.onDataProviderMessage.bind(this),
                        this.onWebSocketError.bind(this)
                    );
                    
                    // Subscribe to current symbol
                    DataProviderManager.subscribeToSymbol(this.symbol, tf);
                } catch (error) {
                    console.error('Error setting up WebSocket with DataProviderManager:', error);
                    // Fallback to original WebSocket setup
                    this.setupOriginalWebSocket();
                }
            } else {
                this.setupOriginalWebSocket();
            }
        },
        
        setupOriginalWebSocket() {
            // Original WebSocket setup logic
            if (this.session.crypto === "Y" && this.connector === "Binance") {
                if (this.symbol.toUpperCase().includes(".P")) {
                    this.createBinanceFuturesWebsocket();
                } else {
                    this.createBinanceSpotWebsocket();
                }
            }
            if (this.session.international === "Y" && this.connector === "Int") {
                this.createIntWebsocket();
            }
        },
        
        onDataProviderMessage(data) {
            // Handle messages from DataProviderManager WebSocket
            // Process the message based on the current provider
            if (DataProviderManager.getCurrentProvider() === 'aliceblue') {
                this.processAliceBlueMessage(data);
            } else if (DataProviderManager.getCurrentProvider() === 'binance') {
                this.processBinanceMessage(data);
            }
        },
        
        onWebSocketError(error) {
            console.error('WebSocket error:', error);
        },
        
        processAliceBlueMessage(data) {
            // Process AliceBlue WebSocket messages
            if (data.type === 'trade' || data.type === 'kline') {
                // Convert AliceBlue data format to chart format
                const candle = [
                    data.timestamp || data.t,
                    parseFloat(data.open || data.o),
                    parseFloat(data.high || data.h),
                    parseFloat(data.low || data.l),
                    parseFloat(data.close || data.c),
                    parseFloat(data.volume || data.v)
                ];
                
                // Update the chart with new data
                this.updateChartWithNewData(candle);
            }
        },
        
        processBinanceMessage(data) {
            return this.applyBinanceCandle(data);
        },
        updateChartWithNewData(candle) {
            // Update chart with new candle data
            const tf = this.timeframe;
            if (this.timeframes[tf] && this.timeframes[tf].data) {
                // Update or add the new candle
                const lastCandle = this.timeframes[tf].data[this.timeframes[tf].data.length - 1];
                
                if (lastCandle && lastCandle[0] === candle[0]) {
                    // Update existing candle
                    this.timeframes[tf].data[this.timeframes[tf].data.length - 1] = candle;
                } else {
                    // Add new candle
                    this.timeframes[tf].data.push(candle);
                }
                
                this.updateAITradingContext();
                // Trigger chart update
                this.$refs.tv.updateData(this.timeframes[tf].data);
            }
        },
        updateAITradingContext() {
            // Update AI trading context with current chart data
            
            if (this.dataset && this.dataset[this.timeframe] && this.dataset[this.timeframe].chart) {
                
                const chartData = this.dataset[this.timeframe].chart.data;
                if (!chartData || chartData.length === 0) {
                    // Set basic context even without chart data
                    this.aiTradingContext = {
                        symbol: this.symbol || 'N/A',
                        exchange: this.exchange || 'N/A',
                        timeframe: this.timeframe || 'N/A',
                        currentPrice: 'N/A',
                        marketData: {
                            high24h: 'N/A',
                            low24h: 'N/A',
                            volume24h: 'N/A',
                            change: 'N/A',
                            changePercent: 'N/A',
                        },
                        technicalIndicators: {},
                        latestSignal: null,
                        allTimeframeData: {},
                        alignmentScore: 0,
                        chartConfig: {
                            symbol: this.symbol,
                            exchange: this.exchange,
                            timeframe: this.timeframe,
                        }
                    };
                    return;
                }
                
                const latestCandle = chartData[chartData.length - 1];
                const currentPrice = latestCandle && latestCandle[4] ? latestCandle[4] : 0;
                
                // Calculate proper market data from chart data
                const calculateMarketData = (data) => {
                    if (!data || !Array.isArray(data) || data.length === 0) {
                        return {
                            high24h: currentPrice,
                            low24h: currentPrice,
                            volume24h: 0,
                            change: 0,
                            changePercent: '0%',
                        };
                    }
                    
                    const period = Math.min(data.length, 24); // Last 24 periods or available data
                    const recentData = data.slice(-period);
                    const firstCandle = recentData[0];
                    const lastCandle = recentData[recentData.length - 1];
                    
                    const high = Math.max(...recentData.map(c => c[2]));
                    const low = Math.min(...recentData.map(c => c[3]));
                    const totalVolume = recentData.reduce((sum, c) => sum + (c[5] || 0), 0);
                    const change = lastCandle[4] - firstCandle[4];
                    const changePercent = firstCandle[4] !== 0 ? (change / firstCandle[4] * 100).toFixed(2) + '%' : '0%';
                    
                    return {
                        high24h: high,
                        low24h: low,
                        volume24h: totalVolume,
                        change: change.toFixed(2),
                        changePercent: changePercent,
                    };
                };
                
                // Update all timeframe data for AI context
                const allTimeframeData = {};
                Object.keys(this.timeframes).forEach(tf => {
                    if (this.dataset[tf] && this.dataset[tf].chart && this.dataset[tf].chart.data && this.dataset[tf].chart.data.length > 0) {
                        const tfData = this.dataset[tf].chart.data;
                        const tfLatest = tfData[tfData.length - 1];
                        const tfTrend = this.calculateTrend(tfData);
                        
                        // Calculate proper confidence based on trend strength and data quality
                        let confidence = 50;
                        if (tfTrend !== 'NEUTRAL') {
                            const trendStrength = Math.abs(tfLatest[4] - tfData[0][4]) / tfData[0][4] * 100;
                            confidence = Math.min(95, Math.max(25, 50 + trendStrength * 2));
                        }
                        
                        const confirmationCount = tfTrend === 'NEUTRAL' ? 0 : Math.floor(confidence / 20);
                        const confirmationPct = tfTrend === 'NEUTRAL' ? 0 : Math.floor(confidence);
                        
                        allTimeframeData[tf] = {
                            trend: tfTrend,
                            signal: tfTrend === 'BULLISH' ? 'buy' : tfTrend === 'BEARISH' ? 'sell' : 'neutral',
                            confidence: Math.round(confidence),
                            currentPrice: tfLatest[4],
                            confirmationCount: confirmationCount,
                            confirmationPct: confirmationPct,
                            hasVTSignal: false,
                            scoreBreakdown: { 
                                bullish: tfTrend === 'BULLISH' ? Math.round(confidence) : 40, 
                                bearish: tfTrend === 'BEARISH' ? Math.round(confidence) : 40 
                            },
                            confirmationIndicators: {
                                visualTrading: { latest: null, performance: { winRate: Math.round(confidence) } },
                                trendConfirmation: { ema50: tfLatest[4], validBuy: tfTrend === 'BULLISH', validSell: tfTrend === 'BEARISH' },
                                momentumConfirmation: { rsi14: 50 + (tfTrend === 'BULLISH' ? 10 : tfTrend === 'BEARISH' ? -10 : 0), validBuy: tfTrend === 'BULLISH', validSell: tfTrend === 'BEARISH' },
                                volumeConfirmation: { volumeRatio: 1.2, strength: tfTrend, validSignal: tfTrend !== 'NEUTRAL' },
                                volatilityFilter: { atr14: 0.02, atrTrend: 'NEUTRAL' },
                                marketStructure: { structure: tfTrend },
                                macd: { latest: { trend: tfTrend } },
                                momentum: { latest: { signal: tfTrend === 'BULLISH' ? 'BUY' : tfTrend === 'BEARISH' ? 'SELL' : 'NEUTRAL' } }
                            }
                        };
                    }
                });
                
                // Update the AI chat component's trading context
                const newContext = {
                    symbol: this.symbol,
                    exchange: this.exchange,
                    timeframe: this.timeframe,
                    currentPrice: currentPrice,
                    marketData: calculateMarketData(chartData),
                    technicalIndicators: {},
                    latestSignal: null,
                    allTimeframeData: allTimeframeData,
                    alignmentScore: this.calculateAlignmentScore(allTimeframeData),
                    chartConfig: {
                        symbol: this.symbol,
                        exchange: this.exchange,
                        timeframe: this.timeframe,
                    }
                };
                
                this.aiTradingContext = newContext;
            }
        },
        createIntWebsocket() {
            intApi.createWs()
            intApi.connectWs(this.onIntMessage)
        },
        createFyersWebsocket() {
            // Pass the current symbol for WebSocket subscription
            const symbolForWs = this.symbol || null;
            const symbols = symbolForWs ? [symbolForWs] : [];
            fyersApi.connectWebSocket(symbols, this.onFyersMessage);
        },
        onFyersMessage(data) {
            if (this.protectChart) return;
            if (!data) return;

            try {
                // Fyers v3 symbolUpdate response format (JSON dict):
                // { symbol, ltp, open_price, high_price, low_price, close_price,
                //   prev_close_price, vol_traded_today, last_traded_time, ... }
                // Also handle legacy/tick format for backward compatibility.
                var tick = null;

                const ltp = Number(data.ltp || data.last_traded_price || 0);
                if (ltp > 0) {
                    tick = {
                        instrument_token: data.symbol || this.symbol,
                        date: data.last_traded_time
                            ? data.last_traded_time * 1000
                            : (data.timestamp || Date.now()),
                        open: Number(data.open_price || data.open || ltp),
                        high: Number(data.high_price || data.high || ltp),
                        low: Number(data.low_price || data.low || ltp),
                        close: ltp,
                        volume: Number(data.vol_traded_today || data.volume || 0)
                    };
                }

                if (tick && tick.close !== 0 && this.dataset[this.timeframe]) {
                    this.feedReceived();
                    for (var timeframe in this.chart) {
                        this.chart[timeframe].update({
                            price: Number(tick.close),
                            volume: 0
                        });

                    }
                }
            } catch (error) {
                console.error('Error processing Fyers message:', error);
            }
        },
        createUpstoxWebsocket() {
            const symbolForWs = this.pairId || this.symbol || null;
            const symbols = symbolForWs ? [symbolForWs] : [];
            upstoxApi.connectWebSocket(symbols, this.onUpstoxMessage);
        },
        createDhanWebsocket() {
            const symbolForWs = this.pairId || this.symbol || null;
            const symbols = symbolForWs ? [symbolForWs] : [];
            dhanApi.connectWebSocket(symbols, this.onDhanMessage);
        },
        onDhanMessage(data) {
            if (this.protectChart) return;
            if (!data) return;

            try {
                const ticks = Array.isArray(data) ? data : [data];
                for (const tickData of ticks) {
                    if (!tickData) continue;
                    const ltp = Number(tickData.ltp || tickData.LTP || 0);
                    if (ltp <= 0) continue;
                    this.feedReceived();

                    if (this.dataset[this.timeframe] && this.dataset[this.timeframe].chart &&
                        this.dataset[this.timeframe].chart.data && this.dataset[this.timeframe].chart.data.length > 0) {
                        for (var timeframe in this.chart) {
                            this.chart[timeframe].update({
                                price: ltp,
                                volume: 0
                            });

                        }
                    }
                }
            } catch (error) {
                console.error('Error processing Dhan message:', error);
            }
        },
        onUpstoxMessage(data) {
            if (this.protectChart) return;
            if (!data) return;

            try {
                // Upstox tick format: { symbol, ltp, timestamp, volume, close_price, type: 'upstox_tick' }
                // May also be an array of ticks
                const ticks = Array.isArray(data) ? data : [data];

                for (const tickData of ticks) {
                    if (!tickData) continue;
                    const ltp = Number(tickData.ltp || 0);
                    if (ltp <= 0) continue;
                    this.feedReceived();


                    if (this.dataset[this.timeframe] && this.dataset[this.timeframe].chart &&
                        this.dataset[this.timeframe].chart.data && this.dataset[this.timeframe].chart.data.length > 0) {
                        // Update all timeframes (same pattern as Binance handler)
                        for (var timeframe in this.chart) {
                            this.chart[timeframe].update({
                                price: ltp,
                                volume: 0
                            });

                        }
                    }
                }
            } catch (error) {
                console.error('Error processing Upstox message:', error);
            }
        },
        onBinanceMessage(e) {
            try { this.applyBinanceCandle(JSON.parse(e.data)); } catch (error) { console.warn('Invalid Binance candle', error); }
        },
        // Shared by onCoinbaseMessage/onKrakenMessage/onBybitMessage/
        // onOkxMessage: feeds a live last-traded price into every mounted
        // timeframe's chart, the same way onBinanceMessage does above.
        applyLiveTick(price, volume) {
            if (this.protectChart || !Number.isFinite(price)) return
            if (!this.dataset[this.timeframe] || !this.dataset[this.timeframe].chart.data) return
            this.feedReceived()
            this.tick_count++
            for (var timeframe in this.chart) {
                this.chart[timeframe].update({
                    price: price,
                    volume: 0
                })
            }
        },
        onCoinbaseMessage(e) {
            try {
                var msg = JSON.parse(e.data)
                if (msg.type === "ticker" && msg.product_id === this.symbol && msg.price != null) {
                    this.applyLiveTick(Number(msg.price), msg.last_size)
                }
            } catch (err) {}
        },
        onKrakenMessage(e) {
            try {
                var msg = JSON.parse(e.data)
                if (Array.isArray(msg) && msg[2] === "ticker" && msg[3] === (this.pairId || this.symbol) && msg[1] && msg[1].c) {
                    this.applyLiveTick(Number(msg[1].c[0]), msg[1].v ? msg[1].v[0] : 0)
                }
            } catch (err) {}
        },
        onBybitMessage(e) {
            try {
                var msg = JSON.parse(e.data)
                if (msg.topic === "tickers." + this.symbol && msg.data) {
                    var d = Array.isArray(msg.data) ? msg.data[0] : msg.data
                    if (d && d.lastPrice != null) this.applyLiveTick(Number(d.lastPrice), d.volume24h)
                }
            } catch (err) {}
        },
        onOkxMessage(e) {
            try {
                if (e.data === "pong") return
                var msg = JSON.parse(e.data)
                if (msg.arg && msg.arg.channel === "tickers" && msg.arg.instId === this.symbol && Array.isArray(msg.data) && msg.data[0]) {
                    var d = msg.data[0]
                    if (d.last != null) this.applyLiveTick(Number(d.last), d.vol24h)
                }
            } catch (err) {}
        },
        onIntMessage(e) {
            var data = e.data
            if (this.protectChart) {
                return
            }
            var parsedData;
            if (data !== 'o') {
                parsedData = JSON.parse(data.substring(1));
            }

            if (parsedData) {

                for (let i = 0; i < parsedData.length; i++) {
                    let parsedItem = parsedData[i];
                    if (parsedItem) {
                        if (typeof parsedItem === 'string' || parsedItem instanceof String) {
                            if (parsedItem.indexOf("::") !== -1) {
                                const dataArray = parsedItem.split("::");

                                if (dataArray && dataArray.length > 1) {
                                    var slicedItem = dataArray[1].split("\\").join("").slice(0, -2);
                                    var record = JSON.parse(slicedItem);
                                    var tick = {
                                      instrument_token: record.pid,
                                      start_date: moment(record.timestamp * 1000).format(),
                                      end_date: moment(record.timestamp * 1000).format(),
                                      date: record.timestamp,
                                      high: Number(record.high),
                                      close: Number(record.last_numeric),
                                      volume: record.turnover_numeric
                                    };
                                    if (tick && Number(this.pairId) === Number(tick.instrument_token) && this.dataset[this.timeframe]) {
                                        var candles = this.dataset[this.timeframe].chart.data

                                        if(this.dataset[this.timeframe] && candles) {
                                            this.tick_count++
                                            var last_candle = candles[candles.length - 1]

                                            for (var timeframe in this.chart) {
                                                this.chart[timeframe].update({
                                                    price: Number(tick.close),
                                                    volume: 0
                                                })

                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    },
    created: function() {
        // Initialize DataProviderManager
        DataProviderManager.init();

        // Listen for overlay data requests (LSR/MTFTargets init)
        EventBus.$on('request-timeframes', () => {
            if (this.timeframes && Object.keys(this.timeframes).length > 0) {
                EventBus.$emit('timeframes-updated', this.timeframes);
            }
        });
        
        this.spotSymbols = BINANCE_SPOT
        this.futureSymbols = BINANCE_FUTURES
        this.intSymbols = INT
        this.indicesSymbols = ANT_INDICES
        this.nseSymbols = ANT_NSE
        this.nfoSymbols = ANT_NFO

        this.indexMap = []
        this.allSymbols = {}
        this.spotSymbols.symbols.map(function(item, key) {
            item.exchange = "Binance"
            item.type = "Crypto Spot"
        })
        this.futureSymbols.symbols.map(function(item, key) {
            item.exchange = "Binance"
            item.type = "Crypto Futures"
        })

        // Initialize session locally (no server dependency)
        // internationalCharts is controlled via Clerk private_metadata (off by default)
        const intEnabled = this.$store && this.$store.getters.internationalChartsEnabled;
        const indianEnabled = this.$store && this.$store.getters.indianMarketDataEnabled;
        this.session = {
            sessionToken: null,
            properties: {},
            crypto: "Y",
            international: intEnabled ? "Y" : "N",
            indian: indianEnabled ? "Y" : "N",
            active: "Y"
        }

        // Custom timeframes must be available before init/loadChart so the
        // automatic preload queue treats them like normal chart timeframes.
        this.loadCustomTimeframes();
        
        // Initialize the chart if user is authenticated, or this is the
        // signed-out demo experience (gated by demoGuard/tc_demo_count
        // instead of a subscription).
        if (this.$route.path === '/demo' || (this.$clerk.isAuthenticated && (this.$clerk.subscriptionValid || this.strategyWorkspaceEntry))) {
            this.init()
        }

        // Clear existing interval if any
        if (this.interval) {
            clearInterval(this.interval);
        }
        
        // Only process timeframes when document is visible
        this.interval = setInterval(() => {
            if (document.hidden) return;
            if (this.timeframe && this.dataset[this.timeframe]) {
                this.processAllTimeframes();
            }
        }, 30000);
    },
    destroyed() {
        // Cleanup intervals and caches
        clearInterval(this.interval);
        
        // Clear performance caches
        this._processCache = {};
        this._cacheKeys = [];
        
        // Destroy WebSocket connections
        binanceApi.destroyWs();
        intApi.destroyWs();
        fyersApi.destroyWs();
        upstoxApi.destroyWs();
        dhanApi.destroyWs();
        coinbaseApi.destroyWs();
        krakenApi.destroyWs();
        bybitApi.destroyWs();
        okxApi.destroyWs();

        // Clear data references
        this.dataset = {};
        this.timeframes = {};
        this.chart = {};
        this.touchTimeframeRender();
    },
    async mounted() {
        document.body.classList.add('chart-active')
        window.dc = this.dc
        window.tv = this.$refs.tv
        let q = this.win_query()

        // Sync night mode from store (don't override with false)
        if (this.$store) {
            this.night = this.$store.getters.isDarkTheme;
        }
        // Query param can still force night mode on
        if (q.nm === 'true') {
            this.night = true
        }

        if (q.ov) this.current = q.ov
        if (q.header === 'false') this.top = 0
        
        // Check for mobile callback parameters
        if (q.session_id && q.subscription_data) {
            // Legacy mobile callback — no longer used with Clerk auth
        }
        
        window.addEventListener('resize', this.onResize)
        this.onResize()
        this.$nextTick(() => {
            this.updateChartDimensions()
            this.setupChartResizeObserver()
        })

        // Check if AI chat is enabled
        let token = null;
        if (this.$clerk && this.$clerk.isAuthenticated) {
            token = await this.$clerk.getToken();
        }
        this.aiChatEnabled = await AIChatService.isEnabled(token);
        // Initialize custom timeframes from localStorage
        this.loadCustomTimeframes();
        // Add custom timeframes to the timeframes object
        this.initializeCustomTimeframes();
        // Update AI trading context periodically
        this._aiContextInterval = setInterval(() => {
            this.updateAITradingContext();
        }, 5000);
        this.updateAITradingContext();
        // Re-apply any persisted CandleScript indicators after initial chart load
        setTimeout(() => {
            this._reapplyCandleScriptIndicators();
        }, 2000);
        // Check for expired sessions periodically
        this._brokerSessionInterval = setInterval(async () => {
            await Promise.all([
                this.checkFyersConnection(),
                this.checkUpstoxConnection(),
                this.checkAliceBlueConnection(),
                this.checkDhanConnection()
            ]);
            this.checkBrokerSessions();
        }, 60000);
        
        // Initial check for expired sessions
        setTimeout(async () => {
            await Promise.all([
                this.checkFyersConnection(),
                this.checkUpstoxConnection(),
                this.checkAliceBlueConnection(),
                this.checkDhanConnection()
            ]);
            this.checkBrokerSessions();
        }, 2000);
        
        // Clerk handles authentication — no QR modal needed.
        // /demo renders this same component for signed-out visitors
        // (gated by its own demoGuard/tc_demo_count check instead), so
        // don't bounce them straight back out.
        if (!this.$clerk.isAuthenticated && this.$route.path !== '/demo') {
            this.$router.push('/');
        }
    },
    computed: {
        userInitial() {
            if (this.$clerk && this.$clerk.user) {
                const name = this.$clerk.user.fullName || this.$clerk.user.firstName || this.$clerk.user.email || '';
                return name.charAt(0).toUpperCase() || 'U';
            }
            return 'U';
        },
        chartTimezone() {
            // If user has manually selected a timezone, use that
            if (this.userSelectedTimezone !== null) {
                return this.userSelectedTimezone;
            }
            // IST (UTC+5:30) for Indian market connectors, browser timezone for others
            const indianConnectors = ['Fyers', 'Upstox', 'Indian', 'AliceBlue', 'DhanHQ', 'Dhan', 'TrueData', 'GlobalDataFeeds'];
            if (indianConnectors.includes(this.connector)) {
                return 5.5;
            }
            // For international/crypto, use 0 (UTC) — chart shows raw exchange time
            return 0;
        },
        chartWidth() {
            return Math.max(this.chartContainerWidth || this.width || 0, 180);
        },
        chartHeight() {
            return Math.max(this.chartContainerHeight || this.height || 0, 120);
        },
        cbox_width() { return Math.floor(this.width / 2 - 1) },
        cbox_height() { return Math.floor(this.height / 2 - 1) },
        symbolDisplay() {
            // Show only the symbol name, strip exchange/pair suffixes
            let s = this.query || this.symbol || ''
            
            // For Fyers symbols, show the formatted name if available
            if (this.connector === 'Fyers' && this.name) {
                let n = this.name
                // Strip exchange prefix like "NSE:" from the name
                if (n.includes(':')) {
                    n = n.split(':').pop()
                }
                // Remove suffixes like -EQ, -INDEX, -FUT
                n = n.replace(/-EQ$/, '').replace(/-INDEX$/, '').replace(/-FUT$/, '')
                return n;
            }
            
            // Remove exchange prefix like "NSE:" or "BSE:"
            if (s.includes(':')) {
                s = s.split(':')[1] || s.split(':')[0];
            }
            
            // Strip common suffixes
            return s.replace(/-EQ$/, '').replace(/-INDEX$/, '').replace(/-FUT$/, '');
        },
        isIndianMarketActive() {
            return this.connector === 'Indian' && this.category === 'IndianMarket';
        },
        isCryptoActive() {
            return this.connector === 'Binance';
        },
        scannerAvailable() {
            return ['Binance','Coinbase','Kraken','Bybit','OKX'].includes(this.connector) || /^Crypto/.test(this.category || '');
        },
        activeScannerMode() {
            return 'crypto';
        },
        activeScannerTitle() {
            return 'Scanner';
        },
        indicatorTrendVisible() {
            return [...(this.workspaceSource?.onchart||[]),...(this.workspaceSource?.offchart||[])].some(overlay=>overlay.type==='TimeframeTrend'&&overlay.settings?.display!==false);
        },
        indicatorTrendTimeframes() {return [...new Set([this.timeframe,...Object.keys(this.timeframes||{})])]},
        indicatorTrendSources() {
            return Object.fromEntries(this.indicatorTrendTimeframes.map(tf=>[tf,this.chart[tf]?.data||this.dataset[tf]]));
        },
        indicatorTrendIntervals() {return Object.fromEntries(this.indicatorTrendTimeframes.map(tf=>[tf,this.timeframeToMs(tf)]))},
        regularTimeframeItems() {
            void this.timeframeRenderTick;
            return Object.keys(this.timeframes || {})
                .filter(tf => !this.getCustomTimeframe(tf))
                .map(tf => this.makeTimeframeItem(tf, this.timeframes[tf]));
        },
        customTimeframeItems() {
            void this.timeframeRenderTick;
            return this.customTimeframes
                .filter(ctf => this.timeframes && this.timeframes[ctf.timeframe])
                .map(ctf => this.makeTimeframeItem(ctf.timeframe, this.timeframes[ctf.timeframe]));
        },
        signalTimeframeItems() {
            void this.timeframeRenderTick;
            return [
                ...this.regularTimeframeItems,
                ...this.customTimeframeItems
            ];
        },
        // Custom timeframe computed properties
        isValidCustomTimeframe() {
            return this.customTimeframeForm.name.trim().length >= 1 &&
                   this.customTimeframeForm.name.trim().length <= 2 &&
                   this.customTimeframeForm.value &&
                   this.customTimeframeForm.value > 0 &&
                   this.customTimeframeForm.value <= 999;
        },
        colors() {
            return this.night ? {
                colorBack: '#131722',
                colorHeader: 'rgb(18, 24, 38)',
                colorGrid: '#202431',
                colorScale: '#2a2e39',
                colorText: '#d1d4dc',
                cmBack: '#121826f0',
                cmCode: '#d1d4dc',
                upBarColor: 'rgb(38, 166, 154)',
                downBarColor: 'rgb(239, 83, 80)',
                selection: '#2a2e3e99',
                cmLineNumber: '#6d8a8882',
                border: '#373b4b88',
                shadow: '#00000044'
            } : {
                colorBack: '#fff',
                colorHeader: '#fff',
                colorGrid: '#f0f3f9',
                colorScale: '#e6e8eb',
                colorText: '#333',
                cmBack: '#fffffff0',
                cmCode: '#333',
                upBarColor: 'rgb(38, 166, 154)',
                downBarColor: 'rgb(239, 83, 80)',
                selection: '#eeeeef99',
                cmLineNumber: '#6d8a8882',
                border: '#88888888',
                shadow: '#0b0e1422'
            }
        },
        lst() {
            return this.night ? { 'opacity': '0.2' } : {}
        },
        current_app() {
            return this.apps.find(
                x => x.id === this.current).comp
        }
    },
    watch: {
        indicatorTrendVisible(visible) {
            if(!visible)return;
            const marker=[...(this.workspaceSource?.onchart||[]),...(this.workspaceSource?.offchart||[])].find(item=>item.type==='TimeframeTrend');
            if(['all','current'].includes(marker?.settings?.trendScope))this.indicatorTrendLoadScope=marker.settings.trendScope;
            this.ensureIndicatorTrendTimeframes();
        },
        indicatorTrendTimeframes() {if(this.indicatorTrendVisible)this.ensureIndicatorTrendTimeframes()},
        workspacePanel(panel) {
            if (['alerts','orderflow','objects','paper'].includes(panel)) {
                this.watchlistVisible=false; this.aiChatVisible=false; this.candleScriptVisible=false; this.cryptoScannerVisible=false;
            }
            this.refreshChartRender();
        },
        scannerAvailable(available) { if (!available) this.cryptoScannerVisible = false; },
        // React to the internationalCharts flag changing in the store
        // (e.g. after the subscription status API responds)
        '$store.state.internationalCharts': function (enabled) {
            if (this.session) {
                this.session.international = enabled ? "Y" : "N";
            }
        },
        '$store.state.indianMarketData': function (enabled) {
            if (this.session) {
                this.session.indian = enabled ? "Y" : "N";
            }
        },
        // Sync theme from store
        '$store.state.theme': {
            immediate: true,
            handler(theme) {
                this.night = theme === 'dark';
            }
        },
        // When night mode changes, re-render chart with new colors
        night() {
            this.$nextTick(() => {
                this.resetkey++;
            });
        },
        // When AI chat visibility changes, trigger chart resize

        // Ensure chart type is applied whenever chart data changes
        chart: {
            deep: false,
            handler(newChart) {
                if (!newChart) return;
                // Apply current chart type to any newly loaded timeframe
                for (const tf in newChart) {
                    const dc = newChart[tf];
                    if (dc && dc.data && dc.data.chart && dc.data.chart.type !== this._getChartTypeOverlay(this.currentChartType)) {
                        dc.data.chart.type = this._getChartTypeOverlay(this.currentChartType);
                    }
                }
            }
        },
    },
    beforeDestroy() {
        document.body.classList.remove('chart-active')
        window.removeEventListener('resize', this.onResize)
        if (this._resizeTimer) clearTimeout(this._resizeTimer)
        if (this._containerResizeTimer) clearTimeout(this._containerResizeTimer)
        if (this._chartResizeFrame) cancelAnimationFrame(this._chartResizeFrame)
        if (this._chartResizeObserver) this._chartResizeObserver.disconnect()
        if (this._aiContextInterval) clearInterval(this._aiContextInterval)
        if (this._brokerSessionInterval) clearInterval(this._brokerSessionInterval)
    },
    data() {
        let uri = window.location.search.substring(1);
        let params = new URLSearchParams(uri);
        var code = params.get("c")
        var d_code = ""
        if (code) {
            try {
                d_code = atob(code)
            } catch (error) {
                console.warn('[Chart] Invalid chart code in URL; chart load will be skipped', error)
            }
        }
        // Support both new "::" separator and legacy "_" separator
        var d_code_arr = d_code ? (d_code.includes('::') ? d_code.split("::") : d_code.split("_")) : []
        var symbol = d_code_arr[0] ? d_code_arr[0] : ""
        var q_symbol = symbol
        var pairId = d_code_arr[1] ? d_code_arr[1] : ""
        var connector = d_code_arr[2] ? d_code_arr[2] : ""
        var exchange = d_code_arr[3] ? d_code_arr[3] : ""
        var category = d_code_arr[4] ? d_code_arr[4] : ""
        var tf = d_code_arr[5] ? d_code_arr[5]: "5m"

        if (!code) {
            symbol = "BTCUSDT"
            q_symbol = symbol
            pairId = symbol
            connector = "Binance"
            exchange = "Binance"
            category = "Crypto Spot"
        }

        // QR authentication is now handled by the $qrAuth service

        return {
            night: this.$store ? this.$store.getters.isDarkTheme : false,
            userMenuOpen: false,
            chartFeatureView: '',
            drawingRailExpanded: false,
            showDrawingTools: localStorage.getItem('tc_show_drawings') !== 'false',
            chartData: {},
            chart: {},
            dataset: [],
            width: window.innerWidth,
            height: window.innerHeight - 46 - 32,
            chartContainerWidth: Math.max(window.innerWidth - 48, 400),
            chartContainerHeight: Math.max(window.innerHeight - 46 - 32, 240),
            // Vue component definitions must never be deep-reactive (Vue 3
            // warns and will otherwise treat each re-render's resolution
            // of these as a distinct component type, forcing overlay/
            // extension components to unmount+remount instead of patching
            // in place — including re-running their mounted() worker setup).
            extensions: markRaw(Object.values(Extensions)),
            ext_names: Object.keys(Extensions),
            overlays: markRaw(Object.values(Overlays)),
            overlay_names: ['Default', ...Object.keys(Overlays)],
            top: 48,
            resetkey: 0,
            protectChart: true,
            spotSymbols: {},
            futureSymbols: {},
            intSymbols: [],
            nseSymbols: [],
            nfoSymbols: [],
            indicesSymbols: [],
            allSymbols: {},
            selectedSymbolInfo: null,
            symbol: symbol,
            pairId: pairId,
            query: symbol,
            category: category,
            connector: connector,
            selected: symbol,
            placement: "top",
            fixed: true,
            exchange: exchange,
            timeframe: tf,
            // Timeframes will be set properly in loadChart based on connector
            indicatorTrendLoadScope: (()=>{try{return localStorage.getItem('tc_timeframe_trend_scope')==='current'?'current':'all'}catch(_){return 'all'}})(),
            timeframes: {},
            loaded: {},
            timeframeRenderTick: 0,
            latest_timeframe: "",
            latest_trend: "",
            tf: tf,
            showSecondsMenu: false, // For seconds dropdown menu visibility
            selectedSeconds: "", // For seconds dropdown
            // Custom timeframe properties
            showCustomTimeframeMenu: false,
            showCustomTimeframePopover: false,
            customTimeframes: [],
            customTimeframeForm: {
                name: '',
                type: 'minute',
                value: null
            },
            showQRModal: false,
            xsett: {
                'grid-resize': { min_height: 80 },
                'chart-link': { rules: {} }
            },
            chart_config: {
                DEFAULT_LEN: Utils.is_mobile ? 32 : 65
            },
            tv_id: 'chart-main',
            rules: DEFAULT,
            code: true,
            tree: {
                dir: 'horizontal', // Left | Right
                // Other split
                first: {
                    dir: 'vertical',
                    first: 0,    // these numbers represents the views slot ID
                    second: 2
                },
                second: 1
            },
            // AI Chat properties
            timeframeMenuPosition: {},
            analysisVisible: false,
            aiChatEnabled: false,
            aiChatVisible: false,
            aiChatFloatMode: false,
            aiChatPanelWidth: 380,
            // Watchlist properties
            watchlistVisible: false,
            watchlistFloatMode: false,
            watchlistPanelWidth: 360,
            cryptoScannerVisible: false,
            candleScriptFloatMode: false,
            candleScriptPanelWidth: 680,
            candleScriptVisible: false,
            // Splitter drag state
            splitterDragging: false,
            startX: 0,
            startWidth: 0,
            // Broker reconnect state
            brokerReconnectNeeded: false,
            showBrokerReconnectModal: false,
            expiredBroker: null, // 'fyers', 'upstox', 'aliceblue', 'dhan'
            fyersConnected: false,
            upstoxConnected: false,
            aliceblueConnected: false,
            dhanConnected: false,
            // Chart type properties - persisted in localStorage
            currentChartType: localStorage.getItem('tc_chart_type') || 'candles',
            aiTradingContext: {
                symbol: 'N/A',
                exchange: 'N/A',
                timeframe: 'N/A',
                currentPrice: 'N/A',
                marketData: {},
                technicalIndicators: {},
                latestSignal: null,
                allTimeframeData: {},
                alignmentScore: 0,
                chartConfig: null,
            },
            // Fyers search loading state
            fyersSearchLoading: false,
            // Incremented on every loadChart / loadAllTimeframe call so that
            // stale async responses from a previous symbol can be discarded.
            _loadGeneration: 0,
            userSelectedTimezone: null
        };
    },
};
</script>

<style>
html,
body {
    margin: 0;
    padding: 0;
}

body.chart-active {
    overflow: hidden;
}

/* Root chart page container */
.chart-page {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    background: #ffffff;
}
.chart-page-dark {
    background: rgb(18, 24, 38);
}

#tc-header {
    position: absolute;
    height: 45px;
    left: 0;
    right: 0;
    top: 0;
    color: #131722;
    border-bottom: 1px solid #e0e3eb;
    background: #ffffff;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 8px;
    z-index: 100;
    overflow: visible;
}
#tc-header.dark {
    background: rgba(18, 24, 38, 1);
    border-bottom-color: rgba(55, 59, 75, 0.6);
    color: #d1d4dc;
}

#tc-header img{
    width: 36px;
    height: 36px;
    margin: 8px;
}

#tc-header label{
    vertical-align: top;
    line-height: 2.3em;
    margin-left: 2px;
    font-weight: 500;
}
#tc-header h1 {
    color: #9b9ca0;
    margin: 0px 0 0 3px;
}
#tc-header p {
    position: absolute;
    width: 100%;
    top: 1px;
    text-align: center;
    font-family: -apple-system,BlinkMacSystemFont,
    Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,
    Fira Sans,Droid Sans,Helvetica Neue,
    sans-serif;
    font-weight: 200;
}
#tc-header-span {
    top: 0px;
    left: 50px;
}

/* AI Chat Button */
.ai-chat-btn {
    color: #22c55e;
    border-color: #22c55e;
}

.ai-chat-btn:hover {
    background: rgba(34, 197, 94, 0.1);
}

/* Broker Reconnect Button */
.reconnect-broker-btn {
    color: #f59e0b;
    border-color: #f59e0b;
    animation: reconnect-pulse 2s infinite;
}

.reconnect-broker-btn:hover {
    background: rgba(245, 158, 11, 0.15);
    animation: none;
}

.right-tools-bar.dark .reconnect-broker-btn {
    color: #fbbf24;
    border-color: #fbbf24;
}

.right-tools-bar.dark .reconnect-broker-btn:hover {
    background: rgba(251, 191, 36, 0.15);
    animation: none;
}

@keyframes reconnect-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
}

/* AI Chat Panel - Right side panel */
.chart-layout-container {
    display: flex;
    flex-direction: row;
    height: 100%;
    width: 100%;
    position: relative;
    overflow: hidden;
}

.chart-main-column {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    height: 100%;
    overflow: hidden;
    position: relative;
}

.right-tools-bar {
    width: 48px;
    flex: 0 0 48px;
    height: 100%;
    min-height: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    padding: 8px 6px;
    border-left: 1px solid #e0e3eb;
    background: #ffffff;
    box-shadow: -1px 0 0 rgba(0, 0, 0, 0.02);
    z-index: 12;
    overflow: visible;
}

.right-tools-bar.dark {
    background: #101523;
    border-left-color: rgba(55, 59, 75, 0.8);
    box-shadow: -1px 0 0 rgba(0, 0, 0, 0.25);
}

.right-tools-main,
.right-tools-bottom {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    width: 100%;
}

.right-tools-main {
    flex: 0 1 auto;
    min-height: 0;
}

.right-tools-bottom {
    margin-top: auto;
    padding-top: 6px;
    border-top: 1px solid #e5e7eb;
    flex: 0 0 auto;
}

.right-tools-bar.dark .right-tools-bottom {
    border-top-color: rgba(55, 59, 75, 0.75);
}

.right-tool-btn {
    position: relative;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #111111;
    cursor: pointer;
    padding: 0;
    transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.right-tool-btn:hover,
.right-tool-btn.active {
    background: rgba(249, 115, 22, 0.12);
    color: #f97316;
}

.right-tools-bar.dark .right-tool-btn {
    color: #eef1f6;
}

.right-tools-bar.dark .right-tool-btn:hover,
.right-tools-bar.dark .right-tool-btn.active {
    background: rgba(249, 115, 22, 0.18);
    color: #f97316;
}

.right-tool-btn span {
    position: absolute;
    right: 44px;
    top: 50%;
    transform: translateY(-50%);
    max-width: 180px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    padding: 6px 9px;
    border-radius: 6px;
    background: #131722;
    color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
    transition: opacity 0.12s ease, transform 0.12s ease;
}

.right-tool-btn:hover span {
    opacity: 1;
    transform: translate(-2px, -50%);
}

.chart-sidebar-column {
    width: 350px;
    min-width: 300px;
    max-width: 400px;
    height: 100%;
    overflow: hidden;
    border-left: 1px solid #e0e3eb;
    background: #ffffff;
}

.chart-page-dark .chart-sidebar-column {
    border-left-color: #374151;
    background: #1e2433;
}

.crypto-scanner-panel {
    width: min(760px, 48vw);
    min-width: 520px;
    flex: 0 0 auto;
    height: 100%;
    display: flex;
    overflow: visible;
    position: relative;
    z-index: 10;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.06);
}

.crypto-scanner-panel.dark {
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
}

.crypto-scanner-btn {
    color: #111111;
}

.crypto-scanner-btn:hover,
.crypto-scanner-btn.active {
    background: rgba(249, 115, 22, 0.12);
    color: #f97316;
}

.right-tools-bar.dark .crypto-scanner-btn {
    color: #eef1f6;
}

.right-tools-bar.dark .crypto-scanner-btn:hover,
.right-tools-bar.dark .crypto-scanner-btn.active {
    background: rgba(249, 115, 22, 0.18);
    color: #f97316;
}

@media (max-width: 900px) {
    .crypto-scanner-panel {
        position: fixed;
        top: 46px;
        right: 48px;
        width: calc(100vw - 48px);
        min-width: 0;
        height: calc(100vh - 78px);
        z-index: 1000;
    }
}

.ai-chat-panel {
    border-left: 1px solid #e0e3eb;
    background: #ffffff;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    z-index: 10;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.06);
}

/* Inline mode - sidebar */
.ai-chat-panel.inline-mode {
    min-width: 300px;
    max-width: 600px;
    flex-shrink: 0;
}

/* Float mode - floating over chart */
.ai-chat-panel.float-mode {
    position: fixed;
    top: 45px;
    right: 0px;
    width: 380px;
    height: calc(100vh - 80px);
    max-height: 800px;
    border-radius: 8px;
    border: 1px solid #e0e3eb;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    z-index: 1000;
}

.ai-chat-panel.dark {
    background: rgb(18, 24, 38);
    border-left-color: rgba(55, 59, 75, 0.6);
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
}

.ai-chat-panel.float-mode.dark {
    border-color: rgba(55, 59, 75, 0.6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

/* Splitter for inline mode */
.ai-chat-splitter {
    position: absolute;
    left: -4px;
    top: 0;
    width: 8px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    z-index: 11;
}

.ai-chat-splitter:hover .splitter-handle,
.ai-chat-splitter.dragging .splitter-handle {
    background: #2962ff;
}

.splitter-handle {
    position: absolute;
    left: 3px;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 40px;
    background: #d1d5db;
    border-radius: 1px;
    transition: background 0.2s ease;
}

.ai-chat-panel.dark .ai-chat-splitter:hover .splitter-handle,
.ai-chat-panel.dark .ai-chat-splitter.dragging .splitter-handle {
    background: #5c9aff;
}

.ai-chat-panel.dark .splitter-handle {
    background: #4b5563;
}


/* Watchlist Panel */
.watchlist-panel {
    border-left: 1px solid #e0e3eb;
    background: #ffffff;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    z-index: 10;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.06);
}

/* Inline mode - sidebar */
.watchlist-panel.inline-mode {
    min-width: 320px;
    max-width: 460px;
    flex-shrink: 0;
}

/* Float mode - floating over chart */
.watchlist-panel.float-mode {
    position: fixed;
    top: 45px;
    right: 0px;
    width: 380px;
    height: calc(100vh - 80px);
    max-height: 800px;
    border-radius: 8px;
    border: 1px solid #e0e3eb;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    z-index: 1000;
}

.watchlist-panel.dark {
    background: rgb(18, 24, 38);
    border-left-color: rgba(55, 59, 75, 0.6);
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
}

.watchlist-panel.float-mode.dark {
    border-color: rgba(55, 59, 75, 0.6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.watchlist-with-signals {
    min-width: 320px;
}

.watchlist-stack-main {
    flex: 0 0 52%;
    min-height: 260px;
    overflow: hidden;
    border-bottom: 1px solid #e0e3eb;
}

.watchlist-panel.dark .watchlist-stack-main {
    border-bottom-color: rgba(55, 59, 75, 0.8);
}

.watchlist-signals-panel {
    flex: 1 1 48%;
    min-height: 210px;
    overflow: hidden;
}

@media (max-width: 900px) {
    .watchlist-panel {
        position: fixed;
        top: 46px;
        right: 48px;
        width: calc(100vw - 48px) !important;
        min-width: 0;
        height: calc(100vh - 78px);
        z-index: 1000;
    }

    .watchlist-stack-main {
        flex-basis: 48%;
        min-height: 220px;
    }
}

/* Watchlist button */
.watchlist-btn {
    color: #111111;
}
.watchlist-btn:hover,
.watchlist-btn.active {
    background: rgba(249, 115, 22, 0.12);
    color: #f97316;
}
.right-tools-bar.dark .watchlist-btn {
    color: #eef1f6;
}
.right-tools-bar.dark .watchlist-btn:hover,
.right-tools-bar.dark .watchlist-btn.active {
    background: rgba(249, 115, 22, 0.18);
    color: #f97316;
}


/* CandleScript Builder Panel */
.candlescript-panel {
    border-left: 1px solid #e0e3eb;
    background: #ffffff;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.06);
}

/* Inline mode - sidebar */
.candlescript-panel.inline-mode {
    min-width: 500px;
    max-width: 800px;
    flex-shrink: 0;
}

/* Float mode - floating over chart */
.candlescript-panel.float-mode {
    position: fixed;
    top: 45px;
    right: 0px;
    width: 680px;
    height: calc(100vh - 80px);
    max-height: 800px;
    border-radius: 8px;
    border: 1px solid #e0e3eb;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    z-index: 1000;
}

.candlescript-panel.dark {
    background: rgb(18, 24, 38);
    border-left-color: rgba(55, 59, 75, 0.6);
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
}

.candlescript-panel.float-mode.dark {
    border-color: rgba(55, 59, 75, 0.6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

/* Splitter for inline mode */
.candlescript-splitter {
    position: absolute;
    left: -4px;
    top: 0;
    width: 8px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    z-index: 11;
}

.candlescript-splitter:hover .splitter-handle,
.candlescript-splitter.dragging .splitter-handle {
    background: #2962ff;
}

.candlescript-panel.dark .candlescript-splitter:hover .splitter-handle,
.candlescript-panel.dark .candlescript-splitter.dragging .splitter-handle {
    background: #5c9aff;
}

.candlescript-panel.dark .splitter-handle {
    background: #4b5563;
}

/* CandleScript Button */
.candlescript-btn {
    color: #111111;
}
.candlescript-btn:hover {
    background: rgba(249, 115, 22, 0.12);
    color: #f97316;
}
.candlescript-btn.active {
    background: rgba(249, 115, 22, 0.16);
    color: #f97316;
}

.right-tools-bar.dark .candlescript-btn {
    color: #eef1f6;
}

.right-tools-bar.dark .candlescript-btn:hover,
.right-tools-bar.dark .candlescript-btn.active {
    background: rgba(249, 115, 22, 0.18);
    color: #f97316;
}

/* Slide transition for AI chat panel */
.slide-panel-enter-active,
.slide-panel-leave-active {
    transition: transform 0.25s ease, opacity 0.25s ease;
}
.slide-panel-enter,
.slide-panel-leave-to {
    transform: translateX(100%);
    opacity: 0;
}

.scrollable {
    -webkit-overflow-scrolling: touch;
    overflow: visible;
    width: 100%;
}
.searchbar {
    position: relative;
    height: 34px;
    min-width: 190px;
    max-width: 280px;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0;
    padding: 0 12px;
    pointer-events: all;
    cursor: pointer;
    z-index: 999;
    border-right: 1px solid rgba(130, 130, 130, 0.2);
    border-radius: 8px;
    box-sizing: border-box;
    transition: background-color 0.15s ease;
    margin-left: 6px;
}
.searchbar-text {
    font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #131722;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    text-align: left;
}
.searchbar:hover {
    background-color: rgba(0, 0, 0, 0.04);
}
.dark .searchbar:hover {
    background-color: rgba(255, 255, 255, 0.06);
}
.searchbar-text {
    font-size: 13px;
    font-weight: 700;
    color: #131722;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.searchbar-text.dark {
    color: rgba(255, 255, 255, 0.9);
}

.multi_timeframe_bar {
    position: relative;
    height: 100%;
    display: inline-flex;
    align-items: center;
    background-color: transparent;
    pointer-events: all;
    z-index: 999;
    gap: 0;
    padding: 0 2px;
    margin-left: 8px;
    overflow: visible;
    white-space: nowrap;
}

.tf-bar-workspace-controls {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 6px;
}
.tf-bar-workspace-controls .workspace-command,
.tf-bar-workspace-controls .workspace-button,
.tf-bar-workspace-controls .workspace-icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 30px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    color: inherit;
}
.tf-bar-workspace-controls .workspace-command {
    min-width: 0 !important;
    padding: 0 8px !important;
    background: transparent !important;
    border: 1px solid var(--ws-border, #e0e3eb) !important;
    color: #787b86 !important;
}
.tf-bar-workspace-controls .workspace-command kbd {
    margin-left: 2px;
}
.tf-bar-workspace-controls .workspace-button {
    padding: 0 8px;
    background: transparent;
    border: 1px solid var(--ws-border, #e0e3eb);
}
.tf-bar-workspace-controls .workspace-icon-button {
    width: 30px;
    padding: 0;
    border: none;
    background: transparent;
}
.tf-bar-workspace-controls button:hover {
    background: var(--ws-hover, #f0f3fa);
}
.chart-page-dark .tf-bar-workspace-controls .workspace-command,
.chart-page-dark .tf-bar-workspace-controls .workspace-button {
    border-color: #2a2e39;
    color: #9598a1;
}
@media (max-width: 1100px) {
    .tf-bar-workspace-controls .workspace-command span,
    .tf-bar-workspace-controls .workspace-command kbd {
        display: none;
    }
    .tf-bar-workspace-controls .workspace-button span:not(.workspace-save-dot) {
        display: none;
    }
}

/* ══════════════════════════════════════════
   Flat minimal timeframe buttons
   ══════════════════════════════════════════ */
.tf-slot {
    display: inline-flex;
    align-items: center;
    height: 32px;
}

.tf-indicator-glyph { margin-left:3px; font-size:9px; font-weight:700; }
.tf-btn.tf-indicator-colored { border-radius:4px; }
.tf-btn {
    font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    min-width: 18px;
    font-size: 12px;
    font-weight: 500;
    padding: 0 5px;
    cursor: pointer;
    background: transparent;
    color: #787b86;
    border: none;
    outline: none;
    margin: 0;
    border-radius: 0;
    transition: color 0.15s ease;
    white-space: nowrap;
    line-height: 1;
    box-sizing: border-box;
    border-bottom: 2px solid transparent;
}

.tf-btn:hover {
    color: #131722;
}

/* ── Active / selected timeframe — bottom accent bar ── */
.tf-btn.tf-active {
    color: #131722;
    font-weight: 600;
    border-bottom: 2px solid #2962ff;
}

/* ── Hidden timeframes ── */
.tf-btn.tf-hidden {
  display: none;
}

/* ── Timeframe divider ── */
.tf-divider {
  width: 1px;
  height: 24px;
  background-color: #d1d5db;
  margin: 0 8px;
  flex-shrink: 0;
}

.chart-page-dark .tf-divider {
  background-color: #4b5563;
}

/* ── Signal colors ── */
.tf-btn.tc-buy {
    color: #2ECC71;
    font-weight: 600;
}

.tf-btn.tc-sell {
    color: #E74C3C;
    font-weight: 600;
}

.tf-btn.tc-buy-completed {
    color: #138D75;
    font-weight: 600;
}

.tf-btn.tc-sell-completed {
    color: #EC7063;
    font-weight: 600;
}

/* ── Activated signals — subtle rounded outline ── */
.tf-btn.tc-activated-buy {
    color: #2ECC71;
    font-weight: 600;
    border: 1px solid rgba(46, 204, 113, 0.45);
    border-radius: 3px;
    margin: 0 1px;
    padding: 0 4px;
    border-bottom-width: 1px;
}

.tf-btn.tc-activated-sell {
    color: #E74C3C;
    font-weight: 600;
    border: 1px solid rgba(231, 76, 60, 0.45);
    border-radius: 3px;
    margin: 0 1px;
    padding: 0 4px;
    border-bottom-width: 1px;
}

/* Activated + active */
.tf-btn.tc-activated-buy.tf-active {
    border-bottom: 2px solid #2ECC71;
    border-radius: 4px 4px 0 0;
}

.tf-btn.tc-activated-sell.tf-active {
    border-bottom: 2px solid #E74C3C;
    border-radius: 4px 4px 0 0;
}

/* ── Shadow classes — no visible border-top, just a subtle left accent ── */
.tf-btn.tc-shadow-buy { }
.tf-btn.tc-shadow-sell { }
.tf-btn.tc-shadow-buy-completed { }
.tf-btn.tc-shadow-sell-completed { }

/* ── Latest signal — gentle pulse ── */
.tf-btn.tc-latest-signal {
    animation: tf-pulse-latest 2.5s ease-in-out infinite;
}

@keyframes tf-pulse-latest {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
}

.no_signal {
    color: #787b86;
}

/* ── Seconds dropdown toggle ── */
.tf-dropdown-toggle {
    min-width: 16px !important;
    padding: 0 3px !important;
    font-size: 10px;
    color: #787b86;
}

.chart-type-selector-wrapper {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 8px;
}

.chart-top-actions {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-left: 4px;
}

.tf-dropdown-toggle:hover {
    color: #131722;
}

.tf-dropdown-toggle.active {
    color: #2962ff;
}

.tc-logo-wrapper {
    position: relative;
    width: 45px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
}

.tc-logo-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    text-decoration: none;
    transition: opacity 0.2s;
}

.tc-logo-link:hover {
    opacity: 0.8;
}

.tc-logo-img {
    max-width: 32px;
    max-height: 32px;
    object-fit: contain;
}

.tc-profile-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.tc-profile-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;
    padding: 0;
    background: #6366f1;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    overflow: hidden;
}

.tc-profile-btn:hover {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

.tc-profile-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.tc-profile-fallback {
    color: white;
    font-size: 12px;
    font-weight: 600;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.tc-avatar-btn {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;
    padding: 0;
    background: #6366f1;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    overflow: hidden;
}

.tc-avatar-btn:hover {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
}

.tc-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}

.tc-avatar-fallback {
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
}

.tc-menu-overlay {
    position: fixed;
    inset: 0;
    z-index: 9998;
}

.tc-user-menu {
    position: absolute;
    top: 44px;
    right: 0;
    width: 280px;
    background: #ffffff;
    border: 1px solid #e0e3eb;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    transition: all 0.2s ease;
    z-index: 9999;
}

.tc-user-menu.dark {
    background: #1e222d;
    border-color: #2a2e39;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.menu-fade-enter-active, .menu-fade-leave-active {
    transition: all 0.2s ease;
}
.menu-fade-enter, .menu-fade-leave-to {
    opacity: 0;
    visibility: hidden;
    transform: translateY(-8px);
}
.menu-fade-enter-to, .menu-fade-leave {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
}

.tc-menu-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
}

.tc-menu-logo {
    width: 24px;
    height: 24px;
    border-radius: 4px;
}

.tc-menu-brand {
    font-size: 15px;
    font-weight: 700;
    color: #1f2937;
}

.tc-user-menu.dark .tc-menu-brand {
    color: #f1f5f9;
}

.tc-menu-user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 16px;
    cursor: pointer;
    transition: background 0.15s ease;
    border-radius: 4px;
    margin: 0 4px;
}

.tc-menu-user:hover {
    background: #f3f4f6;
}

.tc-user-menu.dark .tc-menu-user:hover {
    background: #2a2e39;
}

.tc-menu-user-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
}

.tc-menu-user-name {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.tc-user-menu.dark .tc-menu-user-name {
    color: #e2e8f0;
}

.tc-menu-chevron {
    color: #9ca3af;
}

.tc-menu-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 6px 0;
}

.tc-user-menu.dark .tc-menu-divider {
    background: #2a2e39;
}

.tc-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    font-size: 14px;
    color: #374151;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s ease;
    border: none;
    background: none;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    font-family: inherit;
    min-width: 0;
}

.tc-menu-item span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.tc-menu-item:hover {
    background: #f3f4f6;
}

.tc-user-menu.dark .tc-menu-item {
    color: #d1d4dc;
}

.tc-user-menu.dark .tc-menu-item:hover {
    background: #2a2e39;
}

.tc-menu-item svg {
    color: #6b7280;
    flex-shrink: 0;
}

.tc-user-menu.dark .tc-menu-item svg {
    color: #9ca3af;
}

.tc-menu-external {
    margin-left: auto;
    color: #9ca3af;
    flex-shrink: 0;
}

.tc-menu-signout {
    color: #ef4444;
}

.tc-menu-signout:hover {
    background: rgba(239, 68, 68, 0.08);
}

.tc-user-menu.dark .tc-menu-signout {
    color: #f87171;
}

.tc-user-menu.dark .tc-menu-signout:hover {
    background: rgba(248, 113, 113, 0.1);
}

.tc-menu-toggle {
    cursor: default;
}

.tc-menu-toggle:hover {
    background: transparent;
}

.tc-user-menu.dark .tc-menu-toggle:hover {
    background: transparent;
}

/* Toggle Switch */
.tc-toggle {
    position: relative;
    display: inline-block;
    width: 36px;
    min-width: 36px;
    height: 20px;
    margin-left: auto;
    cursor: pointer;
    flex-shrink: 0;
}

.tc-toggle input {
    opacity: 0;
    width: 0;
    height: 0;
}

.tc-toggle-slider {
    position: absolute;
    inset: 0;
    background: #d1d5db;
    border-radius: 20px;
    transition: background 0.2s ease;
}

.tc-toggle-slider::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    left: 2px;
    bottom: 2px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s ease;
}

.tc-toggle input:checked + .tc-toggle-slider {
    background: #6366f1;
}

.tc-toggle input:checked + .tc-toggle-slider::before {
    transform: translateX(16px);
}

/* Menu fade transition */
.menu-fade-enter-active,
.menu-fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-fade-enter,
.menu-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

.night-mode {
    /* Trailing flex child of #tc-header, pushed to the far right by its own
       auto margin instead of floating as a fixed overlay — keeps it from
       covering whatever now sits inline before it (the merged action bar). */
    margin-left: auto;
    flex: 0 0 auto;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 8px;
    background: transparent;
    z-index: 1000;
}
.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}
.header-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #787b86;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
    padding: 0;
    font-size: 14px;
}
.header-btn:hover {
    background-color: rgba(251, 146, 60, 0.1);
    color: #fb923c;
}
.header-btn:active {
    background-color: rgba(0, 0, 0, 0.1);
}
.dark .header-btn {
    color: #b2b5be;
}
.dark .header-btn:hover {
    background-color: rgba(251, 146, 60, 0.15);
    color: #fb923c;
}
.indicator-btn:hover {
    color: #fb923c;
}
.dark .indicator-btn:hover {
    color: #fb923c;
}
.theme-btn:hover {
    color: #f59e0b;
}
.dark .theme-btn:hover {
    color: #fbbf24;
}
#app-conainer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
}

.vue-input {
  font-family: -apple-system,BlinkMacSystemFont,'Trebuchet MS',Roboto,Ubuntu,sans-serif;
  font-size: 14px !important;
  text-transform: uppercase;
  padding: 9px 4px 9px 40px !important;
  font-weight: 700;
  height: 44px !important;
  line-height: 44px;
  border: none !important;
  border-bottom: 2px solid #e0e3eb !important;
  flex-shrink: 0 !important;
  color: #131722;
  background: transparent url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' class='bi bi-search' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z'%3E%3C/path%3E%3C/svg%3E") no-repeat 13px center !important;
  transition: border-color 0.15s ease;
}
.vue-input:focus {
  border-bottom-color: #2962ff !important;
}

.vue-panel-item {
    border-bottom: 1px solid #f0f3fa;
    transition: background-color 0.1s ease;
}

.vue-panel-item:hover {
    background: #f0f3fa !important;
}

/* Dark theme search panel */
.chart-page-dark .vue-input {
    background: #1e2436 !important;
    border-bottom-color: rgba(55, 59, 75, 0.6) !important;
    color: #d1d4dc !important;
}
.chart-page-dark .vue-panel-item {
    border-bottom-color: rgba(55, 59, 75, 0.3);
}
.chart-page-dark .vue-panel-item:hover {
    background: rgba(255, 255, 255, 0.05) !important;
}

.tc-search-item {
  font-family: -apple-system,BlinkMacSystemFont,'Trebuchet MS',Roboto,Ubuntu,sans-serif;
  font-feature-settings: "tnum" on,"lnum" on;
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 20px;
  cursor: pointer;
  transition: background-color 0.1s ease;
  gap: 8px;
}
.tc-search-item:hover {
  background: #f0f3fa;
}
.search-item-value{
  font-size: 14px;
  font-weight: 600;
  color: #131722;
  text-align: left !important;
  width: 25%;
  min-width: 120px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* Dark theme search items */
.chart-page-dark .search-item-value {
  color: #d1d4dc;
}
.chart-page-dark .tc-search-item:hover {
  background: rgba(255, 255, 255, 0.06);
}
.search-item-desc{
  font-size: 12px;
  color: #787b86;
  text-align: left !important;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.search-item-time{
  font-size: 11px;
  color: #9ca3af;
  text-align: right;
  flex-shrink: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

/* ══════════════════════════════════════════
   CHART PAGE DARK MODE
   ══════════════════════════════════════════ */
.chart-page-dark .vue-input {
  color: #d1d4dc;
  border-bottom-color: rgba(55, 59, 75, 0.6) !important;
}
.chart-page-dark .vue-panel-item {
  border-bottom-color: rgba(55, 59, 75, 0.3);
}
.chart-page-dark .vue-panel-item:hover {
  background: rgba(255, 255, 255, 0.06) !important;
}
.chart-page-dark .search-item-value {
  color: #d1d4dc;
}
.chart-page-dark .search-item-desc {
  color: #787b86;
}
.chart-page-dark .search-item-time {
  color: #6b7280;
}
/* ── Dark mode timeframe overrides ── */
.chart-page-dark .tf-btn {
  color: #9ca3af;
  background: transparent;
}
.chart-page-dark .tf-btn:hover {
  color: #d1d4dc;
  background: rgba(255, 255, 255, 0.05);
}
.chart-page-dark .tf-btn.tf-active {
  color: #d1d4dc;
  font-weight: 600;
  border-bottom: 2px solid #5b9cf6;
  background: rgba(91, 156, 246, 0.1);
}
.chart-page-dark .tf-btn.tc-buy {
  color: #22c55e;
}
.chart-page-dark .tf-btn.tc-sell {
  color: #ef4444;
}
.chart-page-dark .tf-btn.tc-buy-completed {
  color: #26a69a;
}
.chart-page-dark .tf-btn.tc-sell-completed {
  color: #ef5350;
}
.chart-page-dark .tf-btn.tc-activated-buy {
  color: #22c55e;
  border-color: rgba(34, 197, 94, 0.35);
  background: rgba(34, 197, 94, 0.1);
}
.chart-page-dark .tf-btn.tc-activated-sell {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.35);
  background: rgba(239, 68, 68, 0.1);
}
.chart-page-dark .tf-btn.tc-activated-buy.tf-active {
  border-bottom-color: #22c55e;
}
.chart-page-dark .tf-btn.tc-activated-sell.tf-active {
  border-bottom-color: #ef4444;
}
/* ── Seconds dropdown ── */
.seconds-dropdown-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.seconds-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background: #ffffff;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 9999;
    min-width: 60px;
}

.dropdown-item {
    padding: 6px 12px;
    cursor: pointer;
    font-size: 12px;
    color: #131722;
    transition: background-color 0.15s ease;
}

.dropdown-item:hover {
    background-color: #f0f3fa;
}

.dropdown-item.dropdown-active {
    background-color: #2962ff;
    color: #ffffff;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
    transition: opacity 0.2s ease;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
    opacity: 0;
}

.tf-dropdown-toggle {
    min-width: 16px !important;
    padding: 0 3px !important;
    font-size: 10px;
    color: #787b86;
}

.tf-dropdown-toggle:hover {
    color: #131722;
}

.tf-dropdown-toggle.active {
    color: #2962ff;
}

/* Dark theme dropdown toggle */
.chart-page-dark .tf-dropdown-toggle {
    color: #9ca3af;
}
.chart-page-dark .tf-dropdown-toggle:hover {
    color: #d1d4dc;
}
.chart-page-dark .tf-dropdown-toggle.active {
    color: #5b9cf6;
}

/* Dark theme dropdown */
.chart-page-dark .seconds-dropdown-menu {
    background: #1a2035;
    border-color: rgba(55, 59, 75, 0.6);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.chart-page-dark .dropdown-item {
    color: #d1d4dc;
}

.chart-page-dark .dropdown-item:hover {
    background-color: rgba(255, 255, 255, 0.05);
}

.chart-page-dark .dropdown-item.dropdown-active {
    background-color: #5b9cf6;
    color: #ffffff;
}

/* Exchange badges in search results */
.exchange-badge {
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 700;
  margin-left: 4px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  vertical-align: middle;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  flex-shrink: 0;
}
.fyers-badge {
  background: rgba(255, 107, 53, 0.12);
  color: #ff6b35;
  border: 1px solid rgba(255, 107, 53, 0.25);
}
.binance-badge {
  background: rgba(240, 185, 11, 0.12);
  color: #d4a20a;
  border: 1px solid rgba(240, 185, 11, 0.25);
}
.binance-futures-badge {
  background: rgba(240, 185, 11, 0.12);
  color: #d4a20a;
  border: 1px solid rgba(240, 185, 11, 0.25);
}

.chart-page-dark .fyers-badge {
    background: rgba(255, 140, 66, 0.15);
    color: #ff8c42;
    border-color: rgba(255, 140, 66, 0.3);
}
.chart-page-dark .binance-badge {
    background: rgba(240, 185, 11, 0.15);
    color: #f0b90b;
    border-color: rgba(240, 185, 11, 0.3);
}
.chart-page-dark .binance-futures-badge {
    background: rgba(240, 185, 11, 0.15);
    color: #f0b90b;
    border-color: rgba(240, 185, 11, 0.3);
}

.seconds-dropdown-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    height: 100%;
    margin-left: 2px;
}

/* Dropdown fade transition */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-fade-enter,
.dropdown-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

.seconds-dropdown-menu {
    position: absolute;
    top: calc(100% + 2px);
    right: 0;
    background: #fff;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    z-index: 9999;
    min-width: 48px;
    overflow: hidden;
    padding: 4px 0;
}

.dropdown-item {
    padding: 5px 12px;
    font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #787b86;
    cursor: pointer;
    transition: color 0.12s ease, background-color 0.12s ease;
    white-space: nowrap;
    text-align: left;
}

.dropdown-item:hover {
    background: #f0f3fa;
    color: #131722;
}

.dropdown-item.dropdown-active {
    color: #2962ff;
    font-weight: 600;
}

/* Dark mode dropdown */
.chart-page-dark .seconds-dropdown-menu {
    background: rgb(30, 38, 54);
    border-color: rgba(55, 59, 75, 0.6);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.chart-page-dark .dropdown-item {
    color: #636978;
}

.chart-page-dark .dropdown-item:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #d1d4dc;
}

.chart-page-dark .dropdown-item.dropdown-active {
    color: #5b9cf6;
}

/* Fyers search loading spinner */
.fyers-search-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  gap: 8px;
  color: #888;
}

/* Custom timeframe dropdown styles */
.custom-timeframe-dropdown-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  overflow: visible;
  z-index: 1100;
}

.custom-timeframe-dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #ffffff;
  border: 1px solid #e0e3eb;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  z-index: 1200;
  min-width: 180px;
  padding: 4px 0;
}

.custom-timeframe-dropdown-menu .dropdown-item {
  color: #111827;
  background: transparent;
}

.custom-timeframe-dropdown-menu .dropdown-item:hover {
  background: rgba(249, 115, 22, 0.12);
  color: #c2410c;
}

.custom-timeframe-dropdown-menu .dropdown-item.dropdown-active {
  background: rgba(249, 115, 22, 0.18);
  color: #c2410c;
  font-weight: 700;
}

.dropdown-divider {
  height: 1px;
  background: #e0e3eb;
  margin: 4px 0;
}

.chart-page-dark .custom-timeframe-dropdown-menu {
  background: #1e2436;
  border-color: rgba(55, 59, 75, 0.8);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
}

.chart-page-dark .custom-timeframe-dropdown-menu .dropdown-item {
  color: #eef1f6;
  background: transparent;
}

.chart-page-dark .custom-timeframe-dropdown-menu .dropdown-item:hover {
  background: rgba(249, 115, 22, 0.18);
  color: #fb923c;
}

.chart-page-dark .custom-timeframe-dropdown-menu .dropdown-item.dropdown-active {
  background: rgba(249, 115, 22, 0.24);
  color: #fb923c;
  font-weight: 700;
}

.chart-page-dark .dropdown-divider {
  background: rgba(55, 59, 75, 0.6);
}

.dropdown-icon {
  margin-right: 8px;
  font-weight: bold;
  color: #f97316;
}

.chart-page-dark .dropdown-icon {
  color: #fb923c;
}

/* Custom timeframe popover styles */
.custom-timeframe-popover-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.custom-timeframe-popover {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 400px;
  overflow: hidden;
}

.custom-timeframe-popover.dark {
  background: rgb(30, 38, 54);
  border: 1px solid rgba(55, 59, 75, 0.6);
}

.popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e3eb;
}

.popover-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #131722;
}

.custom-timeframe-popover.dark .popover-header {
  border-bottom-color: rgba(55, 59, 75, 0.6);
}

.custom-timeframe-popover.dark .popover-header h3 {
  color: #d1d4dc;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #787b86;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.12s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}

.custom-timeframe-popover.dark .close-btn {
  color: #636978;
}

.custom-timeframe-popover.dark .close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.popover-content {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #131722;
}

.custom-timeframe-popover.dark .form-group label {
  color: #d1d4dc;
}

.form-input,
.form-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e3eb;
  border-radius: 4px;
  font-size: 13px;
  background: #ffffff;
  color: #131722;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #2962ff;
  box-shadow: 0 0 0 2px rgba(41, 98, 255, 0.2);
}

.custom-timeframe-popover.dark .form-input,
.custom-timeframe-popover.dark .form-select {
  background: rgb(18, 24, 38);
  border-color: rgba(55, 59, 75, 0.6);
  color: #d1d4dc;
}

.custom-timeframe-popover.dark .form-input:focus,
.custom-timeframe-popover.dark .form-select:focus {
  border-color: #5b9cf6;
  box-shadow: 0 0 0 2px rgba(91, 156, 246, 0.2);
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.btn-cancel,
.btn-add {
  flex: 1;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s ease;
}

.btn-cancel {
  background: #f0f3fa;
  color: #787b86;
}

.btn-cancel:hover {
  background: #e0e3eb;
  color: #131722;
}

.btn-add {
  background: #2962ff;
  color: #ffffff;
}

.btn-add:hover:not(:disabled) {
  background: #1e4fdb;
}

.btn-add:disabled {
  background: #e0e3eb;
  color: #787b86;
  cursor: not-allowed;
}

.custom-timeframe-popover.dark .btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: #636978;
}

.custom-timeframe-popover.dark .btn-cancel:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #d1d4dc;
}

/* Chart type selector styling */
.chart-type-selector-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  position: relative;
}

.header-indicator-btn {
  width: 32px;
  height: 32px;
}

/* Confidence donut chart styling */
.confidence-donut-wrapper {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 0 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.confidence-donut-wrapper:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.chart-page-dark .confidence-donut-wrapper:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.custom-timeframe-popover.dark .btn-add:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: #636978;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-top-color: #ff6b35;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Dark mode adjustments */
.chart-page-dark .fyers-search-loading {
  color: #aaa;
}

.chart-page-dark .spinner {
  border-color: #444;
  border-top-color: #ff6b35;
}

</style>

<style>
.chart-page .right-tool-btn > span{display:none}
.chart-page .right-tool-btn{min-height:38px;height:38px;width:42px;padding:8px}
.chart-page .right-tools-bar{width:46px;min-width:46px;background:rgba(245,248,255,.76);backdrop-filter:blur(18px)}
.chart-page-dark .right-tools-bar{background:rgba(20,27,43,.76)}
.chart-page .right-tool-btn.active{color:#2864F0;background:rgba(40,100,240,.12);box-shadow:inset 0 -2px #2864F0}
.tc-panel-pin{position:relative;z-index:2;align-self:flex-start;border:0;border-radius:4px;padding:5px 10px;background:#2864F018;color:#2864F0;cursor:pointer}
.chart-page .chart-feature-bar{overflow-x:auto;white-space:nowrap}
.chart-page .chart-bottom-actions{margin-left:auto;border-left:1px solid #8794aa40;padding-left:12px;flex-shrink:0}
.chart-page .chart-bottom-vt-actions:empty{display:none}
.tc-timeframe-portal{max-height:70vh;overflow:auto;background:rgba(250,252,255,.92);backdrop-filter:blur(18px)}
.tc-timeframe-portal.dark{background:rgba(23,30,47,.94);color:#edf2fa}
@media(prefers-reduced-motion:reduce){.slide-panel-enter-active,.slide-panel-leave-active{transition:none!important}}
</style>

<style>.tc-timeframe-portal.dark .dropdown-item{color:#edf2fa}.tc-timeframe-portal .dropdown-item:hover,.tc-timeframe-portal .dropdown-active{color:#2864F0!important;background:#2864F018!important}</style>

<style src="./workspace/chart-shell.css"></style>
